// v2026-03-27 cache bust
import RepairModule from './RepairModule';
import SalesModule from './SalesModule';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTenant } from './contexts/TenantContext';
import { useAuth } from './contexts/AuthContext';
import axios from './lib/axiosClient';
import { deleteOrArchiveOrder, unarchiveOrder } from './lib/orderArchive';
import { daysBetween, describeRateLine } from './lib/rentalDays';
import { isLiveOrder, liveOrders as liveOnly } from './lib/liveOrders';
import { fmtDate } from './lib/fmtDate';
import { getAutoRateType, getAutoPrice, getLineTotal } from './lib/rentalPricing';
import RentalQuotes from './RentalQuotes';
import RentalContractAdmin from './RentalContractAdmin';
import RentalSettings from './RentalSettings';
import SupplyChainView from './SupplyChainView';
import RentalBilling from './RentalBilling';
import Billing from './Billing';
import ClientProfilePage from './ClientProfilePage';
import BarcodeScanner from './BarcodeScanner';
import CategoryBrowser from './CategoryBrowser';
import InventoryBrowser from './InventoryBrowser';
import ItemPickerModal from './ItemPickerModal';
import OrderDetailPage from './OrderDetailPage';
import DamagePhoto from './DamagePhoto';
import RetainerContracts from './RetainerContracts';
import MaintenanceSchedules from './MaintenanceSchedules';
import { OrderActionProvider, useOrderActions, ActivePage } from './components/orderPipeline/OrderActionContext';
import { useIsNarrow } from './components/orderPipeline/pipeline';
import NextStepButton from './components/orderPipeline/NextStepButton';
import OrderMoreMenu from './components/orderPipeline/OrderMoreMenu';
import StageStrip from './components/orderPipeline/StageStrip';

// Business identity for printed documents — from tenant settings and the
// default location, never hardcoded. Cached per page load.
let _bizInfo = null;
async function getBizInfo() {
  if (_bizInfo) return _bizInfo;
  const [s, l] = await Promise.all([
    axios.get('/api/rentals/settings').catch(() => ({ data: {} })),
    axios.get('/api/rental-locations/locations').catch(() => ({ data: {} })),
  ]);
  const settings = s.data?.settings || {};
  const locs = l.data?.locations || [];
  const loc = locs.find(x => x.is_default) || locs[0] || {};
  _bizInfo = {
    name: settings.business_name || 'Equipment Rentals',
    place: [loc.city, loc.state].filter(Boolean).join(', '),
    email: settings.notification_email || '',
    phone: loc.phone || '',
    timezone: loc.timezone || null,   // the default location's clock — "today" for a new order's dates
  };
  return _bizInfo;
}
// Created-at in the order's own location time zone (falls back to the browser's).
function fmtCreated(o) {
  if (!o?.created_at) return '—';
  const tz = o.rental_locations?.timezone;
  try { return new Date(o.created_at).toLocaleString('en-US', { ...(tz ? { timeZone: tz } : {}), month:'short', day:'numeric', hour:'numeric', minute:'2-digit' }); }
  catch { return new Date(o.created_at).toLocaleString('en-US', { month:'short', day:'numeric', hour:'numeric', minute:'2-digit' }); }
}

// ── CONSTANTS ────────────────────────────────────────────────────────────────
const STATUSES = ['draft','reserved','started','returned','cancelled']; // = the DB check constraint; 'archived' never existed there
const PAYMENT_STATUSES = ['payment_due','partially_paid','paid','overpaid','process_deposit'];
const FULFILLMENTS = ['pickup','delivery'];
const RATE_TYPES = ['hour','day','week','month','flat'];
const CATEGORIES = ['Grinding','Scarifying','Blasting','Application','Polishing','Accessories','Dust Control','Cutting','Prep'];

const STATUS_LABEL = { draft:'Draft', reserved:'Reserved', started:'Out', returned:'Returned', cancelled:'Cancelled' };
const PAYMENT_LABEL = { payment_due:'Payment Due', partially_paid:'Partially Paid', paid:'Paid', overpaid:'Overpaid', process_deposit:'Process Deposit' };

const STATUS_COLOR = {
  draft:    { bg:'#1e2235', color:'#e2e8f0', border:'#3a3f60' },
  reserved: { bg:'#1a2a4a', color:'#60a5fa', border:'#1e3a6a' },
  started:  { bg:'#1a3a2a', color:'#34d399', border:'#1e4a35' },
  returned: { bg:'#2a2a1a', color:'#fbbf24', border:'#3a3a1e' },
  archived: { bg:'#1e1e2a', color:'#e2e8f0', border:'#2a2a3a' },
  cancelled:{ bg:'#3a1a1a', color:'#f87171', border:'#4a2020' },
};
const PAYMENT_COLOR = {
  payment_due:'#f87171', partially_paid:'#fbbf24', paid:'#34d399', overpaid:'#a78bfa', process_deposit:'#60a5fa',
};

const fmt = (n) => '$' + Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});

// ── STYLES v2 ───────────────────────────────────────────────────────────────────
const S = {
  wrap:    { fontFamily:"'DM Mono','Courier New',monospace", background:'#1e2235', minHeight:'100vh', color:'#e2e8f0', display:'flex' },
  sidebar: { width:200, minWidth:200, background:'#1a1c2e', borderRight:'1px solid #1a1d2e', display:'flex', flexDirection:'column', flexShrink:0, fontFamily:"'DM Sans',Arial,sans-serif" },
  logo:    { padding:'18px 20px 14px', borderBottom:'1px solid #1a1d2e' },
  logoTxt: { fontSize:15, fontWeight:700, color:'#fff', letterSpacing:0 },
  logoSub: { fontSize:10, color:'#e2e8f0', letterSpacing:1, marginTop:2 },
  nav:     (a) => ({ padding:'11px 16px', fontSize:14, fontWeight:500, color:a?'#f97316':'#cbd5e1', cursor:'pointer', borderRadius:8, margin:'2px 8px', borderLeft:'none', background:a?'rgba(249,115,22,0.1)':'transparent', display:'flex', alignItems:'center', gap:10, transition:'all 0.15s' }),
  navSec:  { padding:'12px 16px 4px', fontSize:10, fontWeight:700, color:'#e2e8f0', letterSpacing:1.5, textTransform:'uppercase' },
  navSub:  (a) => ({ padding:'7px 20px 7px 28px', fontSize:11, fontWeight:500, color:a?'#f97316':'#94a3b8', cursor:'pointer', background:a?'rgba(249,115,22,0.04)':'transparent' }),
  main:    { flex:1, overflow:'auto', padding:28, minWidth:0 },
  hdr:     { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 },
  title:   { fontSize:20, fontWeight:700, color:'#f1f5f9', letterSpacing:-0.5 },
  card:    { background:'#252838', border:'1px solid #1a1d2e', borderRadius:10, padding:20, marginBottom:16 },
  metric:  { background:'#252838', border:'1px solid #1a1d2e', borderRadius:10, padding:'14px 18px', flex:1 },
  mVal:    { fontSize:22, fontWeight:800, color:'#f1f5f9', marginBottom:2 },
  mLbl:    { fontSize:10, color:'#e2e8f0', letterSpacing:1, textTransform:'uppercase' },
  table:   { width:'100%', borderCollapse:'collapse', fontSize:12 },
  th:      { padding:'9px 12px', color:'#e2e8f0', fontWeight:700, letterSpacing:0.8, textTransform:'uppercase', fontSize:10, borderBottom:'1px solid #1a1d2e', textAlign:'left' },
  td:      { padding:'11px 12px', borderBottom:'1px solid #13141f', verticalAlign:'middle', color:'#cbd5e1' },
  input:   { width:'100%', padding:'9px 11px', background:'#1e2030', border:'1px solid #1e2235', borderRadius:6, color:'#e2e8f0', fontSize:13, outline:'none', boxSizing:'border-box' },
  select:  { width:'100%', padding:'9px 11px', background:'#1e2030', border:'1px solid #1e2235', borderRadius:6, color:'#e2e8f0', fontSize:13, outline:'none', boxSizing:'border-box' },
  label:   { display:'block', fontSize:10, fontWeight:700, color:'#e2e8f0', letterSpacing:1, textTransform:'uppercase', marginBottom:5 },
  btn:     (v) => ({ padding:v==='sm'?'5px 12px':'9px 18px', background:v==='primary'?'linear-gradient(135deg,#f97316,#ea580c)':v==='danger'?'#7f1d1d':'#2e3150', color:v==='primary'?'#fff':'#94a3b8', border:v==='ghost'?'1px solid #2a2d3e':'none', borderRadius:6, fontSize:12, fontWeight:700, cursor:'pointer', letterSpacing:0.5 }),
  badge:   (type,val) => { const c=type==='status'?STATUS_COLOR[val]||STATUS_COLOR.draft:{bg:'transparent',color:PAYMENT_COLOR[val]||'#888',border:'transparent'}; return {display:'inline-block',padding:'3px 9px',borderRadius:4,fontSize:10,fontWeight:700,background:c.bg,color:c.color,border:`1px solid ${c.border||'transparent'}`,letterSpacing:0.5}; },
  g2:      { display:'grid', gridTemplateColumns:'1fr 1fr', gap:13 },
  g3:      { display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:13 },
  tabs:    { display:'flex', borderBottom:'1px solid #1a1d2e', marginBottom:0 },
  tab:     (a) => ({ padding:'8px 16px', fontSize:12, fontWeight:600, cursor:'pointer', color:a?'#f97316':'#94a3b8', borderBottom:a?'2px solid #f97316':'2px solid transparent', marginBottom:-1 }),
};

// ── MAIN COMPONENT ───────────────────────────────────────────────────────────
// ── CLIENTS VIEW ─────────────────────────────────────────────────────────────
function ClientsView({ clients, orders, onRefresh, onAddClient, onOpenPanel, onOpenProfile, onNewOrder, fmt, fmtDate, axiosClient, showFlash, showInactiveClients, setShowInactiveClients, loading }) {
  const skel = (w, h=12) => ({ display:'inline-block', width:w, height:h, borderRadius:4, background:'linear-gradient(90deg,#1e2235 25%,#2a2d40 37%,#1e2235 63%)', backgroundSize:'400% 100%', animation:'crmSkeleton 1.4s ease infinite' });
  const firstLoad = loading && clients.length===0;
  const [search, setSearch] = useState('');
  const [editClient, setEditClient] = useState(null);
  const [editForm, setEditForm]     = useState({});
  const [saving, setSaving]         = useState(false);
  const [sortBy, setSortBy]         = useState('name');
  const [selected, setSelected]     = useState(new Set());

  const filtered = clients
    .filter(c => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (c.name||'').toLowerCase().includes(q) ||
             (c.company||'').toLowerCase().includes(q) ||
             (c.email||'').toLowerCase().includes(q) ||
             (c.phone||'').includes(q);
    })
    .sort((a,b) => {
      if (sortBy==='spent') return (b.total_spent||0) - (a.total_spent||0);
      if (sortBy==='orders') return (b.orders||0) - (a.orders||0);
      if (sortBy==='recent') return new Date(b.last_order||0) - new Date(a.last_order||0);
      return (a.name||'').localeCompare(b.name||'');
    });

  const inp = { padding:'7px 10px', borderRadius:6, border:'1px solid #2a3050', background:'#1e2235', color:'#e2e8f0', fontSize:12, width:'100%', boxSizing:'border-box' };

  async function saveEdit() {
    setSaving(true);
    try {
      await axiosClient.put(`/api/rentals/clients/${editClient.id}`, editForm);
      showFlash('Client updated'); setEditClient(null); onRefresh();
    } catch(e) { showFlash(e.response?.data?.error||'Save failed','error'); }
    setSaving(false);
  }

  async function deleteClient(id) {
    if (!window.confirm('Remove this client?')) return;
    try { await axiosClient.delete(`/api/rentals/clients/${id}`); showFlash('Client removed'); setTimeout(()=>onRefresh(), 600); }
    catch(e) { showFlash('Failed to remove','error'); }
  }

  async function bulkDelete() {
    if (!window.confirm(`Delete ${selected.size} client${selected.size>1?'s':''}? This cannot be undone.`)) return;
    try {
      const { data } = await axiosClient.delete('/api/rentals/clients/bulk', { data: { ids: [...selected] } });
      showFlash(data.message || `${data.deleted} deleted`, data.blocked?.length ? 'warn' : 'success');
      setSelected(new Set());
      setTimeout(()=>onRefresh(), 600);
    } catch(e) {
      showFlash(e.response?.data?.error || 'Bulk delete failed', 'error');
    }
  }

  const allFilteredSelected = filtered.length > 0 && filtered.every(c => selected.has(c.id));
  function toggleSelectAll() {
    if (allFilteredSelected) {
      setSelected(prev => { const s = new Set(prev); filtered.forEach(c => s.delete(c.id)); return s; });
    } else {
      setSelected(prev => { const s = new Set(prev); filtered.forEach(c => s.add(c.id)); return s; });
    }
  }
  function toggleOne(id) {
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  }

  return (
    <div>
      <style>{`@keyframes crmSkeleton { 0%{background-position:100% 50%} 100%{background-position:0 50%} }`}</style>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:18, fontWeight:700, color:'#e2e8f0' }}>👥 Clients <span style={{ fontSize:13, color:'#e2e8f0', fontWeight:400 }}>({clients.length})</span></div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <input placeholder="Search name, company, email, phone…" value={search} onChange={e=>setSearch(e.target.value)}
            autoComplete="off" spellCheck="false"
            style={{ padding:'7px 12px', borderRadius:7, border:'1px solid #2a3050', background:'#1e2235', color:'#e2e8f0', fontSize:12, width:220 }} />
          <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
            style={{ padding:'7px 10px', borderRadius:7, border:'1px solid #2a3050', background:'#1e2235', color:'#e2e8f0', fontSize:12 }}>
            <option value="name">Sort: Name</option>
            <option value="spent">Sort: Most Spent</option>
            <option value="orders">Sort: Most Orders</option>
            <option value="recent">Sort: Most Recent</option>
          </select>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:'#94a3b8',cursor:'pointer'}}>
              <input type='checkbox' checked={showInactiveClients||false} onChange={e=>setShowInactiveClients&&setShowInactiveClients(e.target.checked)} style={{accentColor:'#f97316'}} />
              Show inactive
            </label>
            {selected.size > 0 && (
              <button onClick={bulkDelete} style={{ padding:'7px 14px', borderRadius:7, border:'none', background:'#7f1d1d', color:'#fca5a5', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                Delete {selected.size} selected
              </button>
            )}
            <button onClick={onAddClient} style={{ padding:'7px 16px', borderRadius:7, border:'none', background:'#f97316', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>+ Add Client</button>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div style={{ display:'flex', gap:12, marginBottom:16 }}>
        {[
          { label:'Total Clients',  val: clients.length, color:'#e2e8f0' },
          { label:'With Orders',    val: clients.filter(c=>c.orders>0).length, color:'#60a5fa' },
          { label:'Total Revenue',  val: '$'+clients.reduce((s,c)=>s+(c.total_spent||0),0).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g,','), color:'#f97316' },
          { label:'Avg Spend',      val: '$'+(clients.length ? (clients.reduce((s,c)=>s+(c.total_spent||0),0)/clients.length).toFixed(0) : '0').replace(/\B(?=(\d{3})+(?!\d))/g,','), color:'#34d399' },
        ].map(m=>(
          <div key={m.label} style={{ flex:1, background:'#252838', border:'1px solid #1a1d2e', borderRadius:8, padding:'10px 14px' }}>
            <div style={{ fontSize:20, fontWeight:700, color:m.color }}>{firstLoad ? <span style={skel(50,20)} /> : m.val}</div>
            <div style={{ fontSize:10, color:'#e2e8f0', textTransform:'uppercase', letterSpacing:'.4px' }}>{m.label}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={{ background:'#252838', border:'1px solid #1a1d2e', borderRadius:10, overflow:'hidden' }}>
        <div style={{overflowX:'auto'}}>
        <table style={{ width:'100%', borderCollapse:'collapse', minWidth:900 }}>
          <thead><tr style={{ background:'#1e2235' }}>
            <th style={{ padding:'9px 12px', borderBottom:'1px solid #1a1d2e', width:36 }}>
              <input type='checkbox' checked={allFilteredSelected} onChange={toggleSelectAll} style={{ accentColor:'#f97316', cursor:'pointer' }} />
            </th>
            {['#','Name','Company','Email','Phone','Address','Discount','Orders','Spent','Last Order',''].map(h=>(
              <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontSize:11, color:'#e2e8f0', fontWeight:600, textTransform:'uppercase', borderBottom:'1px solid #1a1d2e', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {firstLoad ? Array.from({length:6}).map((_,i) => (
              <tr key={'skel-'+i}>
                <td style={{ padding:'9px 12px' }}><span style={skel(16,16)} /></td>
                {Array.from({length:10}).map((_,j) => (
                  <td key={j} style={{ padding:'9px 12px' }}><span style={skel('70%')} /></td>
                ))}
                <td style={{ padding:'9px 12px' }}><span style={skel(60)} /></td>
              </tr>
            )) : <>
            {filtered.map((c, i) => (
              editClient?.id === c.id ? (
                // Inline edit row
                <tr key={c.id} style={{ background:'#2a2d40' }}>
                  <td style={{ padding:'8px 12px' }}><input type='checkbox' checked={selected.has(c.id)} onChange={()=>toggleOne(c.id)} style={{ accentColor:'#f97316', cursor:'pointer' }} /></td>
                  <td style={{ padding:'8px 12px', fontSize:11, color:'#e2e8f0' }}>{i+1}</td>
                  <td style={{ padding:'6px 8px' }}><input style={inp} value={editForm.name||''} onChange={e=>setEditForm(f=>({...f,name:e.target.value}))} /></td>
                  <td style={{ padding:'6px 8px' }}><input style={inp} value={editForm.company||''} onChange={e=>setEditForm(f=>({...f,company:e.target.value}))} /></td>
                  <td style={{ padding:'6px 8px' }}><input style={inp} value={editForm.email||''} onChange={e=>setEditForm(f=>({...f,email:e.target.value}))} /></td>
                  <td style={{ padding:'6px 8px' }}><input style={inp} value={editForm.phone||''} onChange={e=>setEditForm(f=>({...f,phone:e.target.value}))} /></td>
                  <td style={{ padding:'6px 8px' }}><input style={inp} value={editForm.address||''} onChange={e=>setEditForm(f=>({...f,address:e.target.value}))} /></td>
                  <td style={{ padding:'6px 8px' }}>
                    <div style={{display:'flex',alignItems:'center',gap:4}}>
                      <input type="number" style={{...inp,width:52,marginBottom:0}} min={0} max={100} placeholder="0" value={editForm.discount_percent||''} onChange={e=>setEditForm(f=>({...f,discount_percent:e.target.value}))} />
                      <span style={{fontSize:11,color:'#e2e8f0'}}>%</span>
                    </div>
                  </td>
                  <td colSpan={2} style={{ padding:'6px 8px' }}>
                    <textarea style={{ ...inp, resize:'vertical', height:32 }} value={editForm.notes||''} placeholder="Notes…" onChange={e=>setEditForm(f=>({...f,notes:e.target.value}))} />
                  </td>
                  <td style={{ padding:'6px 8px' }}>
                    <div style={{ display:'flex', gap:5 }}>
                      <button onClick={saveEdit} disabled={saving} style={{ padding:'4px 10px', borderRadius:5, border:'none', background:'#f97316', color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer' }}>{saving?'…':'Save'}</button>
                      <button onClick={()=>setEditClient(null)} style={{ padding:'4px 8px', borderRadius:5, border:'1px solid #2a3050', background:'transparent', color:'#e2e8f0', fontSize:11, cursor:'pointer' }}>✕</button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={c.id} onMouseEnter={e=>e.currentTarget.style.background='#2a2d40'} onMouseLeave={e=>e.currentTarget.style.background=selected.has(c.id)?'#2a1f3d':'transparent'} style={{ cursor:'pointer', background: selected.has(c.id)?'#2a1f3d':'transparent' }}>
                  <td style={{ padding:'9px 12px' }} onClick={e=>e.stopPropagation()}><input type='checkbox' checked={selected.has(c.id)} onChange={()=>toggleOne(c.id)} style={{ accentColor:'#f97316', cursor:'pointer' }} /></td>
                  <td style={{ padding:'9px 12px', fontSize:11, color:'#e2e8f0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{i+1}</td>
                  <td style={{ padding:'9px 12px', overflow:'hidden' }} onClick={()=>onOpenProfile?.(c)}>
                    <div style={{ display:'flex', alignItems:'center', gap:9, overflow:'hidden' }}>
                      <div style={{ width:28, height:28, borderRadius:'50%', background:'#1e3a5f', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'#60a5fa', flexShrink:0 }}>{c.name[0].toUpperCase()}</div>
                      <div style={{ overflow:'hidden' }}>
                        <span style={{ fontWeight:600, color:'#60a5fa', fontSize:13, textDecoration:'underline', textDecorationStyle:'dotted', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', display:'block' }}>{c.name}</span>
                        <div style={{marginTop:2}}>
                          {c.portal_status === 'active' ? <span style={{fontSize:9,fontWeight:700,padding:'1px 6px',borderRadius:4,background:'#14532d',color:'#4ade80'}}>Portal Active</span>
                          : c.portal_status === 'invited' ? <span style={{fontSize:9,fontWeight:700,padding:'1px 6px',borderRadius:4,background:'#422006',color:'#fbbf24'}}>Invited</span>
                          : <span style={{fontSize:9,fontWeight:700,padding:'1px 6px',borderRadius:4,background:'#2e3150',color:'#64748b'}}>No Portal</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding:'9px 12px', fontSize:12, color:'#cbd5e1', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.company||'—'}</td>
                  <td style={{ padding:'9px 12px', fontSize:12, color:'#cbd5e1', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.email||'—'}</td>
                  <td style={{ padding:'9px 12px', fontSize:12, color:'#cbd5e1', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.phone||'—'}</td>
                  <td style={{ padding:'9px 12px', fontSize:11, color:'#cbd5e1', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.address||'—'}</td>
                  <td style={{ padding:'9px 12px', fontSize:12, fontWeight:700, color: c.discount_percent>0 ? '#f97316' : '#3a3f60', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {c.discount_percent > 0 ? c.discount_percent + '%' : '—'}
                  </td>
                  <td style={{ padding:'9px 12px', fontSize:12, fontWeight:600, color:'#e2e8f0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.orders||0}</td>
                  <td style={{ padding:'9px 12px', fontSize:13, fontWeight:700, color:'#34d399', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{fmt(c.total_spent||0)}</td>
                  <td style={{ padding:'9px 12px', fontSize:11, color:'#e2e8f0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.last_order ? fmtDate(c.last_order) : '—'}</td>
                  <td style={{ padding:'9px 12px' }}>
                    <div style={{ display:'flex', gap:5 }}>
                      <button onClick={()=>onOpenProfile?.(c)}
                        style={{ padding:'3px 8px', borderRadius:5, border:'none', background:'#1a2e1a', color:'#4ade80', fontSize:10, fontWeight:700, cursor:'pointer' }}>Profile</button>
                      <button onClick={()=>onNewOrder?.(c)}
                        style={{ padding:'3px 10px', borderRadius:5, border:'none', background:'#f97316', color:'#fff', fontSize:10, fontWeight:700, cursor:'pointer' }}>+ Check Out</button>
                      <button onClick={()=>{ setEditClient(c); setEditForm({name:c.name,company:c.company||'',email:c.email||'',phone:c.phone||'',address:c.address||'',notes:c.notes||'',discount_percent:c.discount_percent||0}); }}
                        style={{ padding:'3px 8px', borderRadius:5, border:'none', background:'#1e3a5f', color:'#60a5fa', fontSize:10, fontWeight:700, cursor:'pointer' }}>Edit</button>
                      <button onClick={()=>deleteClient(c.id)}
                        style={{ padding:'3px 8px', borderRadius:5, border:'none', background:'#3b0a0a', color:'#f87171', fontSize:10, fontWeight:700, cursor:'pointer' }}>✕</button>
                    </div>
                  </td>
                </tr>
              )
            ))}
            {filtered.length===0 && (
              <tr><td colSpan={11} style={{ padding:40, textAlign:'center', color:'#94a3b8', fontSize:13 }}>
                {clients.length===0 ? 'No clients yet — add one or import from CSV' : 'No clients match your search'}
              </td></tr>
            )}
            </>}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

// ── IMPORT / EXPORT ──────────────────────────────────────────────────────────
function ImportExport({ orders, items, clients, axiosClient, onRefresh }) {
  const [tab, setTab]           = useState('import');
  const [importType, setImportType] = useState('equipment');
  const [dragOver, setDragOver]  = useState(false);
  const [parsed, setParsed]      = useState(null);   // { headers, rows, errors }
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = React.useRef();

  // ── Styles ──
  const card  = { background:'#252838', border:'1px solid #1a1d2e', borderRadius:10, padding:20, marginBottom:16 };
  const th    = { padding:'8px 12px', textAlign:'left', fontSize:11, color:'#e2e8f0', fontWeight:600, background:'#1e2235', borderBottom:'1px solid #1a1d2e', textTransform:'uppercase' };
  const td    = { padding:'8px 12px', fontSize:12, color:'#e2e8f0', borderBottom:'1px solid #0d0e1a' };
  const tabBtn = (active) => ({ padding:'8px 20px', borderRadius:7, border:'none', cursor:'pointer', fontSize:13, fontWeight:700, background:active?'#f97316':'#1e2235', color:active?'#fff':'#94a3b8' });
  const badge  = (c) => ({ display:'inline-block', padding:'2px 8px', borderRadius:4, fontSize:10, fontWeight:700, background:c==='ok'?'#14532d':c==='warn'?'#3b2a00':'#3b0a0a', color:c==='ok'?'#4ade80':c==='warn'?'#fbbf24':'#f87171' });

  // ── CSV Templates ──
  const TEMPLATES = {
    equipment: {
      headers: ['name','category','description','price_day','price_week','price_month','price_hour','quantity_total','deposit','sku','photo_url','notes'],
      example:  ['Diamond Tooling Set','Concrete','Set of 4 diamond blades','75','400','1200','','1','200','Handle with care'],
    },
    clients: {
      headers: ['name','email','phone','company','address','city','state','zip','dl_number','notes'],
      example:  ['John Smith','john@email.com','555-555-0100','Smith Construction','123 Main St','Saint George','UT','84770','D123456','VIP client'],
    },
    orders: {
      headers: ['client_name','client_email','client_phone','from_date','to_date','fulfillment_type','status','payment_status','notes','total'],
      example:  ['John Smith','john@email.com','555-555-0100','2026-03-15','2026-03-20','pickup','reserved','payment_due','Large concrete pour job','750'],
    },
  };

  function downloadTemplate(type) {
    const t = TEMPLATES[type];
    const csv = [t.headers.join(','), t.example.map(v=>`"${v}"`).join(',')].join('\n');
    const blob = new Blob([csv], { type:'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `ivision_${type}_template.csv`; a.click();
  }

  // ── CSV Parser ──
  function parseCSV(text) {
    const lines = text.trim().split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) return { headers:[], rows:[], errors:['File has no data rows'] };
    const headers = lines[0].split(',').map(h => h.replace(/^"|"$/g,'').trim().toLowerCase().replace(/ /g,'_'));
    const rows = lines.slice(1).map((line, i) => {
      // Handle quoted commas
      const cols = [];
      let cur = '', inQ = false;
      for (let ch of line) {
        if (ch === '"') { inQ = !inQ; }
        else if (ch === ',' && !inQ) { cols.push(cur.trim()); cur = ''; }
        else cur += ch;
      }
      cols.push(cur.trim());
      const row = {};
      headers.forEach((h, j) => { row[h] = cols[j]?.replace(/^"|"$/g,'') || ''; });
      row._line = i + 2;
      return row;
    });
    return { headers, rows, errors:[] };
  }

  function validateRows(rows, type) {
    return rows.map(row => {
      const errs = [];
      if (type === 'equipment') {
        if (!row.name) errs.push('name required');
        if (row.price_day && isNaN(Number(row.price_day))) errs.push('price_day must be a number');
        if (row.quantity_total && isNaN(Number(row.quantity_total))) errs.push('quantity_total must be a number');
      }
      if (type === 'clients') {
        if (!row.name) errs.push('name required');
      }
      if (type === 'orders') {
        if (!row.client_name) errs.push('client_name required');
        if (!row.from_date)   errs.push('from_date required');
        if (!row.to_date)     errs.push('to_date required');
      }
      return { ...row, _errors: errs, _status: errs.length ? 'error' : 'ok' };
    });
  }

  function handleFile(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = parseCSV(e.target.result);
      const validated = validateRows(result.rows, importType);
      setParsed({ ...result, rows: validated });
      setImportResult(null);
    };
    reader.readAsText(file);
  }

  async function runImport() {
    if (!parsed) return;
    const validRows = parsed.rows.filter(r => r._status === 'ok');
    if (!validRows.length) return;
    setImporting(true);
    setImportResult(null);
    let ok = 0, fail = 0, failedRows = [];
    for (const row of validRows) {
      try {
        const { _line, _errors, _status, ...data } = row;
        if (importType === 'equipment') {
          await axiosClient.post('/api/rentals/items', {
            name: data.name, category: data.category||'General', description: data.description||'',
            price_day: Number(data.price_day)||0, price_week: Number(data.price_week)||0,
            price_month: Number(data.price_month)||0, price_hour: Number(data.price_hour)||0,
            quantity_total: Number(data.quantity_total)||1, deposit: Number(data.deposit)||0,
            sku: data.sku||'', img_url: data.photo_url||'', notes: data.notes||'',
          });
        } else if (importType === 'clients') {
          await axiosClient.post('/api/rentals/clients', {
            name: data.name, email: data.email||'', phone: data.phone||'',
            company: data.company||'', address: data.address||'',
            city: data.city||'', state: data.state||'', zip: data.zip||'',
            dl_number: data.dl_number||'', notes: data.notes||'',
          });
        } else if (importType === 'orders') {
          await axiosClient.post('/api/rentals/orders', {
            client_name: data.client_name, client_email: data.client_email||'',
            client_phone: data.client_phone||'', from_date: data.from_date, to_date: data.to_date,
            fulfillment_type: data.fulfillment_type||'pickup', status: data.status||'draft',
            payment_status: data.payment_status||'payment_due', notes: data.notes||'',
            total: Number(data.total)||0, items:[],
          });
        }
        ok++;
      } catch(e) { fail++; failedRows.push({ line: row._line, error: e.message }); }
    }
    setImporting(false);
    setImportResult({ ok, fail, failedRows });
    if (ok > 0) { setParsed(null); onRefresh(); }
  }

  // ── Export ──
  function exportCSV(type) {
    let headers, rows;
    if (type === 'orders') {
      headers = ['id','client_name','client_email','client_phone','client_company','from_date','to_date','fulfillment_type','status','payment_status','total','deposit_amount','notes','created_at'];
      rows = orders.map(o => headers.map(h => `"${(o[h]||'').toString().replace(/"/g,'""')}"`).join(','));
    } else if (type === 'equipment') {
      headers = ['id','name','category','description','price_day','price_week','price_month','quantity_total','quantity_available','deposit','notes'];
      rows = items.map(i => headers.map(h => `"${(i[h]||'').toString().replace(/"/g,'""')}"`).join(','));
    } else if (type === 'clients') {
      const clientMap = {};
      orders.forEach(o => {
        if (!clientMap[o.client_name]) clientMap[o.client_name] = { client_name:o.client_name, client_email:o.client_email||'', client_phone:o.client_phone||'', client_company:o.client_company||'', orders:0, total_spent:0 };
        clientMap[o.client_name].orders++;
        clientMap[o.client_name].total_spent += Number(o.total||0);
      });
      headers = ['client_name','client_email','client_phone','client_company','orders','total_spent'];
      rows = Object.values(clientMap).map(c => headers.map(h=>`"${(c[h]||'').toString().replace(/"/g,'""')}"`).join(','));
    } else if (type === 'order_items') {
      headers = ['order_id','client_name','item_name','quantity','rate_type','unit_price','total_price','from_date','to_date'];
      rows = [];
      orders.forEach(o => {
        (o.rental_order_items||[]).forEach(li => {
          rows.push(headers.map(h => {
            const val = h==='order_id'?o.id : h==='client_name'?o.client_name : h==='from_date'?o.from_date : h==='to_date'?o.to_date : li[h]||'';
            return `"${val.toString().replace(/"/g,'""')}"`;
          }).join(','));
        });
      });
    }
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type:'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `ivision_${type}_${new Date().toISOString().split('T')[0]}.csv`; a.click();
  }

  function exportJSON(type) {
    const data = type==='orders' ? orders : type==='equipment' ? items : null;
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type:'application/json' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `ivision_${type}_${new Date().toISOString().split('T')[0]}.json`; a.click();
  }

  const validCount   = parsed?.rows.filter(r=>r._status==='ok').length  || 0;
  const errorCount   = parsed?.rows.filter(r=>r._status==='error').length || 0;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div style={{ fontSize:18, fontWeight:700, color:'#e2e8f0' }}>📥 Import / Export</div>
        <div style={{ display:'flex', gap:8 }}>
          <button style={tabBtn(tab==='import')} onClick={()=>setTab('import')}>⬆ Import</button>
          <button style={tabBtn(tab==='export')} onClick={()=>setTab('export')}>⬇ Export</button>
        </div>
      </div>

      {/* ── IMPORT TAB ── */}
      {tab==='import' && (
        <div>
          {/* Type selector */}
          <div style={card}>
            <div style={{ fontSize:12, fontWeight:700, color:'#f97316', marginBottom:12, textTransform:'uppercase', letterSpacing:'.5px' }}>What are you importing?</div>
            <div style={{ display:'flex', gap:10, marginBottom:16 }}>
              {[['equipment','📦 Equipment'],['clients','👥 Clients'],['orders','📋 Orders']].map(([t,l])=>(
                <div key={t} onClick={()=>{ setImportType(t); setParsed(null); setImportResult(null); }}
                  style={{ flex:1, padding:'14px 16px', borderRadius:9, border:`2px solid ${importType===t?'#f97316':'#2e3150'}`, background:importType===t?'rgba(249,115,22,0.06)':'#1e2235', cursor:'pointer', textAlign:'center' }}>
                  <div style={{ fontSize:18, marginBottom:4 }}>{l.split(' ')[0]}</div>
                  <div style={{ fontSize:13, fontWeight:700, color: importType===t?'#f97316':'#94a3b8' }}>{l.split(' ').slice(1).join(' ')}</div>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', gap:10, alignItems:'center' }}>
              <button onClick={()=>downloadTemplate(importType)} style={{ padding:'7px 14px', borderRadius:7, border:'1px solid #2a3050', background:'transparent', color:'#60a5fa', fontSize:12, cursor:'pointer', fontWeight:600 }}>
                ⬇ Download {importType} template
              </button>
              <span style={{ fontSize:11, color:'#e2e8f0' }}>Get the correct column format before uploading</span>
            </div>
          </div>

          {/* Drop zone */}
          {!parsed && (
            <div
              onDragOver={e=>{ e.preventDefault(); setDragOver(true); }}
              onDragLeave={()=>setDragOver(false)}
              onDrop={e=>{ e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
              onClick={()=>fileRef.current.click()}
              style={{ border:`2px dashed ${dragOver?'#f97316':'#3a3f60'}`, borderRadius:12, padding:48, textAlign:'center', cursor:'pointer', background:dragOver?'rgba(249,115,22,0.04)':'transparent', transition:'all 0.15s', marginBottom:16 }}>
              <div style={{ fontSize:36, marginBottom:12 }}>📂</div>
              <div style={{ fontSize:14, fontWeight:700, color:'#e2e8f0', marginBottom:6 }}>Drop your CSV file here</div>
              <div style={{ fontSize:12, color:'#e2e8f0' }}>or click to browse · .csv files only</div>
              <input ref={fileRef} type="file" accept=".csv" style={{ display:'none' }} onChange={e=>handleFile(e.target.files[0])} />
            </div>
          )}

          {/* Preview table */}
          {parsed && (
            <div style={card}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                <div>
                  <div style={{ fontSize:14, fontWeight:700, color:'#e2e8f0' }}>Preview — {parsed.rows.length} rows</div>
                  <div style={{ fontSize:12, color:'#e2e8f0', marginTop:2 }}>
                    <span style={{ color:'#4ade80' }}>✓ {validCount} ready</span>
                    {errorCount > 0 && <span style={{ color:'#f87171', marginLeft:12 }}>✕ {errorCount} errors (will be skipped)</span>}
                  </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={()=>{ setParsed(null); setImportResult(null); fileRef.current.value=''; }} style={{ padding:'6px 14px', borderRadius:7, border:'1px solid #2a3050', background:'transparent', color:'#e2e8f0', fontSize:12, cursor:'pointer' }}>Clear</button>
                  <button onClick={runImport} disabled={importing||validCount===0}
                    style={{ padding:'6px 20px', borderRadius:7, border:'none', background: validCount>0?'#f97316':'#3a3f60', color: validCount>0?'#fff':'#94a3b8', fontSize:12, fontWeight:700, cursor: validCount>0?'pointer':'default' }}>
                    {importing ? 'Importing…' : `Import ${validCount} rows →`}
                  </button>
                </div>
              </div>
              <div style={{ overflowX:'auto', maxHeight:320, overflowY:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', tableLayout:'fixed' }}>
                  <thead>
                    <tr>
                      <th style={th}>Line</th>
                      <th style={th}>Status</th>
                      {parsed.headers.slice(0,6).map(h=><th key={h} style={th}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.rows.map((row, i)=>(
                      <tr key={i} onMouseEnter={e=>e.currentTarget.style.background='#2a2d40'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                        <td style={td}><span style={{ color:'#e2e8f0', fontSize:11 }}>{row._line}</span></td>
                        <td style={td}>
                          {row._status==='ok'
                            ? <span style={badge('ok')}>✓ Ready</span>
                            : <span style={{ ...badge('error'), title:row._errors.join(', ') }}>✕ {row._errors[0]}</span>}
                        </td>
                        {parsed.headers.slice(0,6).map(h=>(
                          <td key={h} style={td}><span style={{ color: row._errors?.some(e=>e.includes(h))?'#f87171':'#e2e8f0' }}>{row[h]||<span style={{color:'#94a3b8'}}>—</span>}</span></td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Result */}
          {importResult && (
            <div style={{ ...card, border:`1px solid ${importResult.fail>0?'#3b1a1a':'#1a3a2a'}`, background: importResult.fail>0?'#0f0a0a':'#0a120a' }}>
              <div style={{ fontSize:14, fontWeight:700, color: importResult.ok>0?'#4ade80':'#f87171', marginBottom:8 }}>
                {importResult.ok > 0 ? `✓ Imported ${importResult.ok} rows successfully` : '✕ Import failed'}
              </div>
              {importResult.fail > 0 && (
                <div>
                  <div style={{ fontSize:12, color:'#f87171', marginBottom:6 }}>{importResult.fail} rows failed:</div>
                  {importResult.failedRows.map((r,i)=>(
                    <div key={i} style={{ fontSize:11, color:'#f87171', marginBottom:2 }}>Line {r.line}: {r.error}</div>
                  ))}
                </div>
              )}
              <button onClick={()=>setImportResult(null)} style={{ marginTop:10, padding:'6px 14px', borderRadius:6, border:'none', background:'#1a2a1a', color:'#4ade80', fontSize:11, cursor:'pointer' }}>Dismiss</button>
            </div>
          )}
        </div>
      )}

      {/* ── EXPORT TAB ── */}
      {tab==='export' && (
        <div>
          <div style={{ fontSize:12, color:'#e2e8f0', marginBottom:16 }}>Export your data to CSV or JSON for use in Excel, Google Sheets, or other systems.</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            {[
              { type:'orders',      icon:'📋', label:'All Orders',       desc:`${orders.length} orders with client info, dates, totals, and status` },
              { type:'order_items', icon:'🔗', label:'Order Line Items',  desc:'Each rented item per order — great for revenue analysis' },
              { type:'equipment',   icon:'📦', label:'Equipment / Inventory', desc:`${items.length} items with pricing, availability, and categories` },
              { type:'clients',     icon:'👥', label:'Client List',       desc:'Unique clients with order count and total spend' },
            ].map(({ type, icon, label, desc })=>(
              <div key={type} style={{ ...card, marginBottom:0 }}>
                <div style={{ fontSize:24, marginBottom:8 }}>{icon}</div>
                <div style={{ fontSize:14, fontWeight:700, color:'#e2e8f0', marginBottom:4 }}>{label}</div>
                <div style={{ fontSize:12, color:'#e2e8f0', marginBottom:16, lineHeight:1.5 }}>{desc}</div>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={()=>exportCSV(type)} style={{ flex:1, padding:'8px', borderRadius:7, border:'none', background:'#f97316', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>⬇ CSV</button>
                  {(type==='orders'||type==='equipment') && (
                    <button onClick={()=>exportJSON(type)} style={{ flex:1, padding:'8px', borderRadius:7, border:'1px solid #2a3050', background:'transparent', color:'#e2e8f0', fontSize:12, fontWeight:600, cursor:'pointer' }}>⬇ JSON</button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Quick stats */}
          <div style={{ ...card, marginTop:14 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#f97316', marginBottom:12, textTransform:'uppercase', letterSpacing:'.5px' }}>Data Summary</div>
            <div style={{ display:'flex', gap:14 }}>
              {[
                { label:'Total Orders',     val: orders.length },
                { label:'Unique Clients',   val: clients.length },
                { label:'Equipment Items',  val: items.length },
                { label:'Line Items',       val: orders.reduce((s,o)=>s+(o.rental_order_items?.length||0),0) },
                { label:'Total Revenue',    val: '$'+orders.reduce((s,o)=>s+Number(o.total||0),0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g,',') },
              ].map(m=>(
                <div key={m.label} style={{ flex:1, background:'#1e2235', borderRadius:8, padding:'10px 14px', border:'1px solid #1a1d2e' }}>
                  <div style={{ fontSize:18, fontWeight:700, color:'#e2e8f0' }}>{m.val}</div>
                  <div style={{ fontSize:10, color:'#e2e8f0', textTransform:'uppercase', letterSpacing:'.4px' }}>{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── CLIENT FORM MODAL ────────────────────────────────────────────────────────
function ClientForm({ onClose, onSave, onSaveBusiness }) {
  const [tab, setTab]   = React.useState('individual');
  const [saving, setSaving] = React.useState(false);
  const [sendInvite, setSendInvite] = React.useState(false);

  // Individual
  const [form, setForm] = React.useState({ name:'', email:'', phone:'', company:'', discount_percent:0, insurance_cert:false, insurance_discount:0, insurance_notes:'' });

  // Business
  const [biz, setBiz]   = React.useState({ company_name:'', email:'', phone:'', address:'', city:'', state:'', zip:'', website:'', tax_id:'', notes:'', insurance_cert:false, insurance_discount:0, insurance_notes:'' });
  const [users, setUsers] = React.useState([{ name:'', email:'', phone:'', role:'admin' }]);
  const [userErrors, setUserErrors] = React.useState([{}]);
  const [bizError, setBizError] = React.useState('');

  const inp = { padding:'8px 12px', borderRadius:7, border:'1px solid #2a3050', background:'#1e2235', color:'#e2e8f0', fontSize:12, width:'100%', boxSizing:'border-box', marginBottom:8 };
  const inpErr = (hasErr) => ({ padding:'8px 12px', borderRadius:7, border:`1px solid ${hasErr?'#ef4444':'#3a3f60'}`, background:'#1e2235', color:'#e2e8f0', fontSize:12, width:'100%', boxSizing:'border-box', marginBottom:0 });
  const lbl = { fontSize:10, color:'#e2e8f0', fontWeight:600, marginBottom:3, display:'block', textTransform:'uppercase', letterSpacing:'.4px' };
  const tabBtn = (t) => ({ padding:'7px 18px', borderRadius:7, border:'none', cursor:'pointer', fontSize:12, fontWeight:700, background: tab===t?'#f97316':'transparent', color: tab===t?'#fff':'#94a3b8' });

  function addUser() {
    setUsers(u => [...u, { name:'', email:'', phone:'', role:'member' }]);
    setUserErrors(e => [...e, {}]);
  }
  function removeUser(i) {
    setUsers(u => u.filter((_,idx)=>idx!==i));
    setUserErrors(e => e.filter((_,idx)=>idx!==i));
  }
  function updateUser(i, field, val) {
    setUsers(u => u.map((u2,idx) => idx===i ? {...u2,[field]:val} : u2));
    setUserErrors(e => e.map((err,idx) => idx===i ? {...err,[field]:undefined} : err));
  }

  async function saveBusiness() {
    setBizError('');
    if (!biz.company_name.trim()) { setBizError('Company name is required'); return; }
    if (!biz.email.trim()) { setBizError('Company email is required'); return; }

    // Validate: each contact row must have BOTH name and email, or be completely empty
    const errors = users.map(u => {
      const hasName = u.name.trim();
      const hasEmail = u.email.trim();
      if (!hasName && !hasEmail) return {}; // fully empty row — ok, will be skipped
      const rowErr = {};
      if (!hasName) rowErr.name = 'Name required';
      if (!hasEmail) rowErr.email = 'Email required';
      return rowErr;
    });
    if (errors.some(e => Object.keys(e).length > 0)) { setUserErrors(errors); return; }

    const validUsers = users.filter(u => u.name.trim() && u.email.trim());
    setSaving(true);
    try {
      const r = await import('./lib/axiosClient').then(m => m.default.post('/api/rental-business/accounts', { ...biz, users: validUsers }));
      onSaveBusiness?.(r.data);
      onClose();
    } catch(e) { setBizError(e.response?.data?.error || 'Failed to save'); }
    setSaving(false);
  }

  return ReactDOM.createPortal(
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:3000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'#252838',border:'1px solid #2a3050',borderRadius:14,width:'100%',maxWidth:520,maxHeight:'90vh',overflowY:'auto',boxShadow:'0 16px 48px rgba(0,0,0,0.6)'}}>

        {/* Header */}
        <div style={{padding:'20px 24px 0',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{fontSize:16,fontWeight:700,color:'#e2e8f0'}}>Add Client</div>
          <button onClick={onClose} style={{background:'none',border:'none',color:'#e2e8f0',cursor:'pointer',fontSize:18}}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{display:'flex',gap:4,padding:'14px 24px 0',background:'transparent'}}>
          <button style={tabBtn('individual')} onClick={()=>setTab('individual')}>👤 Individual</button>
          <button style={tabBtn('business')} onClick={()=>setTab('business')}>🏢 Business Account</button>
        </div>
        <div style={{height:1,background:'#2e3150',margin:'12px 0 0'}} />

        <div style={{padding:'16px 24px 24px'}}>

          {/* ── INDIVIDUAL TAB ── */}
          {tab === 'individual' && (
            <div>
              <label style={lbl}>Full Name <span style={{color:'#64748b',fontWeight:400,textTransform:'none',letterSpacing:0,fontSize:10}}>(required if no company)</span></label>
              <input style={inp} placeholder="John Smith" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} autoFocus />
              <label style={lbl}>Company</label>
              <input style={inp} placeholder="Acme Concrete LLC" value={form.company||''} onChange={e=>setForm(f=>({...f,company:e.target.value}))} />
              <label style={lbl}>Email</label>
              <input style={inp} placeholder="john@email.com" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} />
              <label style={lbl}>Phone</label>
              <input style={inp} placeholder="(702) 555-0000" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} />
              <label style={lbl}>Driver's License</label>
              <input style={{...inp,borderColor:!form.dl_number?'#f87171':undefined}} placeholder="DL number *required" value={form.dl_number||''} onChange={e=>setForm(f=>({...f,dl_number:e.target.value}))} />
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div><label style={lbl}>Address <span style={{color:'#f87171',fontSize:10}}>*required for tracking</span></label><input style={inp} placeholder="123 Main St" value={form.address||''} onChange={e=>setForm(f=>({...f,address:e.target.value}))} /></div>
                <div><label style={lbl}>City</label><input style={inp} placeholder="St. George" value={form.city||''} onChange={e=>setForm(f=>({...f,city:e.target.value}))} /></div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div>
                  <label style={lbl}>State</label>
                  <select style={inp} value={form.state||''} onChange={e=>setForm(f=>({...f,state:e.target.value}))}>
                    <option value=''>Select state...</option>
                    {['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'].map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div><label style={lbl}>ZIP</label><input style={inp} placeholder="84770" value={form.zip||''} onChange={e=>setForm(f=>({...f,zip:e.target.value}))} /></div>
              </div>
              <label style={lbl}>Account Discount %</label>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:16}}>
                <input type="number" style={{...inp,marginBottom:0,width:90}} min={0} max={100} placeholder="0" value={form.discount_percent||''} onChange={e=>setForm(f=>({...f,discount_percent:e.target.value}))} />
                <span style={{fontSize:12,color:'#e2e8f0'}}>Auto-applied when client rents via portal</span>
              </div>
              <div style={{padding:'12px',background:'#1e2235',borderRadius:8,border:'1px solid #1a1d2e',marginBottom:16,marginTop:4}}>
                <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',marginBottom:form.insurance_cert?10:0}}>
                  <input type="checkbox" checked={form.insurance_cert} onChange={e=>setForm(f=>({...f,insurance_cert:e.target.checked}))} style={{accentColor:'#f97316',width:14,height:14,display:'none'}} />
                  <span style={{fontSize:12,color:'#e2e8f0',fontWeight:600,display:'none'}}>🛡 Has insurance certificate on file</span>
                </label>
                {form.insurance_cert && (
                  <div style={{marginTop:8,display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                    <div>
                      <label style={lbl}>Rental Discount %</label>
                      <input type="number" style={{...inp,marginBottom:0}} min={0} max={100} value={form.insurance_discount||''} onChange={e=>setForm(f=>({...f,insurance_discount:e.target.value}))} />
                    </div>
                    <div>
                      <label style={lbl}>Notes</label>
                      <input style={{...inp,marginBottom:0}} placeholder="Policy #, expiry..." value={form.insurance_notes||''} onChange={e=>setForm(f=>({...f,insurance_notes:e.target.value}))} />
                    </div>
                  </div>
                )}
              </div>
              <div style={{padding:'10px 12px',background:'#1e2235',borderRadius:8,border:'1px solid #1a1d2e',marginBottom:14}}>
                <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                  <input type="checkbox" checked={sendInvite} onChange={e=>setSendInvite(e.target.checked)} style={{accentColor:'#f97316',width:14,height:14}} disabled={!form.email} />
                  <span style={{fontSize:12,color:form.email?'#e2e8f0':'#64748b',fontWeight:600}}>📧 Send portal invite</span>
                </label>
                {!form.email && sendInvite === false && <div style={{fontSize:10,color:'#64748b',marginTop:4,marginLeft:22}}>Add an email to enable portal invite</div>}
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:8,marginTop:4}}>
                <button onClick={()=>{ if(!form.name.trim() && !form.company?.trim()) return; onSave(form, sendInvite, false); }} style={{width:'100%',padding:'11px',borderRadius:8,border:'none',background:'#f97316',color:'#fff',cursor:'pointer',fontSize:13,fontWeight:700}}>Save & Create Order →</button>
                <button onClick={()=>{ if(!form.name.trim() && !form.company?.trim()) return; onSave(form, sendInvite, true); }} style={{width:'100%',padding:'10px',borderRadius:8,border:'1px solid #2a3050',background:'transparent',color:'#94a3b8',cursor:'pointer',fontSize:12}}>Save Client Only</button>
                <button onClick={onClose} style={{width:'100%',padding:'8px',borderRadius:8,border:'none',background:'transparent',color:'#64748b',cursor:'pointer',fontSize:12}}>Cancel</button>
              </div>
            </div>
          )}

          {/* ── BUSINESS ACCOUNT TAB ── */}
          {tab === 'business' && (
            <div>
              <div style={{fontSize:11,color:'#60a5fa',background:'#0a1a2e',borderRadius:7,padding:'8px 12px',marginBottom:14}}>
                🏢 A business account lets multiple contacts from the same company sign into the client portal and place orders under one account.
              </div>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:4}}>
                <div style={{gridColumn:'1/-1'}}>
                  <label style={lbl}>Company Name *</label>
                  <input style={inp} placeholder="Acme Concrete LLC" value={biz.company_name} onChange={e=>setBiz(f=>({...f,company_name:e.target.value}))} autoFocus />
                </div>
                <div>
                  <label style={lbl}>Business Email *</label>
                  <input style={inp} placeholder="info@company.com" value={biz.email} onChange={e=>setBiz(f=>({...f,email:e.target.value}))} />
                </div>
                <div>
                  <label style={lbl}>Phone</label>
                  <input style={inp} placeholder="(702) 555-0000" value={biz.phone} onChange={e=>setBiz(f=>({...f,phone:e.target.value}))} />
                </div>
                <div style={{gridColumn:'1/-1'}}>
                  <label style={lbl}>Address</label>
                  <input style={inp} placeholder="Street address" value={biz.address} onChange={e=>setBiz(f=>({...f,address:e.target.value}))} />
                </div>
                <div>
                  <label style={lbl}>City</label>
                  <input style={inp} placeholder="City" value={biz.city} onChange={e=>setBiz(f=>({...f,city:e.target.value}))} />
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                  <div>
                    <label style={lbl}>State</label>
                    <input style={inp} placeholder="NV" value={biz.state} onChange={e=>setBiz(f=>({...f,state:e.target.value}))} />
                  </div>
                  <div>
                    <label style={lbl}>ZIP</label>
                    <input style={inp} placeholder="89101" value={biz.zip} onChange={e=>setBiz(f=>({...f,zip:e.target.value}))} />
                  </div>
                </div>
                <div>
                  <label style={lbl}>Website</label>
                  <input style={inp} placeholder="https://company.com" value={biz.website} onChange={e=>setBiz(f=>({...f,website:e.target.value}))} />
                </div>
                <div>
                  <label style={lbl}>Tax ID / EIN</label>
                  <input style={inp} placeholder="XX-XXXXXXX" value={biz.tax_id} onChange={e=>setBiz(f=>({...f,tax_id:e.target.value}))} />
                </div>
              </div>

              {/* Account Discount */}
              <div style={{padding:'10px 12px',background:'#1e2235',borderRadius:8,border:'1px solid #1e3a1a',marginBottom:14}}>
                <div style={{fontSize:12,fontWeight:700,color:'#4ade80',marginBottom:8}}>🏷 Account Discount</div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <input type="number" style={{...inp,marginBottom:0,width:90}} min={0} max={100} placeholder="0" value={biz.discount_percent||''} onChange={e=>setBiz(f=>({...f,discount_percent:e.target.value}))} />
                  <span style={{fontSize:12,color:'#e2e8f0'}}>% off all portal orders for this company</span>
                </div>
              </div>

              {/* Insurance */}
              <div style={{padding:'10px 12px',background:'#1e2235',borderRadius:8,border:'1px solid #1a1d2e',marginBottom:14}}>
                <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                  <input type="checkbox" checked={biz.insurance_cert} onChange={e=>setBiz(f=>({...f,insurance_cert:e.target.checked}))} style={{accentColor:'#f97316'}} />
                  <span style={{fontSize:12,color:'#e2e8f0',fontWeight:600}}>🛡 Insurance certificate on file</span>
                </label>
                {biz.insurance_cert && (
                  <div style={{marginTop:8,display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                    <div>
                      <label style={lbl}>Discount %</label>
                      <input type="number" style={{...inp,marginBottom:0}} min={0} max={100} value={biz.insurance_discount||''} onChange={e=>setBiz(f=>({...f,insurance_discount:e.target.value}))} />
                    </div>
                    <div>
                      <label style={lbl}>Notes</label>
                      <input style={{...inp,marginBottom:0}} placeholder="Policy #, expiry..." value={biz.insurance_notes||''} onChange={e=>setBiz(f=>({...f,insurance_notes:e.target.value}))} />
                    </div>
                  </div>
                )}
              </div>

              {/* Contacts / Users */}
              <div style={{marginBottom:14}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <div style={{fontSize:12,fontWeight:700,color:'#e2e8f0'}}>👥 Authorized Contacts</div>
                  <button onClick={addUser} style={{padding:'3px 10px',borderRadius:5,border:'none',background:'#1e3a5f',color:'#60a5fa',fontSize:11,fontWeight:700,cursor:'pointer'}}>+ Add</button>
                </div>
                <div style={{fontSize:11,color:'#e2e8f0',marginBottom:8}}>These people can log into the client portal and place orders on behalf of the company.</div>
                {users.map((u,i) => (
                  <div key={i} style={{background:'#1e2235',border:'1px solid #1a1d2e',borderRadius:8,padding:'10px 12px',marginBottom:8}}>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:6}}>
                      <div>
                        <label style={lbl}>Name *</label>
                        <input style={inpErr(userErrors[i]?.name)} placeholder="Jane Doe" value={u.name} onChange={e=>updateUser(i,'name',e.target.value)} />
                        {userErrors[i]?.name && <div style={{color:'#ef4444',fontSize:10,marginTop:2}}>{userErrors[i].name}</div>}
                      </div>
                      <div>
                        <label style={lbl}>Email *</label>
                        <input style={inpErr(userErrors[i]?.email)} placeholder="jane@company.com" value={u.email} onChange={e=>updateUser(i,'email',e.target.value)} />
                        {userErrors[i]?.email && <div style={{color:'#ef4444',fontSize:10,marginTop:2}}>{userErrors[i].email}</div>}
                      </div>
                      <div>
                        <label style={lbl}>Phone</label>
                        <input style={{...inp,marginBottom:0}} placeholder="(702) 555-0000" value={u.phone} onChange={e=>updateUser(i,'phone',e.target.value)} />
                      </div>
                      <div>
                        <label style={lbl}>Role</label>
                        <select style={{...inp,marginBottom:0}} value={u.role} onChange={e=>updateUser(i,'role',e.target.value)}>
                          <option value="admin">Admin (full access)</option>
                          <option value="member">Member (view only)</option>
                        </select>
                      </div>
                    </div>
                    {users.length > 1 && (
                      <button onClick={()=>removeUser(i)} style={{fontSize:10,color:'#f87171',background:'none',border:'none',cursor:'pointer',padding:0}}>✕ Remove contact</button>
                    )}
                  </div>
                ))}
              </div>

              <div>
                <label style={lbl}>Internal Notes</label>
                <textarea style={{...inp,height:60,resize:'none',fontFamily:'inherit'}} placeholder="Credit terms, special rates, preferences..." value={biz.notes} onChange={e=>setBiz(f=>({...f,notes:e.target.value}))} />
              </div>

              {bizError && <div style={{background:'#2d1515',border:'1px solid #ef4444',borderRadius:7,padding:'8px 12px',marginBottom:10,color:'#f87171',fontSize:12}}>{bizError}</div>}
              <div style={{display:'flex',gap:10,marginTop:8}}>
                <button onClick={onClose} style={{flex:1,padding:'10px',borderRadius:8,border:'1px solid #2a3050',background:'transparent',color:'#e2e8f0',cursor:'pointer',fontSize:13}}>Cancel</button>
                <button onClick={saveBusiness} disabled={saving}
                  style={{flex:2,padding:'10px',borderRadius:8,border:'none',background:'#f97316',color:'#fff',cursor:'pointer',fontSize:13,fontWeight:700,opacity:saving?0.7:1}}>
                  {saving ? 'Creating…' : '🏢 Create Business Account'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── CLIENT QUICK-VIEW PANEL (draggable) ──────────────────────────────────────
function ClientPanel({ client, offset, onClose, onNewOrder, fmt, fmtDate, onCheckout, onReturn, onCollect }) {
  const [pos, setPos]       = React.useState({ x: window.innerWidth - 320 - offset * 28, y: 80 + offset * 28 });
  const [minimized, setMinimized] = React.useState(false);
  const [tab, setTab]       = React.useState('info');
  const [clientOrders, setClientOrders] = React.useState([]);
  const [loadingOrders, setLoadingOrders] = React.useState(false);

  // Fetch orders when Orders or Spend tab is opened
  React.useEffect(() => {
    if ((tab === 'orders' || tab === 'spend') && clientOrders.length === 0) {
      setLoadingOrders(true);
      import('./lib/axiosClient').then(({ default: axios }) => {
        axios.get(`/api/rentals/clients/${client.id}`)
          .then(r => setClientOrders(r.data.orders || []))
          .catch(() => {})
          .finally(() => setLoadingOrders(false));
      });
    }
  }, [tab, client.id]);

  function startDrag(e) {
    if (e.target.tagName === 'BUTTON' || e.target.tagName === 'SELECT') return;
    e.preventDefault();
    const sx = e.clientX - pos.x, sy = e.clientY - pos.y;
    function onMove(ev) { setPos({ x: Math.max(0, Math.min(window.innerWidth-300, ev.clientX-sx)), y: Math.max(0, Math.min(window.innerHeight-40, ev.clientY-sy)) }); }
    function onUp() { window.removeEventListener('mousemove',onMove); window.removeEventListener('mouseup',onUp); }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  const row    = { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'7px 0', borderBottom:'1px solid #0d0e1a' };
  const tabBtn = (t) => ({ padding:'5px 12px', borderRadius:6, border:'none', cursor:'pointer', fontSize:11, fontWeight:700, background: tab===t?'#f97316':'transparent', color: tab===t?'#fff':'#94a3b8' });

  const spent = clientOrders.reduce((s,o)=>s+Number(o.total||0),0);

  const panel = (
    <div style={{ position:'fixed', left:pos.x, top:pos.y, width:300, background:'#252838', border:'1px solid #2a3050', borderRadius:12, boxShadow:'0 8px 32px rgba(0,0,0,0.6)', zIndex: 2100+offset, userSelect:'none' }}>
      <div onMouseDown={startDrag} style={{ padding:'10px 14px', background:'#1e2235', borderRadius:'12px 12px 0 0', borderBottom:'1px solid #1a1d2e', display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'grab' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:32, height:32, borderRadius:'50%', background:'#1e3a5f', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color:'#60a5fa' }}>{(client.name||'?')[0].toUpperCase()}</div>
          <div>
            <div style={{ fontWeight:700, fontSize:13, color:'#e2e8f0' }}>{client.name}</div>
            {client.company && <div style={{ fontSize:10, color:'#e2e8f0' }}>{client.company}</div>}
          </div>
        </div>
        <div style={{ display:'flex', gap:6, alignItems:'center' }}>
          <button onClick={onNewOrder} style={{ padding:'3px 8px', borderRadius:5, border:'none', background:'#1a2e1a', color:'#4ade80', fontSize:10, fontWeight:700, cursor:'pointer' }}>+ Order</button>
          <button onClick={()=>setMinimized(m=>!m)} style={{ background:'none', border:'none', color:'#e2e8f0', cursor:'pointer', fontSize:14 }}>{minimized?'▲':'▼'}</button>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#e2e8f0', cursor:'pointer', fontSize:16 }}>✕</button>
        </div>
      </div>

      {!minimized && (
        <div style={{ padding:'10px 14px', maxHeight:440, overflowY:'auto' }}>
          <div style={{ display:'flex', gap:4, marginBottom:12, background:'#1e2235', borderRadius:7, padding:3 }}>
            {['info','orders','spend'].map(t=>(
              <button key={t} style={tabBtn(t)} onClick={()=>setTab(t)}>{t.charAt(0).toUpperCase()+t.slice(1)}</button>
            ))}
          </div>

          {tab==='info' && (
            <div>
              {[['Name',client.name],['Company',client.company],['Email',client.email],['Phone',client.phone],['Address',client.address],['City',client.city],['State',client.state],['DL #',client.dl_number],['Notes',client.notes]]
                .filter(([,v])=>v).map(([label,val])=>(
                <div key={label} style={row}>
                  <span style={{ fontSize:11, color:'#e2e8f0' }}>{label}</span>
                  <span style={{ fontSize:12, color:'#e2e8f0', maxWidth:180, textAlign:'right', wordBreak:'break-all' }}>{val}</span>
                </div>
              ))}
              <div style={{ marginTop:14, display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                <div style={{ background:'#1e2235', borderRadius:8, padding:'10px 12px', border:'1px solid #1a1d2e' }}>
                  <div style={{ fontSize:18, fontWeight:700, color:'#f97316' }}>{client.orders||0}</div>
                  <div style={{ fontSize:10, color:'#e2e8f0', textTransform:'uppercase' }}>Total Orders</div>
                </div>
                <div style={{ background:'#1e2235', borderRadius:8, padding:'10px 12px', border:'1px solid #1a1d2e' }}>
                  <div style={{ fontSize:18, fontWeight:700, color:'#34d399' }}>{fmt(client.total_spent||0)}</div>
                  <div style={{ fontSize:10, color:'#e2e8f0', textTransform:'uppercase' }}>Total Spent</div>
                </div>
              </div>
            </div>
          )}

          {tab==='orders' && (
            <div>
              {loadingOrders && <div style={{ textAlign:'center', color:'#e2e8f0', padding:20, fontSize:12 }}>Loading…</div>}
              {!loadingOrders && clientOrders.length===0 && <div style={{ textAlign:'center', color:'#94a3b8', padding:20, fontSize:12 }}>No orders yet</div>}
              {[...clientOrders].sort((a,b)=>new Date(b.from_date)-new Date(a.from_date)).map(o=>(
                <div key={o.id} style={{ padding:'9px 0', borderBottom:'1px solid #0d0e1a' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                    <span style={{ fontSize:11, color:'#e2e8f0', fontFamily:'monospace' }}>{o.order_number||'#'+o.id}</span>
                    <span style={{ fontSize:12, fontWeight:700, color:'#f97316' }}>{fmt(o.total)}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontSize:11, color:'#e2e8f0' }}>{fmtDate(o.from_date)} → {fmtDate(o.to_date)}</span>
                    <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                      <span style={{ fontSize:10, fontWeight:700, color: STATUS_COLOR[o.status]?.color||'#94a3b8', textTransform:'capitalize', marginRight:4 }}>{STATUS_LABEL[o.status]||o.status}</span>
                      {o.status==='reserved' && <button style={{padding:'2px 8px',borderRadius:4,border:'none',background:'#1a2e1a',color:'#4ade80',fontSize:10,fontWeight:700,cursor:'pointer'}} onClick={()=>onCheckout&&onCheckout(o)}>▶ Check Out</button>}
                      {o.status==='started' && <button style={{padding:'2px 8px',borderRadius:4,border:'none',background:'#0a1a2e',color:'#60a5fa',fontSize:10,fontWeight:700,cursor:'pointer'}} onClick={()=>onReturn&&onReturn(o)}>↩ Return</button>}
                      {['started','reserved'].includes(o.status) && o.payment_status!=='paid' && <button style={{padding:'2px 8px',borderRadius:4,border:'none',background:'#1a2e1a',color:'#4ade80',fontSize:10,fontWeight:700,cursor:'pointer'}} onClick={()=>onCollect&&onCollect(o)}>💰 Collect</button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab==='spend' && (
            <div>
              {loadingOrders && <div style={{ textAlign:'center', color:'#e2e8f0', padding:20, fontSize:12 }}>Loading…</div>}
              {!loadingOrders && [
                ['Total Orders',    clientOrders.length],
                ['Total Spent',     fmt(spent)],
                ['Avg Order Value', fmt(spent/(clientOrders.length||1))],
                ['Paid',           fmt(clientOrders.filter(o=>o.payment_status==='paid').reduce((s,o)=>s+Number(o.total),0))],
                ['Outstanding',    fmt(clientOrders.filter(o=>o.payment_status==='payment_due').reduce((s,o)=>s+Number(o.total),0))],
                ['Last Order',     fmtDate([...clientOrders].sort((a,b)=>new Date(b.from_date)-new Date(a.from_date))[0]?.from_date)||'—'],
              ].map(([label,val])=>(
                <div key={label} style={row}>
                  <span style={{ fontSize:11, color:'#e2e8f0' }}>{label}</span>
                  <span style={{ fontSize:12, fontWeight:600, color:'#e2e8f0' }}>{val}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
  return ReactDOM.createPortal(panel, document.body);
}

// ── ORDER QUICK-VIEW PANEL (draggable) ──────────────────────────────────────
function OrderPanel({ order, offset, onClose, onEdit, patchOrder, fmt, fmtDate, onCollect }) {
  const PAYMENT_LABEL    = { payment_due:'Payment Due', partially_paid:'Partially Paid', paid:'Paid', overpaid:'Overpaid', process_deposit:'Process Deposit' };
  const PAYMENT_STATUSES = ['payment_due','partially_paid','paid','overpaid','process_deposit'];
  // The panel may be opened from a dashboard row that lacks next_action /
  // events; fetch the full order and re-fetch after any pipeline action.
  const actions = useOrderActions();
  const [fresh, setFresh] = React.useState(null);
  React.useEffect(() => {
    let on = true;
    axios.get('/api/rentals/orders/' + order.id).then(r => { if (on) setFresh(r.data.order || null); }).catch(() => {});
    return () => { on = false; };
  }, [order.id, actions?.version]);
  const o = fresh || order;

  // Initial position — staggered from bottom-right
  const [pos, setPos] = React.useState({ x: window.innerWidth - 316 - offset * 24, y: window.innerHeight - 480 - offset * 24 });
  const [minimized, setMinimized] = React.useState(false);
  const dragRef = React.useRef(null);

  function startDrag(e) {
    if (e.target.tagName === 'BUTTON' || e.target.tagName === 'SELECT') return;
    e.preventDefault();
    const startX = e.clientX - pos.x;
    const startY = e.clientY - pos.y;
    function onMove(ev) {
      setPos({
        x: Math.max(0, Math.min(window.innerWidth  - 300, ev.clientX - startX)),
        y: Math.max(0, Math.min(window.innerHeight - 40,  ev.clientY - startY)),
      });
    }
    function onUp() {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
  }

  const inp = { padding:'6px 10px', borderRadius:6, border:'1px solid #2a3050', background:'#1e2235', color:'#e2e8f0', fontSize:12, width:'100%', boxSizing:'border-box' };
  const row = { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'7px 0', borderBottom:'1px solid #0d0e1a' };

  const panel = (
    <div ref={dragRef} data-testid="order-panel" data-order-id={order.id} style={{
      position:'fixed', left:pos.x, top:pos.y, width:300,
      background:'#252838', border:'1px solid #2a3050',
      borderRadius:12, boxShadow:'0 8px 32px rgba(0,0,0,0.6)',
      zIndex: 2000 + offset, userSelect:'none',
    }}>
      {/* Header — drag handle */}
      <div onMouseDown={startDrag} style={{
        padding:'10px 14px', background:'#1e2235', borderRadius:'12px 12px 0 0',
        borderBottom:'1px solid #1a1d2e', display:'flex', justifyContent:'space-between',
        alignItems:'center', cursor:'grab',
      }}>
        <div>
          <div style={{ fontWeight:700, fontSize:13, color:'#e2e8f0' }}>{order.client_name}</div>
          <div style={{ fontSize:10, color:'#e2e8f0' }}>Order #{order.id} · drag to move</div>
        </div>
        <div style={{ display:'flex', gap:6, alignItems:'center' }}>
          <button onClick={onEdit} style={{ padding:'3px 8px', borderRadius:5, border:'none', background:'#1e3a5f', color:'#60a5fa', fontSize:10, fontWeight:700, cursor:'pointer' }}>Edit</button>
          <button onClick={()=>setMinimized(m=>!m)} style={{ background:'none', border:'none', color:'#e2e8f0', cursor:'pointer', fontSize:14, lineHeight:1 }}>{minimized?'▲':'▼'}</button>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#e2e8f0', cursor:'pointer', fontSize:16, lineHeight:1 }}>✕</button>
        </div>
      </div>

      {/* Body */}
      {!minimized && (
        <div style={{ padding:'12px 14px', maxHeight:400, overflowY:'auto' }}>
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:10, fontWeight:700, color:'#f97316', textTransform:'uppercase', letterSpacing:'.4px', marginBottom:6 }}>Client</div>
            {[['Name',order.client_name],['Company',order.client_company],['Email',order.client_email],['Phone',order.client_phone],['Address',order.signer_address],['DL #',order.signer_dl_number]]
              .filter(([,v])=>v).map(([label,val])=>(
              <div key={label} style={row}>
                <span style={{ fontSize:11, color:'#e2e8f0' }}>{label}</span>
                <span style={{ fontSize:12, color:'#e2e8f0', fontWeight:500, maxWidth:180, textAlign:'right', wordBreak:'break-all' }}>{val}</span>
              </div>
            ))}
          </div>

          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:10, fontWeight:700, color:'#f97316', textTransform:'uppercase', letterSpacing:'.4px', marginBottom:6 }}>Rental</div>
            {[['From',fmtDate(order.from_date)],['To',fmtDate(order.to_date)],['Fulfillment',order.fulfillment_type],['Total',fmt(order.total)],['Deposit',order.deposit_amount?fmt(order.deposit_amount):null]]
              .filter(([,v])=>v).map(([label,val])=>(
              <div key={label} style={row}>
                <span style={{ fontSize:11, color:'#e2e8f0' }}>{label}</span>
                <span style={{ fontSize:12, color:label==='Total'?'#f97316':'#e2e8f0', fontWeight:label==='Total'?700:500 }}>{val}</span>
              </div>
            ))}
          </div>

          {(order.rental_order_items||[]).length > 0 && (
            <div style={{ marginBottom:10 }}>
              <div style={{ fontSize:10, fontWeight:700, color:'#f97316', textTransform:'uppercase', letterSpacing:'.4px', marginBottom:6 }}>Items</div>
              {(order.rental_order_items||[]).map((li,i)=>(
                <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid #0d0e1a', fontSize:11 }}>
                  <span style={{ color:'#e2e8f0' }}>{li.item_name} × {li.quantity}</span>
                  <span style={{ color:'#e2e8f0' }}>{fmt(li.total_price||li.subtotal)}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop:10 }}>
            <div style={{ fontSize:10, fontWeight:700, color:'#f97316', textTransform:'uppercase', letterSpacing:'.4px', marginBottom:6 }}>Quick Update</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:8 }}>
              <div>
                <div style={{ fontSize:10, color:'#e2e8f0', marginBottom:3 }}>Payment</div>
                {/* derived from the ledger (collect / refund) — the panel is a lookup tool, not a place to set money state */}
                <span data-testid="panel-payment-badge" style={{ fontSize:12, fontWeight:700, color:PAYMENT_COLOR[o.payment_status]||'#94a3b8' }}>{PAYMENT_LABEL[o.payment_status]||o.payment_status}{o.payment?.owed_to_client > 0 ? ` · owed ${fmt(o.payment.owed_to_client)}` : ''}</span>
              </div>
            </div>
          </div>

          {/* Pipeline: one primary action + ⋯ (status select removed) */}
          <div style={{ marginTop:10 }}><StageStrip order={o} compact /></div>
          <div style={{ marginTop:8, display:'flex', gap:6, alignItems:'stretch' }}>
            <div style={{ flex:1 }}><NextStepButton order={o} size="md" fullWidth /></div>
            <OrderMoreMenu order={o} actions={{ edit: onEdit, collect: onCollect ? ()=>onCollect(o) : undefined }} size="md" />
          </div>
          {o.signed_at && (
            <div style={{ marginTop:8, padding:'6px 10px', background:'#0c1a14', borderRadius:6, border:'1px solid #1a3a2a', fontSize:11, color:'#4ade80' }}>
              ✓ Signed {new Date(o.signed_at).toLocaleDateString()}
            </div>
          )}
          {order.notes && (
            <div style={{ marginTop:8, padding:'8px 10px', background:'#1e2235', borderRadius:6, border:'1px solid #1a1d2e', fontSize:11, color:'#e2e8f0', lineHeight:1.5 }}>
              {order.notes}
            </div>
          )}

          {/* ── Damage / Return Info ── */}
          {order.return_condition && order.return_condition !== 'good' && (
            <div style={{ marginTop:10, padding:'10px 12px', background:'rgba(245,158,11,0.06)', borderRadius:8, border:'1px solid rgba(245,158,11,0.2)' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#f59e0b', marginBottom:6, textTransform:'uppercase', letterSpacing:'.5px' }}>
                {order.return_condition === 'damaged' ? '⚠️ Damage Report' : '❌ Missing Parts Report'}
              </div>
              {order.return_notes && <div style={{ fontSize:12, color:'#e2e8f0', marginBottom:6, lineHeight:1.5 }}>{order.return_notes}</div>}
              {order.damage_charge > 0 && (
                <div style={{ fontSize:12, fontWeight:700, color:'#f87171' }}>💸 Damage Charge: ${Number(order.damage_charge).toFixed(2)}</div>
              )}
              {order.damage_photo_url && <DamagePhoto orderId={order.id} />}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Render into document.body so it's not clipped by any parent
  return ReactDOM.createPortal(panel, document.body);
}

// ── REPORT SECTION ───────────────────────────────────────────────────────────
function ReportSection({ orders, items }) {
  const [activeReport, setActiveReport] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');

  const inp = { padding:'7px 10px', borderRadius:6, border:'1px solid #2a3050', background:'#252838', color:'#e2e8f0', fontSize:12 };
  const th  = { padding:'8px 12px', textAlign:'left', fontSize:11, color:'#e2e8f0', fontWeight:600, background:'#1e2235', borderBottom:'1px solid #1a1d2e', textTransform:'uppercase' };
  const td  = { padding:'9px 12px', borderBottom:'1px solid #0d0e1a', fontSize:12, color:'#e2e8f0' };
  const fmt = n => '$' + Number(n||0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g,',');

  const today = new Date().toISOString().split('T')[0];

  // Default date range: current month
  const defaultFrom = dateFrom || (today.slice(0,8) + '01');
  const defaultTo   = dateTo   || today;

  // Every report and the summary cards read from these three, never from
  // the raw props:
  //  - liveOrders: parallel-run test orders (is_test) never count.
  //  - filtered:   liveOrders inside the period, cancelled excluded.
  //  - rentalItems: utilization/availability only make sense for rental
  //    products — sell items (tape, mixers, …) are left out.
  // Line items carry product_group_id (rental_item_id is a legacy alias that
  // no longer exists as a column); liKey reads whichever is present.
  const liveOrders  = orders.filter(o => !o.is_test); // period filter below also drops cancelled → same as lib/liveOrders isLiveOrder
  const isRentalProduct = i => !i.product_type || i.product_type === 'rental' || i.product_type === 'both';
  const rentalItems = items.filter(isRentalProduct);
  const liKey = li => li.product_group_id || li.rental_item_id || null;

  const filtered = orders.filter(o => {
    if (!isLiveOrder(o)) return false; // shared rule: no test orders, no cancelled
    if (dateFrom && o.from_date && o.from_date < dateFrom) return false;
    if (dateTo   && o.to_date   && o.to_date   > dateTo)   return false;
    return true;
  });

  // Summary cards — same period, same exclusions as every report below.
  const sumBy = (rows, f) => rows.reduce((s, o) => s + f(o), 0);
  const summary = {
    revenue:     sumBy(filtered, o => Number(o.total || 0)),
    collected:   sumBy(filtered, o => Number(o.amount_paid || 0)),
    outstanding: sumBy(filtered, o => Math.max(0, Number(o.total || 0) - Number(o.amount_paid || 0))),
    completed:   filtered.filter(o => o.status === 'returned' || o.status === 'completed').length,
    active:      filtered.filter(o => o.status === 'started').length,
  };

  // Quick date presets
  const setPreset = (preset) => {
    const now = new Date();
    if (preset === 'mtd') {
      setDateFrom(now.toISOString().slice(0,8) + '01');
      setDateTo(today);
    } else if (preset === 'last30') {
      const d = new Date(now - 30*86400000);
      setDateFrom(d.toISOString().slice(0,10));
      setDateTo(today);
    } else if (preset === 'last90') {
      const d = new Date(now - 90*86400000);
      setDateFrom(d.toISOString().slice(0,10));
      setDateTo(today);
    } else if (preset === 'ytd') {
      setDateFrom(now.getFullYear() + '-01-01');
      setDateTo(today);
    } else if (preset === 'all') {
      setDateFrom(''); setDateTo('');
    }
  };

  // ── Per-Item Revenue Breakdown ──────────────────────────────
  function ItemRevenue() {
    const itemMap = {};
    filtered.forEach(o => {
      (o.rental_order_items||[]).forEach(li => {
        const id  = liKey(li) || li.item_name || 'unknown';
        const nm  = li.item_name || items.find(i=>i.id===liKey(li))?.name || `Item ${id}`;
        const cat = items.find(i=>i.id===liKey(li))?.category || '—';
        if (!itemMap[id]) itemMap[id] = { id, name:nm, category:cat, rentals:0, units:0, revenue:0, paid:0, clients:new Set() };
        itemMap[id].rentals++;
        itemMap[id].units    += Number(li.quantity||1);
        itemMap[id].revenue  += Number(li.total_price||0);
        itemMap[id].clients.add(o.client_name);
        // Attribute collected amount proportionally
        const orderTotal = Number(o.total||0);
        const share = orderTotal > 0 ? Number(li.total_price||0) / orderTotal : 0;
        itemMap[id].paid += Number(o.amount_paid||0) * share;
      });
    });
    const rows = Object.values(itemMap).sort((a,b) => b.revenue - a.revenue);
    const grandRevenue = rows.reduce((s,r) => s+r.revenue, 0);
    const grandPaid    = rows.reduce((s,r) => s+r.paid, 0);

    return (
      <div>
        <div style={{display:'flex',gap:12,padding:'12px 16px',borderBottom:'1px solid #1a1d2e'}}>
          {[
            {l:'Total Billed', v:fmt(grandRevenue), c:'#f97316'},
            {l:'Collected',    v:fmt(grandPaid),    c:'#4ade80'},
            {l:'Items',        v:rows.length,        c:'#60a5fa'},
          ].map(({l,v,c})=>(
            <div key={l} style={{background:'#181928',borderRadius:8,padding:'10px 14px',flex:1}}>
              <div style={{fontSize:9,color:'#e2e8f0',textTransform:'uppercase',letterSpacing:'.4px',marginBottom:3}}>{l}</div>
              <div style={{fontSize:18,fontWeight:800,color:c}}>{v}</div>
            </div>
          ))}
        </div>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead><tr>
            <th style={th}>Item</th>
            <th style={th}>Category</th>
            <th style={th}>Rentals</th>
            <th style={th}>Units Out</th>
            <th style={th}>Unique Clients</th>
            <th style={th}>Billed</th>
            <th style={th}>Collected</th>
            <th style={th}>% of Revenue</th>
          </tr></thead>
          <tbody>
            {rows.map((r,i)=>{
              const revPct = grandRevenue > 0 ? Math.round(r.revenue/grandRevenue*100) : 0;
              return (
                <tr key={r.id} onMouseEnter={e=>e.currentTarget.style.background='#2a2d40'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <td style={td}><span style={{fontWeight:700,color:'#e2e8f0'}}>{r.name}</span></td>
                  <td style={td}><span style={{color:'#f97316',fontSize:11}}>{r.category}</span></td>
                  <td style={td}>{r.rentals}</td>
                  <td style={td}>{r.units}</td>
                  <td style={td}>{r.clients.size}</td>
                  <td style={td}><span style={{color:'#f97316',fontWeight:700}}>{fmt(r.revenue)}</span></td>
                  <td style={td}><span style={{color:'#4ade80',fontWeight:700}}>{fmt(r.paid)}</span></td>
                  <td style={td}>
                    <div style={{display:'flex',alignItems:'center',gap:6}}>
                      <div style={{background:'#2e3150',borderRadius:3,height:6,width:60,overflow:'hidden'}}>
                        <div style={{height:'100%',width:`${revPct}%`,background:'#f97316',borderRadius:3}} />
                      </div>
                      <span style={{fontSize:11,color:'#e2e8f0'}}>{revPct}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
            {rows.length===0&&<tr><td colSpan={8} style={{...td,textAlign:'center',color:'#94a3b8',padding:30}}>No data for this period</td></tr>}
          </tbody>
        </table>
      </div>
    );
  }

  // ── Utilization Over Time ────────────────────────────────────
  function UtilizationOverTime() {
    // Build weekly buckets between dateFrom and dateTo
    const start = new Date((dateFrom || defaultFrom) + 'T00:00:00');
    const end   = new Date((dateTo   || defaultTo)   + 'T23:59:59');
    const weeks = [];
    let cur = new Date(start);
    while (cur <= end) {
      const wkStart = new Date(cur);
      const wkEnd   = new Date(cur); wkEnd.setDate(wkEnd.getDate() + 6);
      const wkStartStr = wkStart.toISOString().slice(0,10);
      const wkEndStr   = (wkEnd > end ? end : wkEnd).toISOString().slice(0,10);
      weeks.push({ label: wkStartStr, from: wkStartStr, to: wkEndStr });
      cur.setDate(cur.getDate() + 7);
    }

    // For each item, count active order-days per week
    const utilizationByItem = rentalItems.map(item => {
      const weekData = weeks.map(wk => {
        let unitsOut = 0;
        liveOrders.forEach(o => {
          if (!o.from_date || !o.to_date) return;
          if (['cancelled'].includes(o.status)) return;
          if (o.from_date > wk.to || o.to_date < wk.from) return;
          const li = (o.rental_order_items||[]).find(l => liKey(l) === item.id);
          if (li) unitsOut += Number(li.quantity||1);
        });
        const pct = item.quantity_total > 0 ? Math.min(100, Math.round(unitsOut / item.quantity_total * 100)) : 0;
        return { label: wk.label, unitsOut, pct };
      });
      return { item, weekData };
    });

    const maxWeeksToShow = 12;
    const shownWeeks = weeks.slice(-maxWeeksToShow);

    return (
      <div style={{padding:'14px 16px'}}>
        <div style={{fontSize:12,color:'#e2e8f0',marginBottom:16}}>Weekly utilization heatmap — each column is one week, darker = higher utilization</div>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',minWidth:500}}>
            <thead>
              <tr>
                <th style={{...th,width:160}}>Item</th>
                {shownWeeks.map(wk=>(
                  <th key={wk.label} style={{...th,textAlign:'center',padding:'6px 4px',fontSize:9,minWidth:36}}>
                    {new Date(wk.label+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})}
                  </th>
                ))}
                <th style={th}>Avg %</th>
              </tr>
            </thead>
            <tbody>
              {utilizationByItem.map(({item, weekData})=>{
                const shownData = weekData.slice(-maxWeeksToShow);
                const avg = shownData.length > 0 ? Math.round(shownData.reduce((s,d)=>s+d.pct,0)/shownData.length) : 0;
                return (
                  <tr key={item.id} onMouseEnter={e=>e.currentTarget.style.background='#2a2d40'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{...td,fontWeight:600,color:'#e2e8f0',fontSize:11}}>{item.name}</td>
                    {shownData.map((d,i)=>{
                      const alpha = d.pct/100;
                      const bg = d.pct===0 ? 'transparent' :
                                 d.pct>=80  ? `rgba(239,68,68,${0.3+alpha*0.7})` :
                                 d.pct>=50  ? `rgba(245,158,11,${0.3+alpha*0.7})` :
                                              `rgba(34,197,94,${0.2+alpha*0.6})`;
                      return (
                        <td key={i} style={{...td,textAlign:'center',padding:'6px 4px',background:bg}} title={`${d.pct}% (${d.unitsOut} out)`}>
                          <span style={{fontSize:10,fontWeight:d.pct>0?700:400,color:d.pct>0?'#fff':'#3a3f60'}}>{d.pct>0?`${d.pct}%`:''}</span>
                        </td>
                      );
                    })}
                    <td style={{...td,textAlign:'center'}}>
                      <span style={{fontWeight:700,color:avg>=80?'#f87171':avg>=50?'#fbbf24':'#4ade80'}}>{avg}%</span>
                    </td>
                  </tr>
                );
              })}
              {utilizationByItem.length===0&&<tr><td colSpan={shownWeeks.length+2} style={{...td,textAlign:'center',color:'#94a3b8',padding:30}}>No inventory found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ── Product Performance ──────────────────────────────────────
  function ProductPerformance() {
    const itemMap = {};
    filtered.forEach(o => {
      (o.rental_order_items||[]).forEach(li => {
        const key = liKey(li) || li.item_name || null;
        if (!key) return;
        const nm = li.item_name || items.find(i=>i.id===liKey(li))?.name || key;
        if (!itemMap[key]) itemMap[key] = { name:nm, count:0, revenue:0, orders:0 };
        itemMap[key].count   += Number(li.quantity||1);
        itemMap[key].revenue += Number(li.total_price||li.unit_price||li.subtotal||0);
        itemMap[key].orders  += 1;
      });
    });
    const rows = Object.values(itemMap).sort((a,b)=>b.orders-a.orders||b.revenue-a.revenue);
    const maxRev = Math.max(...rows.map(r=>r.revenue), 1);
    return (
      <table style={{width:'100%',borderCollapse:'collapse'}}>
        <thead><tr>
          <th style={th}>Product</th><th style={th}>Times Rented</th><th style={th}>Total Units</th><th style={th}>Revenue</th><th style={th}>Bar</th>
        </tr></thead>
        <tbody>
          {rows.map((r,i)=>(
            <tr key={i} onMouseEnter={e=>e.currentTarget.style.background='#2a2d40'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <td style={td}><span style={{fontWeight:600,color:'#e2e8f0'}}>{r.name}</span></td>
              <td style={td}>{r.orders}</td>
              <td style={td}>{r.count}</td>
              <td style={td}><span style={{color:'#f97316',fontWeight:700}}>{fmt(r.revenue)}</span></td>
              <td style={{...td,width:120}}>
                <div style={{background:'#2e3150',borderRadius:4,height:6,overflow:'hidden'}}>
                  <div style={{height:'100%',width:`${Math.round(r.revenue/maxRev*100)}%`,background:'linear-gradient(90deg,#f97316,#fb923c)',borderRadius:4}} />
                </div>
              </td>
            </tr>
          ))}
          {rows.length===0&&<tr><td colSpan={5} style={{...td,textAlign:'center',color:'#94a3b8',padding:30}}>No data for this period</td></tr>}
        </tbody>
      </table>
    );
  }

  // ── Availability ─────────────────────────────────────────────
  function Availability() {
    const from = dateFrom || today;
    const to   = dateTo   || today;
    return (
      <table style={{width:'100%',borderCollapse:'collapse'}}>
        <thead><tr>
          <th style={th}>Equipment</th><th style={th}>Total</th><th style={th}>Booked in Period</th><th style={th}>Available</th><th style={th}>Util %</th>
        </tr></thead>
        <tbody>
          {rentalItems.filter(i=>i.is_active!==false).map(item=>{
            const booked = liveOrders.filter(o => {
              const oFrom = o.from_date||''; const oTo = o.to_date||'';
              return oFrom<=to && oTo>=from && !['cancelled','returned'].includes(o.status) &&
                (o.rental_order_items||[]).some(li=>li.product_group_id===item.id||li.rental_item_id===item.id);
            }).reduce((s,o)=>{
              const li = (o.rental_order_items||[]).find(l=>l.product_group_id===item.id||l.rental_item_id===item.id);
              return s + Number(li?.quantity||1);
            },0);
            const total = Number(item.quantity_total||1);
            const inRepair = (item.variants||[]).filter(v=>['repair','maintenance'].includes(v.unit_status)).length;
            const avail = Math.max(0, total - booked - inRepair);
            const pct   = total > 0 ? Math.round(booked/total*100) : 0;
            return (
              <tr key={item.id} onMouseEnter={e=>e.currentTarget.style.background='#2a2d40'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <td style={td}><span style={{fontWeight:600}}>{item.name}</span></td>
                <td style={td}>{total}</td>
                <td style={td}><span style={{color:'#f97316'}}>{booked}</span></td>
                <td style={td}><span style={{color:avail===0?'#f87171':'#4ade80',fontWeight:700}}>{avail}</span></td>
                <td style={td}>
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    <div style={{background:'#2e3150',borderRadius:3,height:6,width:60,overflow:'hidden'}}>
                      <div style={{height:'100%',width:`${pct}%`,background:pct>=80?'#ef4444':pct>=50?'#f59e0b':'#22c55e'}} />
                    </div>
                    <span style={{fontSize:10,color:'#e2e8f0'}}>{pct}%</span>
                  </div>
                </td>
              </tr>
            );
          })}
          {items.length===0&&<tr><td colSpan={5} style={{...td,textAlign:'center',color:'#94a3b8',padding:30}}>No inventory found</td></tr>}
        </tbody>
      </table>
    );
  }

  // ── Revenue by Client ─────────────────────────────────────────
  function Revenue() {
    const [clientSearch, setClientSearch] = React.useState('');
    const byClient = {};
    filtered.forEach(o => {
      const k = o.client_name||'Unknown';
      if (!byClient[k]) byClient[k] = { name:k, orders:0, paid:0, due:0, total:0, tax:0 };
      byClient[k].orders++;
      byClient[k].total += Number(o.total||0);
      byClient[k].paid  += Number(o.amount_paid||0);
      byClient[k].due   += Math.max(0, Number(o.total||0) - Number(o.amount_paid||0));
      byClient[k].tax   += Number(o.tax_amount||0);
    });
    const allRows    = Object.values(byClient).sort((a,b)=>b.total-a.total);
    const rows       = clientSearch ? allRows.filter(r=>r.name.toLowerCase().includes(clientSearch.toLowerCase())) : allRows;
    const isInactive  = (c) => c.is_active === false;
    const grandTotal = filtered.reduce((s,o)=>s+Number(o.total||0),0);
    const grandPaid  = filtered.reduce((s,o)=>s+Number(o.amount_paid||0),0);
    const grandDue   = filtered.reduce((s,o)=>s+Math.max(0,Number(o.total||0)-Number(o.amount_paid||0)),0);
    const grandTax   = filtered.reduce((s,o)=>s+Number(o.tax_amount||0),0);
    const exportPDF = async () => {
      const bizInfo = await getBizInfo();
      const w = window.open('', '_blank');
      const periodLabel = dateFrom && dateTo ? dateFrom+' to '+dateTo : 'All Time';
      w.document.write('<!DOCTYPE html><html><head><title>Revenue by Client</title><style>body{font-family:Arial,sans-serif;padding:20px;color:#111}h1{font-size:18px;margin-bottom:4px}.sub{font-size:12px;color:#666;margin-bottom:16px}.summary{display:flex;gap:16px;margin-bottom:20px}.card{background:#f5f5f5;border-radius:6px;padding:12px 16px;flex:1}.card-label{font-size:10px;text-transform:uppercase;color:#888;margin-bottom:4px}.card-value{font-size:20px;font-weight:800}table{width:100%;border-collapse:collapse;font-size:13px}th{background:#f0f0f0;padding:8px 12px;text-align:left;font-size:11px;text-transform:uppercase;color:#666;border-bottom:2px solid #ddd}td{padding:8px 12px;border-bottom:1px solid #eee}.total-row td{font-weight:700;background:#fafafa;border-top:2px solid #ddd}button{margin-bottom:16px;padding:8px 16px;background:#f97316;color:#fff;border:none;border-radius:6px;cursor:pointer;font-weight:700}@media print{button{display:none}}</style></head><body>');
      w.document.write('<button onclick="window.print()">Print / Save PDF</button>');
      w.document.write('<h1>Revenue by Client — '+(bizInfo.name)+'</h1>');
      w.document.write('<div class="sub">Period: '+periodLabel+' &nbsp;|&nbsp; Generated: '+new Date().toLocaleDateString()+'</div>');
      w.document.write('<div class="summary"><div class="card"><div class="card-label">Total Billed</div><div class="card-value" style="color:#f97316">'+fmt(grandTotal)+'</div></div><div class="card"><div class="card-label">Collected</div><div class="card-value" style="color:#16a34a">'+fmt(grandPaid)+'</div></div><div class="card"><div class="card-label">Outstanding</div><div class="card-value" style="color:#dc2626">'+fmt(grandDue)+'</div></div><div class="card"><div class="card-label">Tax Collected</div><div class="card-value" style="color:#7c3aed">'+fmt(grandTax)+'</div></div></div>');
      w.document.write('<table><thead><tr><th>Client</th><th>Orders</th><th>Billed</th><th>Collected</th><th>Outstanding</th><th>Tax</th></tr></thead><tbody>');
      rows.forEach(r => { w.document.write('<tr><td><strong>'+r.name+'</strong></td><td>'+r.orders+'</td><td>'+fmt(r.total)+'</td><td>'+fmt(r.paid)+'</td><td style="color:'+(r.due>0?'#dc2626':'#666')+'">'+fmt(r.due)+'</td><td>'+fmt(r.tax)+'</td></tr>'); });
      w.document.write('<tr class="total-row"><td>TOTAL</td><td>'+rows.reduce((s,r)=>s+r.orders,0)+'</td><td>'+fmt(rows.reduce((s,r)=>s+r.total,0))+'</td><td>'+fmt(rows.reduce((s,r)=>s+r.paid,0))+'</td><td>'+fmt(rows.reduce((s,r)=>s+r.due,0))+'</td><td>'+fmt(rows.reduce((s,r)=>s+r.tax,0))+'</td></tr>');
      w.document.write('</tbody></table></body></html>');
      w.document.close();
    };
    return (
      <div>
        <div style={{display:'flex',gap:12,padding:'12px 16px',borderBottom:'1px solid #1a1d2e',flexWrap:'wrap'}}>
          {[{l:'Total Billed',v:fmt(grandTotal),c:'#f97316'},{l:'Collected',v:fmt(grandPaid),c:'#4ade80'},{l:'Outstanding',v:fmt(grandDue),c:'#f87171'},{l:'Tax Collected',v:fmt(grandTax),c:'#a78bfa'}].map(({l,v,c})=>(
            <div key={l} style={{background:'#181928',borderRadius:8,padding:'10px 14px',flex:1,minWidth:120}}>
              <div style={{fontSize:9,color:'#e2e8f0',textTransform:'uppercase',letterSpacing:'.4px',marginBottom:3}}>{l}</div>
              <div style={{fontSize:18,fontWeight:800,color:c}}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{display:'flex',gap:10,padding:'10px 16px',borderBottom:'1px solid #1a1d2e',alignItems:'center'}}>
          <input value={clientSearch} onChange={e=>setClientSearch(e.target.value)} placeholder="Search client..." style={{background:'#252838',border:'1px solid #2a2d3e',borderRadius:6,padding:'6px 12px',color:'#e2e8f0',fontSize:12,flex:1,maxWidth:280}} />
          <span style={{fontSize:11,color:'#555'}}>{rows.length} clients</span>
          <button onClick={exportPDF} style={{background:'#7c3aed',border:'none',color:'#fff',borderRadius:6,padding:'6px 14px',fontSize:12,cursor:'pointer',fontWeight:700,marginLeft:'auto'}}>Export PDF</button>
        </div>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead><tr>
            <th style={th}>Client</th><th style={th}>Orders</th><th style={th}>Billed</th><th style={th}>Collected</th><th style={th}>Outstanding</th><th style={th}>Tax</th>
          </tr></thead>
          <tbody>
            {rows.map((r,i)=>(
              <tr key={i} onMouseEnter={e=>e.currentTarget.style.background='#2a2d40'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <td style={td}><span style={{fontWeight:600}}>{r.name}</span></td>
                <td style={td}>{r.orders}</td>
                <td style={td}><span style={{color:'#f97316',fontWeight:700}}>{fmt(r.total)}</span></td>
                <td style={td}><span style={{color:'#4ade80'}}>{fmt(r.paid)}</span></td>
                <td style={td}><span style={{color:r.due>0?'#f87171':'#94a3b8',fontWeight:r.due>0?700:400}}>{fmt(r.due)}</span></td>
                <td style={td}><span style={{color:'#a78bfa'}}>{fmt(r.tax)}</span></td>
              </tr>
            ))}
            {rows.length===0&&<tr><td colSpan={6} style={{...td,textAlign:'center',color:'#94a3b8',padding:30}}>No data for this period</td></tr>}
          </tbody>
        </table>
      </div>
    );
  }

  // ── Product Fulfillment ──────────────────────────────────────
  function ProductFulfillment() {
    const byType = { pickup:0, delivery:0 };
    filtered.forEach(o => { byType[o.fulfillment_type] = (byType[o.fulfillment_type]||0)+1; });
    return (
      <div>
        <div style={{display:'flex',gap:12,padding:'12px 16px',borderBottom:'1px solid #1a1d2e'}}>
          {[{l:'Delivery',v:byType.delivery||0,c:'#60a5fa'},{l:'Pickup',v:byType.pickup||0,c:'#f97316'},{l:'Total',v:filtered.length,c:'#e2e8f0'}].map(({l,v,c})=>(
            <div key={l} style={{background:'#181928',borderRadius:8,padding:'10px 14px',flex:1}}>
              <div style={{fontSize:9,color:'#e2e8f0',textTransform:'uppercase',letterSpacing:'.4px',marginBottom:3}}>{l}</div>
              <div style={{fontSize:18,fontWeight:800,color:c}}>{v}</div>
            </div>
          ))}
        </div>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead><tr>
            <th style={th}>#</th><th style={th}>Client</th><th style={th}>Type</th><th style={th}>From</th><th style={th}>To</th><th style={th}>Items</th><th style={th}>Total</th><th style={th}>Status</th>
          </tr></thead>
          <tbody>
            {filtered.map(o=>(
              <tr key={o.id} onMouseEnter={e=>e.currentTarget.style.background='#2a2d40'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <td style={td}><span style={{color:'#e2e8f0',fontFamily:'monospace',fontWeight:700}}>{o.order_number||'#'+o.id}</span></td>
                <td style={td}><span style={{fontWeight:600, color:'#e2e8f0'}}>{o.client_name}</span></td>
                <td style={td}><span style={{textTransform:'capitalize',color:'#e2e8f0'}}>{o.fulfillment_type}</span></td>
                <td style={td}><span style={{color:'#e2e8f0'}}>{o.from_date}</span></td>
                <td style={td}><span style={{color:'#e2e8f0'}}>{o.to_date}</span></td>
                <td style={td}>{(o.rental_order_items||[]).length}</td>
                <td style={td}><span style={{color:'#f97316',fontWeight:700}}>{fmt(o.total)}</span></td>
                <td style={td}><span style={{padding:'2px 8px',borderRadius:4,fontSize:11,background:'#1a2a4a',color:'#60a5fa',fontWeight:600}}>{o.status}</span></td>
              </tr>
            ))}
            {filtered.length===0&&<tr><td colSpan={8} style={{...td,textAlign:'center',color:'#94a3b8',padding:30}}>No data for this period</td></tr>}
          </tbody>
        </table>
      </div>
    );
  }

  const exportCSV = (reportId) => {
    let rows = [];
    const dl = (filename, data) => {
      const csv = data.map(r => r.map(cell => {
        const s = String(cell == null ? '' : cell);
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g,'""')}"` : s;
      }).join(',')).join('\n');
      const blob = new Blob([csv], { type:'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    };

    if (reportId === 'item_revenue') {
      const itemMap = {};
      filtered.forEach(o => {
        (o.rental_order_items||[]).forEach(li => {
          const id = liKey(li) || li.item_name || 'unknown';
          const nm = li.item_name || items.find(i=>i.id===id)?.name || `Item ${id}`;
          const cat = items.find(i=>i.id===id)?.category || '';
          if (!itemMap[id]) itemMap[id] = { name:nm, category:cat, rentals:0, units:0, revenue:0, paid:0 };
          itemMap[id].rentals++; itemMap[id].units += Number(li.quantity||1);
          itemMap[id].revenue += Number(li.total_price||0);
          const share = Number(o.total||0) > 0 ? Number(li.total_price||0)/Number(o.total||0) : 0;
          itemMap[id].paid += Number(o.amount_paid||0) * share;
        });
      });
      rows = [['Item','Category','Rentals','Units Out','Revenue','Collected'],
        ...Object.values(itemMap).sort((a,b)=>b.revenue-a.revenue)
          .map(r=>[r.name, r.category, r.rentals, r.units, r.revenue.toFixed(2), r.paid.toFixed(2)])];
      dl(`item-revenue-${dateFrom||'all'}-${dateTo||'today'}.csv`, rows);

    } else if (reportId === 'performance') {
      const itemMap = {};
      filtered.forEach(o => (o.rental_order_items||[]).forEach(li => {
        const k = liKey(li) || li.item_name;
        if (!itemMap[k]) itemMap[k] = { name:k, orders:0, units:0, revenue:0 };
        itemMap[k].orders++; itemMap[k].units += Number(li.quantity||1);
        itemMap[k].revenue += Number(li.total_price||0);
      }));
      rows = [['Product','Times Rented','Total Units','Revenue'],
        ...Object.values(itemMap).sort((a,b)=>b.revenue-a.revenue)
          .map(r=>[r.name, r.orders, r.units, r.revenue.toFixed(2)])];
      dl(`performance-${dateFrom||'all'}.csv`, rows);

    } else if (reportId === 'availability') {
      const from = dateFrom || today; const to = dateTo || today;
      rows = [['Equipment','Category','Total Units','Booked in Period','Available','Utilization %']];
      rentalItems.filter(i=>i.is_active!==false).forEach(item => {
        const booked = liveOrders.filter(o => {
          const oFrom=o.from_date||'',oTo=o.to_date||'';
          return oFrom<=to&&oTo>=from&&!['cancelled','returned'].includes(o.status)&&
            (o.rental_order_items||[]).some(l=>l.product_group_id===item.id||l.rental_item_id===item.id);
        }).reduce((s,o)=>{const l=(o.rental_order_items||[]).find(x=>x.product_group_id===item.id||x.rental_item_id===item.id);return s+Number(l?.quantity||1);},0);
        const total=Number(item.quantity_total||1), avail=Math.max(0,total-booked), pct=total>0?Math.round(booked/total*100):0;
        rows.push([item.name, item.category||'', total, booked, avail, pct+'%']);
      });
      dl(`availability-${from}-${to}.csv`, rows);

    } else if (reportId === 'fulfillment') {
      rows = [['Order #','Client','Email','Phone','Fulfillment','From','To','Items','Total','Status','Payment Status']];
      filtered.forEach(o => rows.push([
        o.id, o.client_name||'', o.client_email||'', o.client_phone||'',
        o.fulfillment_type||'', o.from_date||'', o.to_date||'',
        (o.rental_order_items||[]).length, Number(o.total||0).toFixed(2),
        o.status, o.payment_status
      ]));
      dl(`fulfillment-${dateFrom||'all'}.csv`, rows);

    } else if (reportId === 'revenue') {
      const byClient = {};
      filtered.forEach(o => {
        const k = o.client_name||'Unknown';
        if (!byClient[k]) byClient[k] = { name:k, orders:0, total:0, paid:0, due:0 };
        byClient[k].orders++; byClient[k].total += Number(o.total||0);
        byClient[k].paid += Number(o.amount_paid||0);
        byClient[k].due  += Math.max(0,Number(o.total||0)-Number(o.amount_paid||0));
      });
      rows = [['Client','Orders','Total Billed','Collected','Outstanding'],
        ...Object.values(byClient).sort((a,b)=>b.total-a.total)
          .map(r=>[r.name, r.orders, r.total.toFixed(2), r.paid.toFixed(2), r.due.toFixed(2)])];
      dl(`revenue-by-client-${dateFrom||'all'}.csv`, rows);

    } else if (reportId === 'utilization') {
      // Flat: item + week columns
      const start = new Date((dateFrom||defaultFrom)+'T00:00:00');
      const end   = new Date((dateTo||defaultTo)+'T23:59:59');
      const weeks = [];
      let cur = new Date(start);
      while (cur <= end) {
        const ws = cur.toISOString().slice(0,10);
        const we = new Date(cur); we.setDate(we.getDate()+6);
        weeks.push({ from:ws, to:(we>end?end:we).toISOString().slice(0,10) });
        cur.setDate(cur.getDate()+7);
      }
      const header = ['Item','Category',...weeks.map(w=>w.from),'Avg %'];
      rows = [header];
      rentalItems.forEach(item => {
        const weekPcts = weeks.map(wk => {
          let out=0;
          liveOrders.forEach(o => {
            if (!o.from_date||!o.to_date||o.status==='cancelled') return;
            if (o.from_date>wk.to||o.to_date<wk.from) return;
            const li=(o.rental_order_items||[]).find(l=>liKey(l)===item.id);
            if(li) out+=Number(li.quantity||1);
          });
          return item.quantity_total>0?Math.min(100,Math.round(out/item.quantity_total*100)):0;
        });
        const avg = weekPcts.length>0?Math.round(weekPcts.reduce((s,p)=>s+p,0)/weekPcts.length):0;
        rows.push([item.name, item.category||'', ...weekPcts.map(p=>p+'%'), avg+'%']);
      });
      dl(`utilization-${dateFrom||'all'}.csv`, rows);
    }
  };

  const reports = [
    { id:'item_revenue',   title:'Item Revenue Breakdown', icon:'💎', desc:'Revenue, collections and utilization per piece of equipment.' },
    { id:'utilization',    title:'Utilization Over Time',  icon:'📊', desc:'Weekly utilization heatmap for every item in your fleet.' },
    { id:'performance',    title:'Product Performance',    icon:'📦', desc:'How often each item is rented and how much it earns.' },
    { id:'availability',   title:'Availability',           icon:'📅', desc:'Check availability and shortages for any date range.' },
    { id:'fulfillment',    title:'Product Fulfillment',    icon:'🚚', desc:'All orders by fulfillment type with line-item detail.' },
    { id:'revenue',        title:'Revenue by Client',      icon:'💰', desc:'Full revenue breakdown by client — billed vs collected.' },
  ];

  return (
    <div>
      {/* Period — governs the summary cards and every report */}
      <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:12,padding:'10px 14px',background:'#252838',borderRadius:8,border:'1px solid #1a1d2e',flexWrap:'wrap'}}>
        <span style={{fontSize:12,color:'#e2e8f0',fontWeight:600,flexShrink:0}}>Period:</span>
        {[['mtd','This Month'],['last30','Last 30d'],['last90','Last 90d'],['ytd','YTD'],['all','All Time']].map(([p,l])=>(
          <button key={p} onClick={()=>setPreset(p)}
            style={{padding:'4px 10px',borderRadius:5,border:'1px solid #2a3050',background:'#1e2235',color:'#e2e8f0',fontSize:11,cursor:'pointer'}}>
            {l}
          </button>
        ))}
        <div style={{display:'flex',alignItems:'center',gap:6,marginLeft:4}}>
          <input style={inp} type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} />
          <span style={{color:'#e2e8f0'}}>→</span>
          <input style={inp} type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} />
        </div>
        {(dateFrom||dateTo)&&<button onClick={()=>{setDateFrom('');setDateTo('');}} style={{padding:'4px 10px',borderRadius:5,border:'none',background:'#3a3f60',color:'#e2e8f0',fontSize:11,cursor:'pointer'}}>Clear</button>}
        <span style={{fontSize:11,color:'#e2e8f0',marginLeft:'auto'}}>{filtered.length} orders</span>
      </div>

      {/* Summary — period-filtered, cancelled and test orders excluded */}
      <div style={{display:'flex',gap:14,marginBottom:16}}>
        {[
          {label:'Total Revenue', val:fmt(summary.revenue),     color:'#f97316'},
          {label:'Collected',     val:fmt(summary.collected),   color:'#34d399'},
          {label:'Outstanding',   val:fmt(summary.outstanding), color:'#f87171'},
          {label:'Completed',     val:summary.completed,        color:'#e2e8f0'},
          {label:'Active',        val:summary.active,           color:'#60a5fa'},
        ].map(m=>(
          <div key={m.label} style={{flex:1,background:'#252838',border:'1px solid #1e293b',borderRadius:10,padding:'14px 16px'}}>
            <div style={{fontSize:20,fontWeight:800,color:m.color}}>{m.val}</div>
            <div style={{fontSize:10,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'.4px',marginTop:2}}>{m.label}</div>
          </div>
        ))}
      </div>

      {/* Report grid */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:16}}>
        {reports.map(r=>(
          <div key={r.id} onClick={()=>setActiveReport(activeReport===r.id?null:r.id)}
            style={{background:activeReport===r.id?'rgba(249,115,22,0.08)':'#252838',border:`1px solid ${activeReport===r.id?'#f97316':'#2e3150'}`,borderRadius:10,padding:'12px 14px',cursor:'pointer',transition:'all .15s'}}
            onMouseEnter={e=>{if(activeReport!==r.id){e.currentTarget.style.borderColor='#3a3f60';e.currentTarget.style.background='#2a2d40';}}}
            onMouseLeave={e=>{if(activeReport!==r.id){e.currentTarget.style.borderColor='#2e3150';e.currentTarget.style.background='#252838';}}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
              <span style={{fontSize:18}}>{r.icon}</span>
              <span style={{fontWeight:700,fontSize:12,color:'#e2e8f0'}}>{r.title}</span>
            </div>
            <div style={{fontSize:11,color:'#e2e8f0',marginBottom:6,lineHeight:1.4}}>{r.desc}</div>
            <div style={{fontSize:10,fontWeight:700,color:activeReport===r.id?'#f97316':'#94a3b8'}}>{activeReport===r.id?'▼ Showing':'▶ Run'}</div>
          </div>
        ))}
      </div>


      {/* Output */}
      {activeReport && (
        <div style={{background:'#252838',border:'1px solid #1a1d2e',borderRadius:10,overflow:'hidden'}}>
          <div style={{padding:'12px 16px',borderBottom:'1px solid #1a1d2e',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div style={{fontSize:13,fontWeight:700,color:'#f97316'}}>
              {reports.find(r=>r.id===activeReport)?.icon} {reports.find(r=>r.id===activeReport)?.title}
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <span style={{fontSize:11,color:'#e2e8f0'}}>{dateFrom||'start'} → {dateTo||'today'}</span>
              <button onClick={()=>exportCSV(activeReport)}
                style={{padding:'5px 12px',borderRadius:6,border:'1px solid #2a3050',background:'#1e2235',color:'#4ade80',fontSize:11,cursor:'pointer',fontWeight:700}}>
                ⬇ Export CSV
              </button>
            </div>
          </div>
          <div style={{padding:0}}>
            {activeReport==='item_revenue'  && <ItemRevenue />}
            {activeReport==='utilization'   && <UtilizationOverTime />}
            {activeReport==='performance'   && <ProductPerformance />}
            {activeReport==='availability'  && <Availability />}
            {activeReport==='fulfillment'   && <ProductFulfillment />}
            {activeReport==='revenue'       && <Revenue />}
          </div>
        </div>
      )}
    </div>
  );
}


// ── RecurringView ─────────────────────────────────────────────
function RecurringView() {
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm] = React.useState({ client_name:'', client_email:'', client_phone:'', from_date:'', to_date:'', recurrence_interval:'weekly', recurrence_end_date:'', total:'', notes:'', items:[{item_name:'',quantity:1,unit_price:0,total_price:0}] });
  const [saving, setSaving] = React.useState(false);
  const [flash, setFlash] = React.useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/rental-recurring');
      setOrders(r.data.orders || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  };
  React.useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.client_name || !form.from_date || !form.to_date) return;
    setSaving(true);
    try {
      await axios.post('/api/rental-recurring', { ...form, total: Number(form.total)||0 });
      setFlash('Recurring order created!');
      setTimeout(() => setFlash(null), 2000);
      setShowForm(false);
      load();
    } catch(e) { setFlash('Error: ' + e.message); }
    setSaving(false);
  };

  const inp = { background:'#2e3150', border:'1px solid #2a2d3e', borderRadius:6, padding:'8px 12px', color:'#fff', fontSize:13, width:'100%', boxSizing:'border-box' };
  const INTERVAL_LABEL = { weekly:'Weekly', biweekly:'Every 2 Weeks', monthly:'Monthly' };

  return (
    <div style={{padding:'28px 32px', maxWidth:800}}>
      {flash && <div style={{background:'#14532d',color:'#fff',padding:'10px 16px',borderRadius:6,marginBottom:16,fontSize:13}}>{flash}</div>}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:28}}>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:'#fff',marginBottom:4}}>🔄 Recurring Rentals</div>
          <div style={{fontSize:13,color:'#e2e8f0'}}>Auto-schedule repeat orders for weekly or monthly customers.</div>
        </div>
        <button onClick={()=>setShowForm(p=>!p)} style={{background:'#f5a623',color:'#e2e8f0',border:'none',borderRadius:6,padding:'9px 20px',fontWeight:700,fontSize:13,cursor:'pointer'}}>
          {showForm ? 'Cancel' : '+ New Recurring Order'}
        </button>
      </div>

      {showForm && (
        <div style={{background:'#1e2535',border:'1px solid #1e293b',borderRadius:10,padding:20,marginBottom:28}}>
          <div style={{fontSize:14,fontWeight:700,color:'#fff',marginBottom:16}}>New Recurring Order</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:12}}>
            <div><div style={{fontSize:11,color:'#e2e8f0',marginBottom:4,fontWeight:600}}>CLIENT NAME *</div><input style={inp} value={form.client_name} onChange={e=>setForm(p=>({...p,client_name:e.target.value}))} placeholder="John Smith" /></div>
            <div><div style={{fontSize:11,color:'#e2e8f0',marginBottom:4,fontWeight:600}}>EMAIL</div><input style={inp} value={form.client_email} onChange={e=>setForm(p=>({...p,client_email:e.target.value}))} placeholder="john@email.com" /></div>
            <div><div style={{fontSize:11,color:'#e2e8f0',marginBottom:4,fontWeight:600}}>PHONE</div><input style={inp} value={form.client_phone} onChange={e=>setForm(p=>({...p,client_phone:e.target.value}))} placeholder="+1 702 555 0000" /></div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:12,marginBottom:12}}>
            <div><div style={{fontSize:11,color:'#e2e8f0',marginBottom:4,fontWeight:600}}>START DATE *</div><input type="date" style={inp} value={form.from_date} onChange={e=>setForm(p=>({...p,from_date:e.target.value}))} /></div>
            <div><div style={{fontSize:11,color:'#e2e8f0',marginBottom:4,fontWeight:600}}>END DATE *</div><input type="date" style={inp} value={form.to_date} onChange={e=>setForm(p=>({...p,to_date:e.target.value}))} /></div>
            <div><div style={{fontSize:11,color:'#e2e8f0',marginBottom:4,fontWeight:600}}>REPEATS</div>
              <select style={inp} value={form.recurrence_interval} onChange={e=>setForm(p=>({...p,recurrence_interval:e.target.value}))}>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Every 2 Weeks</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div><div style={{fontSize:11,color:'#e2e8f0',marginBottom:4,fontWeight:600}}>ENDS ON</div><input type="date" style={inp} value={form.recurrence_end_date} onChange={e=>setForm(p=>({...p,recurrence_end_date:e.target.value}))} /></div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
            <div><div style={{fontSize:11,color:'#e2e8f0',marginBottom:4,fontWeight:600}}>TOTAL AMOUNT</div><input type="number" style={inp} value={form.total} onChange={e=>setForm(p=>({...p,total:e.target.value}))} placeholder="0.00" /></div>
            <div><div style={{fontSize:11,color:'#e2e8f0',marginBottom:4,fontWeight:600}}>NOTES</div><input style={inp} value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} placeholder="Weekly skid steer rental..." /></div>
          </div>
          <button onClick={save} disabled={saving||!form.client_name||!form.from_date||!form.to_date} style={{background:'#f5a623',color:'#e2e8f0',border:'none',borderRadius:6,padding:'9px 20px',fontWeight:700,fontSize:13,cursor:'pointer',opacity:saving?0.5:1}}>
            {saving ? 'Saving...' : 'Create Recurring Order'}
          </button>
        </div>
      )}

      {loading ? <div style={{color:'#e2e8f0',fontSize:13}}>Loading...</div> :
        orders.length === 0 ? <div style={{color:'#e2e8f0',fontSize:13,background:'#1e2535',border:'1px solid #1e293b',borderRadius:10,padding:24,textAlign:'center'}}>No recurring orders yet. Create one above to auto-schedule repeat customers.</div> :
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {orders.map(o => (
            <div key={o.id} style={{background:'#1e2535',border:'1px solid #1e293b',borderRadius:10,padding:'16px 20px'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                <div>
                  <div style={{fontSize:15,fontWeight:700,color:'#fff'}}>{o.client_name}</div>
                  <div style={{fontSize:12,color:'#e2e8f0'}}>{o.client_email} {o.client_phone && '· ' + o.client_phone}</div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{background:'#f5a62320',color:'#f5a623',border:'1px solid #f5a62340',borderRadius:20,padding:'3px 10px',fontSize:11,fontWeight:700}}>{INTERVAL_LABEL[o.recurrence_interval]||o.recurrence_interval}</div>
                  <div style={{fontSize:12,color:'#e2e8f0',marginTop:4}}>${Number(o.total||0).toFixed(2)} / cycle</div>
                </div>
              </div>
              <div style={{display:'flex',gap:16,fontSize:12,color:'#e2e8f0'}}>
                <span>📅 {o.from_date} → {o.to_date}</span>
                {o.recurrence_end_date && <span>Ends: {o.recurrence_end_date}</span>}
                <span>{o.order_number||'Order #'+o.id}</span>
              </div>
            </div>
          ))}
        </div>
      }
    </div>
  );
}

// Money tiles exclude parallel-run test orders (lib/liveOrders). While a tenant
// is in parallel-run EVERY order is a test order, so the tiles read zero by
// design; this line says so instead of letting it read as a fault.
export function TestOrdersNote({ rows }) {
  const n = (rows || []).filter(o => o && o.is_test).length;
  if (!n) return null;
  return (
    <div data-testid="test-orders-note" style={{ fontSize: 11, color: '#94a3b8', margin: '-10px 0 14px' }}>
      ℹ️ {n} parallel-run {n === 1 ? 'order is' : 'orders are'} excluded from these totals (test orders never count as billed, collected or due).
    </div>
  );
}

export default function RentalDashboard() {
  const [orderTab, setOrderTab]  = useState('all');
  const { tenant } = useTenant();
  const isOwner = tenant?.userRole === 'owner';
  const { signOut } = useAuth();
  const businessName = tenant?.business_name || 'Rental System';
  const [settingsTab, setSettingsTab] = useState('general');
  const [view, setView]         = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('view') || 'dashboard';
  });
  const [selectedClientId, setSelectedClientId] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('clientId') || null;
  });
  const [profileReturnView, setProfileReturnView] = useState('dashboard');

  // Handle deep links from email alerts (clientId / clientName params)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const clientId   = p.get('clientId');
    const clientName = p.get('clientName');
    const viewParam  = p.get('view');
    if (clientId || clientName) {
      setSelectedClientId(clientId ? Number(clientId) : clientName);
      setProfileReturnView('dashboard');
      setView('clientProfile');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (viewParam && viewParam !== 'dashboard') {
      setView(viewParam);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const [orders, setOrders]     = useState([]);
  const [items, setItems]       = useState([]);
  const [clients, setClients]   = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [categories, setCategories] = useState(['Grinding','Scarifying','Blasting','Application','Polishing','Accessories','Dust Control','Cutting','Prep']);
  const [showScanner, setShowScanner] = useState(false);

  // Navigate to client profile — works even if client_id is null on old orders
  function goToClientProfile(order, returnView='dashboard') {
    let cid = order.client_id;
    if (!cid && order.client_name) {
      const name = order.client_name.toLowerCase();
      const match = clients.find(c => c.name?.toLowerCase() === name);
      cid = match?.id;
    }
    // Always navigate — ClientProfilePage handles name-based lookup as fallback
    setSelectedClientId(cid || order.client_name || '__unknown__');
    setProfileReturnView(returnView);
    setView('clientProfile');
  }
  const [scanTarget, setScanTarget]   = useState(null); // 'inventory' | 'order'
  const [dash, setDash]         = useState({ goingOut:[], comingBack:[], late:[] });
  const [activePage, setActivePage] = useState(null); // { kind:'checkout'|'return', orderId } while a pipeline PAGE is open (host-owned; see OrderActionContext)
  const narrowShell = useIsNarrow();                     // ≤ 640: a page takes the whole screen, the sidebar hides
  const mainRef = useRef(null);                          // the scrolling main area (reset to the top when a page exits)
  const [metrics, setMetrics]   = useState({ count:0, items:0, revenue:0, due:0 });
  const [orderSort, setOrderSort] = useState({ key:'created_at', dir:'desc' }); // newest first by default
  const [pendingQuotes, setPendingQuotes] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [flash, setFlash]       = useState(null);

  // Filters
  const [filterStatus, setFilterStatus]   = useState('all');
  const [filterPayment, setFilterPayment] = useState('all');
  const [filterFulfill, setFilterFulfill] = useState('all');
  const [filterTab, setFilterTab]         = useState('all');
  const [orderDateFrom, setOrderDateFrom]   = useState('');
  const [orderDateTo, setOrderDateTo]       = useState('');
  const [orderClientFilter, setOrderClientFilter] = useState('');
  const [openPanels, setOpenPanels]           = useState([]);
  const [openClientPanels, setOpenClientPanels] = useState([]);
  const [showClientForm, setShowClientForm]     = useState(false);
  const [search, setSearch]               = useState('');
  const [catFilter, setCatFilter]         = useState('all');
  const [invTab, setInvTab]               = useState('products');
  const [invSearch,   setInvSearch]       = useState('');
  const [invSort,     setInvSort]         = useState('name');     // name|availability|revenue|utilization
  const [invLayout,   setInvLayout]       = useState('list');     // grid|list
  const [showInactive,setShowInactive]    = useState(false);
  const [showInactiveClients,setShowInactiveClients] = useState(false);
  const [showArchived,setShowArchived]    = useState(false);       // owner-only: archived test orders in the list (bug #3b)
  const [selItem,     setSelItem]         = useState(null);       // item detail panel
  const [itemDetail,  setItemDetail]      = useState(null);       // { stats, history, upcoming }
  const [itemDetailLoading, setItemDetailLoading] = useState(false);
  const [itemDetailTab, setItemDetailTab] = useState('overview');
  const [expiringAssets, setExpiringAssets] = useState([]);
  const [liveOut,     setLiveOut]         = useState({});         // { [item_id]: qty_out }
  const [bundles, setBundles]             = useState([]);
  const [showBundleForm, setShowBundleForm] = useState(false);
  const [editBundle, setEditBundle]       = useState(null);
  const [bundleForm, setBundleForm]       = useState({});
  const [downtime, setDowntime]           = useState([]);
  const [showDowntimeForm, setShowDowntimeForm] = useState(false);
  const [downtimeForm, setDowntimeForm]   = useState({});
  const [docTab, setDocTab]               = useState('invoices');

  // Form state
  const [showForm, setShowForm]       = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [editOrder, setEditOrder]     = useState(null);
  const [editOpts, setEditOpts]       = useState({});   // { openPicker } — the "+ Add equipment" entry points
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  // Collect Payment lives in the pipeline host (one modal, painted above any step); these names stay for the call sites.
  const setCollectOrder = (o) => actionsRef.current?.openCollect(o);
  const setCollectModal_dash = setCollectOrder;
  const actionsRef = useRef(null); // the pipeline host (OrderActionProvider) — Return/Check Out/Reserve/Cancel steps live there
  const setReturnOrder = (o) => actionsRef.current?.openAction(o, 'return');
  const [openMenu, setOpenMenu] = useState(null); // order id whose overflow menu is open
  const [editItem, setEditItem]       = useState(null);
  const [defaultCategoryId, setDefaultCategoryId] = useState(null);
  const [manageUnitsItem, setManageUnitsItem] = useState(null);

  const showFlash = (msg, type='success') => { setFlash({msg,type}); setTimeout(()=>setFlash(null),3000); };

  // ── API CALLS ──────────────────────────────────────────────────────────────
  const loadDowntime = useCallback(async () => {
    try {
      const res = await axios.get('/api/rentals/repairs/downtime/all?active_only=true');
      setDowntime(res.data.downtime || []);
    } catch (err) {
      console.error('loadDowntime error:', err);
    }
  }, []);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if(filterStatus!=='all') params.status=filterStatus;
      if(filterPayment!=='all') params.payment_status=filterPayment;
      if(filterFulfill!=='all') params.fulfillment_type=filterFulfill;
      if(search) params.search=search;
      if(showArchived && isOwner) params.include_archived='1';
      const r = await axios.get('/api/rentals/orders', {params});
      setOrders(r.data.orders||[]);
      setMetrics(r.data.metrics||{});
    } catch(e){ showFlash(e.response?.data?.error||'Failed to load orders','error'); }
    setLoading(false);
  }, [filterStatus, filterPayment, filterFulfill, search, showArchived, isOwner]);

  const loadItems = useCallback(async () => {
    try {
      const r = await axios.get('/api/rentals/items?all=true');
      setItems(r.data.items||[]);
      // Compute live units out from started orders
      const out = {};
      orders.filter(o => o.status === 'started').forEach(o => {
        (o.rental_order_items || []).forEach(i => {
          out[i.rental_item_id] = (out[i.rental_item_id] || 0) + (i.quantity || 1);
        });
      });
      setLiveOut(out);
    }
    catch(e){ console.error('loadItems error:', e.response?.status, e.response?.data, e.message); showFlash('Failed to load inventory','error'); }
  }, [orders]);

  const loadClients = useCallback(async () => {
    setClientsLoading(true);
    try { const r = await axios.get('/api/rentals/clients?pageSize=500' + (showInactiveClients ? '&includeInactive=true' : '')); setClients(r.data.clients||[]); }
    catch(e){ console.error('loadClients error:', e.message); }
    setClientsLoading(false);
  }, [showInactiveClients]);

  const [maintenanceDue, setMaintenanceDue] = useState([]);
  const loadDash = useCallback(async () => {
    try { const r = await axios.get('/api/rentals/dashboard'); setDash(r.data); }
    catch(e){}
    try {
      const q = await axios.get('/api/rentals/quotes');
      setPendingQuotes((q.data.quotes||[]).filter(x=>x.status==='pending_request'));
    }
    catch(e){}
    try {
      const ea = await axios.get('/api/rental-assets/expiring?days=30');
      setExpiringAssets(ea.data.alerts || []);
    }
    catch(e){}
    try {
      const md = await axios.get('/api/rentals/maintenance-schedules/due');
      setMaintenanceDue((md.data.items||[]).filter(i=>i.status!=='ok'));
    }
    catch(e){}
  }, []);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const getItemDowntime = (itemId) => {
    const today = new Date().toISOString().split('T')[0];
    return downtime.find(d =>
      d.rental_item_id === itemId &&
      d.start_date <= today &&
      d.end_date   >= today
    ) || null;
  };
  useEffect(() => { loadItems(); loadDowntime(); }, [loadItems, loadDowntime]);
  // Only fetch clients once something actually needs them — Clients view,
  // Import/Export, or the order form's client picker — not on every app load.
  useEffect(() => {
    if (view === 'clients' || view === 'import' || showForm) loadClients();
  }, [view, showForm, loadClients]);
  useEffect(() => { if(view==='dashboard') loadDash(); if(view==='calendar') { loadItems(); loadOrders(); } }, [view, loadDash, loadItems, loadOrders]);
  useEffect(() => { loadDash(); }, [loadDash]);   // once on mount too: "Next: N going out today →" reads goingOut before any tab or change
  useEffect(() => {
    axios.get('/api/rentals/settings').then(r => {
      const cats = r.data.settings?.item_categories;
      if (cats && cats.length > 0) setCategories(cats);
    }).catch(()=>{});
  }, []);

  const saveOrder = async (formData, confirmForSale = false) => {
    let createdId = null;
    try {
      if(formData.id) {
        await axios.put(`/api/rentals/orders/${formData.id}`, formData);
        showFlash('Order updated');
      } else {
        // Downtime conflict check
        if (!confirmForSale) {
          const rentalStart = formData.start_date;
          const rentalEnd   = formData.end_date || formData.start_date;
          const downtimeConflicts = (formData.items || []).filter(orderItem => {
            const itemId = orderItem.rental_item_id || orderItem.id;
            return downtime.some(d =>
              d.rental_item_id === itemId &&
              d.start_date <= rentalEnd &&
              d.end_date   >= rentalStart
            );
          });
          if (downtimeConflicts.length > 0) {
            const names = downtimeConflicts.map(i => i.name || i.rental_item_id).join(', ');
            const ok = window.confirm(
              `⚠️ Downtime Conflict\n\nThese item(s) are blocked for repairs during this period:\n\n${names}\n\nCreate the order anyway?`
            );
            if (!ok) return;
          }
        }

        const payload = confirmForSale ? { ...formData, confirm_for_sale: true } : formData;
        try {
          const createRes = await axios.post('/api/rentals/orders', payload);
          // At the counter the next moment is Reserve (deposit + signature live in its dialog):
          // land on the new order's detail page instead of a flash. No automatic signature popup.
          createdId = createRes.data?.order?.id || null;
        } catch(inner) {
          if (inner.response?.status === 409 && inner.response?.data?.error === 'for_sale_warning') {
            const names = inner.response.data.for_sale_items.map(i => i.name).join(', ');
            const confirmed = window.confirm(
              'Warning: The following item(s) are listed for sale:\n\n' + names + '\n\nCreate this rental order anyway?'
            );
            if (confirmed) { await saveOrder(formData, true); return; }
            else return;
          }
          throw inner;
        }
      }
      setShowForm(false); setEditOrder(null); loadOrders();
      if (createdId) { setView('orders'); setSelectedOrderId(createdId); }
    } catch(e){ showFlash(e.response?.data?.error||e.response?.data?.message||'Save failed','error'); }
  };

  const patchOrder = async (id, patch) => {
    try { await axios.patch(`/api/rentals/orders/${id}`, patch); loadOrders(); }
    catch(e){ showFlash('Update failed','error'); }
  };

  const duplicateOrder = async (order) => {
    try {
      const { data: full } = await axios.get(`/api/rentals/orders/${order.id}`);
      const src = full.order || order;
      const { id, created_at, updated_at, signed_at, contract_pdf_url, signer_name, signer_email,
        signer_phone, signer_address, signer_dl_number, ...fields } = src;
      const items = (src.rental_order_items||[]).map(i=>({
        rental_item_id:i.rental_item_id, item_name:i.item_name,
        quantity:i.quantity, rate_type:i.rate_type, unit_price:i.unit_price, total_price:i.total_price
      }));
      await axios.post('/api/rentals/orders', { ...fields, status:'draft', payment_status:'payment_due',
        amount_paid:0, items });
      showFlash('Order duplicated as Draft');
      loadOrders();
    } catch(e) { showFlash('Duplicate failed','error'); }
  };

  // Delete, or — when the backend's 409 says the order carries test
  // payments and qualifies — archive instead (bug #3b, lib/orderArchive).
  const deleteOrder = async (order) => {
    const result = await deleteOrArchiveOrder(order, { showFlash, isOwner });
    if (result) loadOrders();
  };
  const unarchive = async (order) => {
    if (await unarchiveOrder(order, { showFlash })) loadOrders();
  };

  const saveItem = async (formData) => {
    try {
      if(formData.id) { await axios.put(`/api/rentals/items/${formData.id}`, formData); showFlash('Item updated'); }
      else { await axios.post('/api/rentals/items', formData); showFlash('Item added'); }
      setShowItemForm(false); setEditItem(null); loadItems();
    } catch(e){ showFlash(e.response?.data?.error||'Save failed','error'); }
  };

  const deleteItem = async (id) => {
    if(!window.confirm('Remove this item?')) return;
    try { await axios.delete(`/api/rentals/items/${id}`); showFlash('Item removed'); loadItems(); }
    catch(e){ showFlash('Delete failed','error'); }
  };

  const toggleItemActive = async (item) => {
    try {
      await axios.patch(`/api/rentals/items/${item.id}/toggle`);
      showFlash(item.is_active !== false ? 'Item deactivated' : 'Item activated');
      loadItems();
    } catch(e) { showFlash('Update failed','error'); }
  };

  const toggleItemForSale = async (item) => {
    if (item.for_sale) {
      // Remove from sale — clear flag
      if (!window.confirm(`Remove "${item.name}" from sale listings? This will also remove it from the Sales module.`)) return;
      try {
        await axios.put(`/api/rentals/items/${item.id}`, { for_sale: false, sale_asking_price: null, sale_listing_id: null });
        showFlash('Removed from sale listings');
        loadItems();
      } catch(e) { showFlash('Update failed','error'); }
    } else {
      // List for sale — prompt for asking price
      const price = window.prompt(`List "${item.name}" for sale\nEnter asking price ($):`);
      if (!price || isNaN(Number(price))) return;
      try {
        // Create a sale listing in the Sales module
        await axios.post('/api/rentals/sales/items', {
          sale_type: 'owned',
          rental_item_id: item.id,
          title: item.name,
          category: item.category,
          asking_price: Number(price),
          condition: 'good',
        });
        showFlash(`Listed for sale at $${Number(price).toLocaleString()}`);
        loadItems();
      } catch(e) {
        if (e.response?.status === 409) {
          showFlash('This item is already listed for sale','error');
        } else {
          showFlash('Failed to create listing','error');
        }
      }
    }
  };

  const toggleItemOnline = async (item) => {
    const newVal = item.visible_online === false ? true : false;
    try {
      await axios.put(`/api/rentals/items/${item.id}`, { visible_online: newVal });
      showFlash(newVal ? 'Visible on storefront' : 'Hidden from storefront');
      loadItems();
    } catch(e) { showFlash('Update failed','error'); }
  };

  const openItemDetail = async (item) => {
    setSelItem(item);
    setItemDetail(null);
    setItemDetailTab('overview');
    setItemDetailLoading(true);
    try {
      const r = await axios.get(`/api/rentals/items/${item.id}/detail`);
      setItemDetail(r.data);
    } catch(e) { setItemDetail({ error: true }); }
    setItemDetailLoading(false);
  };

  // Filter orders for display
  // Every filter the list can carry, in one place: the sidebar status/payment/
  // fulfillment picks, the client text, the two dates and the tab. "All" means
  // all — a filter set from the Dashboard tiles or the sidebar used to stay on
  // with nothing on the page saying so (tenant-200 trace, 2026-09-25).
  const activeFilters = [
    filterStatus !== 'all'  && { key: 'status',  label: `Status: ${STATUS_LABEL[filterStatus] || filterStatus}`, clear: () => setFilterStatus('all') },
    filterPayment !== 'all' && { key: 'payment', label: `Payment: ${PAYMENT_LABEL[filterPayment] || filterPayment}`, clear: () => setFilterPayment('all') },
    filterFulfill !== 'all' && { key: 'fulfillment', label: `Fulfillment: ${filterFulfill}`, clear: () => setFilterFulfill('all') },
    !!orderClientFilter     && { key: 'client',  label: `Client: ${orderClientFilter}`, clear: () => setOrderClientFilter('') },
    !!orderDateFrom         && { key: 'from',    label: `From ${orderDateFrom}`, clear: () => setOrderDateFrom('') },
    !!orderDateTo           && { key: 'to',      label: `To ${orderDateTo}`, clear: () => setOrderDateTo('') },
    showArchived            && { key: 'archived', label: 'Including archived', clear: () => setShowArchived(false) },
  ].filter(Boolean);
  const clearAllFilters = () => { setFilterStatus('all'); setFilterPayment('all'); setFilterFulfill('all'); setOrderClientFilter(''); setOrderDateFrom(''); setOrderDateTo(''); setFilterTab('all'); setShowArchived(false); };
  const displayOrders = orders.filter(o => {
    if(filterTab==='upcoming') { const now=new Date(); return o.from_date && new Date(o.from_date)>now && ['reserved','draft'].includes(o.status); }
    if(filterTab==='late') return o.next_action ? !!o.next_action.is_overdue : (!!o.to_date && new Date(o.to_date)<new Date() && o.status==='started');
    if(filterTab==='shortage') return (o.rental_order_items||[]).length===0 && !['returned','cancelled'].includes(o.status);
    if(orderDateFrom && o.from_date && o.from_date.slice(0,10) < orderDateFrom) return false;
    if(orderDateTo && o.to_date && o.to_date.slice(0,10) > orderDateTo) return false;
    if(orderClientFilter && !(o.client_name||'').toLowerCase().includes(orderClientFilter.toLowerCase()) && !(o.client_company||'').toLowerCase().includes(orderClientFilter.toLowerCase())) return false;
    return true;
  }).sort((a,b) => {
    const k = orderSort.key, dir = orderSort.dir === 'asc' ? 1 : -1;
    const av = k === 'total' ? Number(a.total||0) : (a[k] || ''), bv = k === 'total' ? Number(b.total||0) : (b[k] || '');
    return (av > bv ? 1 : av < bv ? -1 : 0) * dir;
  });
  // Orders-page cards follow the list in view and the Reports rule: test
  // orders (is_test) and cancelled orders never count.
  const cardOrders = liveOnly(displayOrders);
  const cardStats = {
    count: cardOrders.length,
    items: cardOrders.reduce((s,o) => s + (o.rental_order_items||[]).reduce((si,li) => si + Number(li.quantity||1), 0), 0),
    revenue: cardOrders.filter(o => ['paid','partially_paid'].includes(o.payment_status)).reduce((s,o) => s + Number(o.amount_paid||0), 0),
    due: cardOrders.filter(o => ['payment_due','partially_paid'].includes(o.payment_status)).reduce((s,o) => s + Math.max(0, Number(o.total||0) - Number(o.amount_paid||0)), 0),
  };

  const displayItems = React.useMemo(() => {
    let list = items.filter(i => showInactive ? true : i.is_active !== false);
    if (catFilter !== 'all') list = list.filter(i => i.category === catFilter);
    if (invSearch) { const q = invSearch.toLowerCase(); list = list.filter(i => i.name.toLowerCase().includes(q) || (i.sku||'').toLowerCase().includes(q) || (i.description||'').toLowerCase().includes(q)); }
    list = [...list].sort((a,b) => {
      if (invSort === 'availability') { const ao = liveOut[a.id]||0, bo = liveOut[b.id]||0; const ap = a.quantity_total>0?ao/a.quantity_total:0, bp = b.quantity_total>0?bo/b.quantity_total:0; return bp-ap; }
      if (invSort === 'price') return Number(b.price_day||0) - Number(a.price_day||0);
      return (a.name||'').localeCompare(b.name||'');
    });
    return list;
  }, [items, catFilter, invSearch, invSort, showInactive, liveOut]);

  // ── RENDER ─────────────────────────────────────────────────────────────────
  const saveBundle = () => {
    if(!bundleForm.name) return showFlash('Bundle name required','error');
    const selItems = (bundleForm.selectedItems||[]).map(id=>items.find(i=>i.id===id)).filter(Boolean);
    const newBundle = {
      id: editBundle?.id || Date.now(),
      name: bundleForm.name,
      description: bundleForm.description||'',
      price_day: Number(bundleForm.price_day)||0,
      selectedItems: bundleForm.selectedItems||[],
      item_names: selItems.map(i=>i.name),
    };
    if(editBundle) setBundles(b=>b.map(x=>x.id===editBundle.id?newBundle:x));
    else setBundles(b=>[...b,newBundle]);
    setShowBundleForm(false); setEditBundle(null); setBundleForm({});
    showFlash('Bundle saved!');
  };
  const deleteBundle = (id) => { setBundles(b=>b.filter(x=>x.id!==id)); showFlash('Bundle deleted'); };
  const saveDowntime = async () => {
    if(!downtimeForm.rental_item_id||!downtimeForm.start_date||!downtimeForm.end_date) return showFlash('Fill all required fields','error');
    try {
      await axios.post('/api/rentals/repairs/downtime', downtimeForm);
      showFlash('Downtime logged!');
      setShowDowntimeForm(false); setDowntimeForm({});
      loadDowntime();
    } catch(err) {
      showFlash('Failed to save downtime','error');
    }
  };
  const deleteDowntime = async (id) => {
    if (!window.confirm('Remove this downtime block?')) return;
    try {
      await axios.delete(`/api/rentals/repairs/downtime/${id}`);
      showFlash('Downtime removed');
      loadDowntime();
    } catch (err) {
      showFlash('Failed to remove downtime', 'error');
    }
  };

  const generateInvoicePDF = async (order) => {
    const bizInfo = await getBizInfo();
    const win = window.open('', '_blank');
    // order.total is already tax-inclusive — derive subtotal from it rather
    // than treating it as pre-tax and taxing it again. tax_amount is the
    // stored, correct figure (0 if untaxed), never recomputed from the rate.
    const total = Number(order.total)||0;
    const taxAmt = Number(order.tax_amount)||0;
    const subtotal = Number(order.subtotal)||0;
    const taxRate = Number(order.tax_rate)||0;
    const taxParts = Array.isArray(order.tax_breakdown) ? order.tax_breakdown : [];
    const discount = Number(order.discount_amount)||0;
    const due = total - Number(order.amount_paid||0);
    const lineItems = order.rental_order_items || [];
    win.document.write(`<!DOCTYPE html><html><head><title>Invoice #${order.id}</title>
    <style>
      body { font-family: 'DM Sans', Arial, sans-serif; margin: 0; padding: 40px; color: #1a1a2e; background: #fff; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; padding-bottom: 24px; border-bottom: 2px solid #f97316; }
      .logo { font-size: 24px; font-weight: 800; color: #f97316; }
      .logo-sub { font-size: 12px; color: #888; margin-top: 4px; }
      .invoice-title { font-size: 32px; font-weight: 800; color: #1a1a2e; text-align: right; }
      .invoice-num { font-size: 14px; color: #888; text-align: right; }
      .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-bottom: 32px; }
      .label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px; }
      .value { font-size: 14px; color: #1a1a2e; font-weight: 600; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
      th { background: #f8f8fc; padding: 12px 16px; text-align: left; font-size: 12px; color: #888; text-transform: uppercase; letter-spacing: 1px; }
      td { padding: 12px 16px; border-bottom: 1px solid #f0f0f8; font-size: 14px; }
      .totals { margin-left: auto; width: 280px; }
      .total-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 14px; border-bottom: 1px solid #f0f0f8; }
      .total-final { display: flex; justify-content: space-between; padding: 12px 0; font-size: 18px; font-weight: 800; color: #f97316; border-top: 2px solid #f97316; margin-top: 8px; }
      .badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 700; }
      .badge-paid { background: #d1fae5; color: #065f46; }
      .badge-due { background: #fee2e2; color: #991b1b; }
      .badge-partial { background: #fef3c7; color: #92400e; }
      .footer { margin-top: 48px; padding-top: 24px; border-top: 1px solid #eee; font-size: 12px; color: #888; text-align: center; }
      @media print { body { padding: 20px; } }
    </style></head><body>
    <div class="header">
      <div>
        <div class="logo">${bizInfo.name}</div>
        ${bizInfo.place ? `<div class="logo-sub">Equipment Rentals • ${bizInfo.place}</div>` : ''}
        ${bizInfo.email ? `<div class="logo-sub">${bizInfo.email}</div>` : ''}
      </div>
      <div>
        <div class="invoice-title">INVOICE</div>
        <div class="invoice-num">#${order.id}</div>
        <div class="invoice-num" style="margin-top:8px">
          <span class="badge ${order.payment_status==='paid'?'badge-paid':order.payment_status==='payment_due'?'badge-due':'badge-partial'}">
            ${PAYMENT_LABEL[order.payment_status]||order.payment_status}
          </span>
        </div>
      </div>
    </div>
    <div class="grid">
      <div>
        <div class="label">Bill To</div>
        <div class="value">${order.client_name||''}</div>
        ${order.client_company?`<div style="font-size:13px;color:#555">${order.client_company}</div>`:''}
        ${order.client_email?`<div style="font-size:13px;color:#555">${order.client_email}</div>`:''}
        ${order.client_phone?`<div style="font-size:13px;color:#555">${order.client_phone}</div>`:''}
      </div>
      <div>
        <div class="label">Rental Period</div>
        <div class="value">${fmtDate(order.from_date)} → ${fmtDate(order.to_date)}</div>
        <div style="margin-top:16px"><div class="label">Fulfillment</div>
        <div class="value" style="text-transform:capitalize">${order.fulfillment_type||'pickup'}</div></div>
      </div>
    </div>
    <table>
      <thead><tr><th>Equipment</th><th>Qty</th><th>Rate</th><th>Period</th><th style="text-align:right">Total</th></tr></thead>
      <tbody>
        ${lineItems.length > 0 ? lineItems.map(i=>`
          <tr><td>${i.item_name}</td><td>${i.quantity}</td><td>$${Number(i.unit_price).toFixed(2)}</td>
          <td>${describeRateLine(i, daysBetween(order.from_date, order.to_date))}</td>
          <td style="text-align:right">$${Number(i.total_price).toFixed(2)}</td></tr>
        `).join('') : `<tr><td colspan="5" style="color:#888;text-align:center">No line items</td></tr>`}
        ${order.delivery_fee>0?`<tr><td colspan="4">Delivery Fee</td><td style="text-align:right">$${Number(order.delivery_fee).toFixed(2)}</td></tr>`:''}
      </tbody>
    </table>
    <div class="totals">
      <div class="total-row"><span>Subtotal</span><span>$${subtotal.toFixed(2)}</span></div>
      ${discount>0?`<div class="total-row"><span>Discount</span><span>-$${discount.toFixed(2)}</span></div>`:''}
      ${order.tax_exempt?`<div class="total-row"><span>Tax exempt${order.tax_exempt_reason?` — ${order.tax_exempt_reason}`:''}</span><span>$0.00</span></div>`
        : taxParts.length ? taxParts.map(p=>`<div class="total-row"><span>${p.name} (${Number(p.rate)}%)</span><span>$${Number(p.amount).toFixed(2)}</span></div>`).join('')
        : taxAmt>0?`<div class="total-row"><span>Tax (${taxRate}%)</span><span>$${taxAmt.toFixed(2)}</span></div>`:''}
      <div class="total-row"><span>Total</span><span>$${total.toFixed(2)}</span></div>
      ${order.deposit_amount>0?`<div class="total-row"><span>Deposit</span><span>$${Number(order.deposit_amount).toFixed(2)}</span></div>`:''}
      ${Number(order.amount_paid||0)>0?`<div class="total-row"><span>Amount Paid</span><span style="color:#34d399">-$${Number(order.amount_paid).toFixed(2)}</span></div>`:''}
      <div class="total-final"><span>Balance Due</span><span>$${due.toFixed(2)}</span></div>
    </div>
    ${order.notes?`<div style="margin-top:24px;padding:16px;background:#f8f8fc;border-radius:8px;font-size:13px;color:#555"><strong>Notes:</strong> ${order.notes}</div>`:''}
    <div class="footer">Thank you for your business! • ${bizInfo.name}${bizInfo.place ? ' • ' + bizInfo.place : ''}</div>
    <script>window.onload=()=>{window.print();}</script>
    </body></html>`);
    win.document.close();
  };

  return (
    <OrderActionProvider ref={actionsRef} onChanged={()=>{ loadOrders(); loadDash(); }} showFlash={showFlash} inventoryItems={items} dbCategories={categories}
      onActivePage={setActivePage} nextUp={dash?.goingOut}
      onPageExit={(o)=>{ /* Finish or ← Back: land on that order's detail, at the top */ setOpenPanels([]); setSelectedOrderId(o.id); setView('orders'); try { window.scrollTo(0,0); } catch {} if (mainRef.current) mainRef.current.scrollTop = 0; }}>
    <div style={S.wrap}>
      {/* Flash */}
      {flash && <div style={{position:'fixed',top:20,right:20,zIndex:9999,padding:'12px 20px',borderRadius:8,background:flash.type==='error'?'#7f1d1d':'#14532d',color:'#fff',fontSize:13,fontWeight:600,boxShadow:'0 4px 20px rgba(0,0,0,0.5)'}}>{flash.msg}</div>}

      {/* Sidebar */}
      
      <div data-testid="shell-sidebar" style={activePage && narrowShell ? { ...S.sidebar, display:'none' } : S.sidebar}>
        <div style={S.logo}><div style={S.logoTxt}>{businessName}</div><div style={S.logoSub}>Rental System</div></div>
        <div style={{overflowY:'auto',flex:1}}>
          {/* OPERATIONS */}
          <div style={S.navSec}>Operations</div>
          {[['dashboard','📊 Dashboard'],['calendar','📅 Calendar'],['orders','📋 Orders'],['repairs','🔧 Repairs'],['maintenance','🛠️ Maintenance']].map(([v,l]) => (
            <div key={v} style={S.nav(view===v)} onClick={()=>setView(v)}>{l}</div>
          ))}
          {/* INVENTORY */}
          <div style={S.navSec}>Inventory</div>
          {[['categories','📦 Inventory'],['sales','🏷️ Sales']].map(([v,l]) => (
            <div key={v} style={S.nav(view===v)} onClick={()=>setView(v)}>{l}</div>
          ))}
          {/* CUSTOMERS */}
          <div style={S.navSec}>Customers</div>
          {[['clients','👥 Clients'],['retainers','📋 Retainers'],['documents','🧾 Documents']].map(([v,l]) => (
            <div key={v} style={S.nav(view===v)} onClick={()=>setView(v)}>
              {l}
              {v==='documents' && pendingQuotes.length > 0 && (
                <span style={{marginLeft:'auto',background:'#f97316',color:'#fff',borderRadius:20,padding:'1px 7px',fontSize:10,fontWeight:800,lineHeight:'16px'}}>{pendingQuotes.length}</span>
              )}
            </div>
          ))}
          {/* SUPPLY CHAIN */}
          <div style={S.navSec}>Supply Chain</div>
          <div style={S.nav(view==='supplychain')} onClick={()=>setView('supplychain')}>🔗 Supply Chain</div>
          {/* REPORTS */}
          <div style={S.navSec}>Reports & Data</div>
          {[['reports','📈 Reports'],['import','📥 Import / Export']].map(([v,l]) => (
            <div key={v} style={S.nav(view===v)} onClick={()=>setView(v)}>{l}</div>
          ))}
          {/* ONLINE */}
          <div style={S.navSec}>Online</div>
          <div style={S.nav(false)} onClick={()=>window.open(tenant?.custom_slug ? '/'+tenant.custom_slug : '/rent','_blank')}>🌐 Storefront ↗</div>
          {/* ACCOUNT */}
          <div style={S.navSec}>Account</div>
          {[['billing','💳 Billing'],['settings','⚙️ Settings']].map(([v,l]) => (
            <div key={v} style={S.nav(view===v)} onClick={()=>setView(v)}>{l}</div>
          ))}
          {['orders','documents'].includes(view) && <>
          <div style={S.navSec}>Filters</div>
          {['all','pickup','delivery'].map(f => (
            <div key={f} style={S.navSub(filterFulfill===f)} onClick={()=>setFilterFulfill(f)}>{f==='all'?'All Fulfillment':f.charAt(0).toUpperCase()+f.slice(1)}</div>
          ))}
          <div style={S.navSec}>Status</div>
          {['all',...STATUSES].map(s => (
            <div key={s} data-testid={`side-status-${s}`} style={S.navSub(filterStatus===s)} onClick={()=>setFilterStatus(s)}>{s==='all'?'All':STATUS_LABEL[s]}</div>
          ))}
          <div style={S.navSec}>Payment</div>
          {['all',...PAYMENT_STATUSES].map(p => (
            <div key={p} style={S.navSub(filterPayment===p)} onClick={()=>setFilterPayment(p)}>{p==='all'?'All':PAYMENT_LABEL[p]}</div>
          ))}
          </>}
        </div>
        <div style={{padding:"12px 8px",borderTop:"1px solid #1a1d2e"}}>
          <div style={{padding:"7px 12px",borderRadius:8,cursor:"pointer",color:"#e2e8f0",fontSize:11,display:"flex",alignItems:"center",gap:8,marginBottom:2}}
            onClick={()=>setView('account')}
            onMouseEnter={e=>e.currentTarget.style.color="#94a3b8"}
            onMouseLeave={e=>e.currentTarget.style.color="#94a3b8"}>
            ⚙️ iVision Account
          </div>
          <div style={{padding:"8px 12px",borderRadius:8,cursor:"pointer",color:"#e2e8f0",fontSize:12,display:"flex",alignItems:"center",gap:8}} onClick={signOut} onMouseEnter={e=>e.currentTarget.style.color="#f87171"} onMouseLeave={e=>e.currentTarget.style.color="#94a3b8"}>↪ Sign Out</div>
        </div>
      </div>

      {/* Main — while a pipeline page is open on a phone the gutter shrinks to 12 px so the page gets the width */}
      <div ref={mainRef} style={activePage && narrowShell ? { ...S.main, padding: 12 } : S.main}>
        {/* A pipeline PAGE (Check Out, Return) replaces the current view while it is open */}
        {activePage && <ActivePage />}
        {!activePage && (<>

        {/* ── DASHBOARD ── */}
        {view==='dashboard' && (
          <div>
            {/* ── Header ── */}
            <div style={S.hdr}>
              <div>
                <div style={S.title}>Dashboard</div>
                <div style={{fontSize:12,color:'#e2e8f0',marginTop:2}}>{new Date().toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric'})}</div>
              </div>
              <button style={S.btn('primary')} onClick={()=>{setShowForm(true);setEditOrder(null);}}>+ New Order</button>
            </div>

            {/* ── Quick Actions ── */}
            <div style={{display:'flex',gap:10,marginBottom:16}}>
              {[
                { icon:'➕', label:'New Order',      action:()=>{setShowForm(true);setEditOrder(null);} },
                { icon:'👤', label:'Add Client',     action:()=>setView('clients') },
                { icon:'🔧', label:'New Work Order', action:()=>setView('repairs') },
                { icon:'🏗️', label:'Add Equipment',  action:()=>setView('categories') },
              ].map(({icon,label,action})=>(
                <button key={label} onClick={action} style={{
                  flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                  gap:6, padding:'14px 8px', background:'#252838', border:'1px solid #1a1d2e',
                  borderRadius:10, cursor:'pointer', transition:'border-color .15s',
                }} onMouseEnter={e=>e.currentTarget.style.borderColor='#f97316'}
                   onMouseLeave={e=>e.currentTarget.style.borderColor='#2e3150'}>
                  <span style={{fontSize:20}}>{icon}</span>
                  <span style={{fontSize:11,fontWeight:600,color:'#e2e8f0'}}>{label}</span>
                </button>
              ))}
            </div>

            {expiringAssets.length>0 && (
              <div style={{...S.card,borderColor:'#1a2e4a',marginBottom:16}}>
                <div style={{fontSize:13,fontWeight:700,color:'#fbbf24',marginBottom:12}}>⚠️ Expiring Soon — {expiringAssets.length} item{expiringAssets.length>1?'s':''}</div>
                {expiringAssets.map((a,i)=>(
                  <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 0',borderBottom:'1px solid #13141f',fontSize:12}}>
                    <div>
                      <div style={{fontWeight:600,color:'#e2e8f0'}}>{a.item_name}</div>
                      <div style={{fontSize:11,color:'#e2e8f0'}}>{a.type==='insurance'?'🛡 Insurance · '+( a.carrier||''):'📋 Registration · '+(a.plate_number||'')}</div>
                    </div>
                    <div style={{textAlign:'right'}}>
                      <div style={{fontSize:11,fontWeight:700,color: new Date(a.expiration_date)<new Date()?'#f87171':'#fbbf24'}}>{a.expiration_date}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {(dash?.attention||[]).length>0 && (
              <div style={{background:'#1a0e00',border:'1px solid #7c2d12',borderRadius:12,padding:'14px 18px',marginBottom:16}}>
                <div style={{fontSize:12,fontWeight:700,color:'#fb923c',marginBottom:10,display:'flex',alignItems:'center',gap:6}}>
                  ⚡ Needs Attention <span style={{background:'#7c2d12',color:'#fed7aa',borderRadius:20,padding:'1px 8px',fontSize:11}}>{(dash?.attention||[]).length}</span>
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {(dash?.attention||[]).map((a,i)=>(
                    <div key={i} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 12px',background:'rgba(0,0,0,0.3)',borderRadius:8,cursor:'pointer'}}
                      onClick={()=>{const o=orders.find(x=>x.id===a.order_id);if(o)setOpenPanels(p=>p.find(x=>x.id===o.id)?p:[...p,o].slice(-4));}}>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <span style={{fontSize:14}}>{a.type==='unsigned'?'📝':a.type==='unpaid'?'💸':'⏰'}</span>
                        <div>
                          <div style={{fontSize:12,fontWeight:700,color:'#f1f5f9'}}>
                            <span style={{color:'#60a5fa',cursor:'pointer',textDecoration:'underline',textDecorationStyle:'dotted'}}
                              onClick={(e)=>{e.stopPropagation();const o=orders.find(x=>x.id===a.order_id)||{client_id:null,client_name:a.client_name};goToClientProfile(o,'dashboard');}}>
                              {a.client_name}
                            </span>
                            {' '}<span style={{color:'#e2e8f0',fontWeight:400}}>· {a.label}</span>
                          </div>
                          <div style={{fontSize:11,color:'#e2e8f0'}}>{a.detail}</div>
                        </div>
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        {a.type==='unpaid' && <button style={{...S.btn('sm'),fontSize:10,background:'#14532d',color:'#4ade80'}} onClick={e=>{e.stopPropagation();const o=orders.find(x=>x.id===a.order_id);if(o)setCollectModal_dash(o);}}>💰 Collect</button>}
                        {a.type==='no_deposit' && <button style={{...S.btn('sm'),fontSize:10,background:'#252840',color:'#a78bfa'}} onClick={e=>{e.stopPropagation();const o=orders.find(x=>x.id===a.order_id);if(o)setCollectModal_dash(o);}}>💰 Get Deposit</button>}
                        <span style={{fontSize:11,color:'#e2e8f0'}}>→</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Pending Quote Requests ── */}
            {pendingQuotes.length > 0 && (
              <div
                onClick={()=>{ setView('documents'); }}
                style={{background:'rgba(251,191,36,0.06)',border:'1px solid rgba(251,191,36,0.3)',borderRadius:12,padding:'14px 18px',marginBottom:16,cursor:'pointer',transition:'border-color .15s'}}
                onMouseEnter={e=>e.currentTarget.style.borderColor='rgba(251,191,36,0.6)'}
                onMouseLeave={e=>e.currentTarget.style.borderColor='rgba(251,191,36,0.3)'}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <span style={{fontSize:13,fontWeight:700,color:'#fbbf24'}}>🔔 Quote Requests Waiting</span>
                    <span style={{background:'#92400e',color:'#fbbf24',borderRadius:20,padding:'1px 8px',fontSize:11,fontWeight:800}}>{pendingQuotes.length}</span>
                  </div>
                  <span style={{fontSize:12,color:'#e2e8f0'}}>View in Documents →</span>
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {pendingQuotes.slice(0,3).map(q=>(
                    <div key={q.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 12px',background:'rgba(0,0,0,0.3)',borderRadius:8}}>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <span style={{fontSize:13}}>🔧</span>
                        <div>
                          <div style={{fontSize:12,fontWeight:700,color:'#e2e8f0'}}>{q.client_name}{q.client_company?` · ${q.client_company}`:''}</div>
                          <div style={{fontSize:11,color:'#e2e8f0'}}>{q.fulfillment_type==='delivery'?'🚚 Delivery':'🏪 Pickup'}{q.from_date?` · ${fmtDate(q.from_date,{month:'short',day:'numeric'})} → ${fmtDate(q.to_date,{month:'short',day:'numeric'})}`:''}</div>
                        </div>
                      </div>
                      <span style={{fontSize:11,color:'#fbbf24',fontWeight:600}}>Build Quote →</span>
                    </div>
                  ))}
                  {pendingQuotes.length > 3 && <div style={{fontSize:11,color:'#e2e8f0',textAlign:'center',padding:'4px 0'}}>+{pendingQuotes.length-3} more</div>}
                </div>
              </div>
            )}

            {/* ── KPI Cards ── */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:14}}>
              {[
                {label:'Total Orders',  val:metrics.count||0,         color:'#f97316', grad:'#f97316,#fb923c', icon:'📋', fmt:false, filter:null,       view:'orders',  momPct:null},
                {label:'Items Out',     val:metrics.items||0,         color:'#60a5fa', grad:'#3b82f6,#60a5fa', icon:'🔧', fmt:false, filter:'started',   view:'orders',  momPct:null},
                {label:'Revenue',       val:metrics.revenue||0,       color:'#34d399', grad:'#34d399,#6ee7b7', icon:'💰', fmt:true,  filter:null,        view:'billing', momPct:dash?.momRevenue?.changePct ?? null},
                {label:'Outstanding',   val:metrics.due||0,           color:'#f87171', grad:'#f87171,#fca5a5', icon:'⏳', fmt:true,  filter:'due',       view:'billing', momPct:null},
              ].map(({label,val,color,grad,icon,fmt:doFmt,filter,view:dest,momPct})=>(
                <div key={label}
                  style={{background:'#252838',border:'1px solid #1a1d2e',borderRadius:12,padding:'18px 20px',position:'relative',overflow:'hidden',cursor:'pointer',transition:'border-color .15s'}}
                  onClick={()=>{setFilterTab('all');setFilterStatus(filter&&dest==='orders'?filter:'all');setFilterPayment(filter&&dest==='billing'&&filter==='due'?'payment_due':'all');setView(dest);}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=color;e.currentTarget.style.background='#2a2d40';}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor='#2e3150';e.currentTarget.style.background='#252838';}}>
                  <div style={{position:'absolute',top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${grad})`}} />
                  <div style={{fontSize:11,color:'#e2e8f0',fontWeight:600,letterSpacing:1,textTransform:'uppercase',marginBottom:8}}>{label}</div>
                  <div style={{fontSize:32,fontWeight:800,color,lineHeight:1}}>{doFmt?fmt(val):val}</div>
                  <div style={{fontSize:11,color,marginTop:6,opacity:.7}}>{icon} Click to view →</div>
                  {momPct!==null && (
                    <div style={{fontSize:11,fontWeight:700,marginTop:4,color:momPct>=0?'#34d399':'#f87171'}}>
                      {momPct>=0?'▲':'▼'} {Math.abs(momPct)}% vs last month
                    </div>
                  )}
                </div>
              ))}
            </div>
            <TestOrdersNote rows={orders} />

            {/* ── Maintenance Due Widget ── */}
            {maintenanceDue.length > 0 && (
              <div style={{background:'#252838',border:'1px solid #1a1d2e',borderRadius:10,padding:'14px 18px',marginBottom:14,cursor:'pointer'}} onClick={()=>setView('maintenance')}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <div style={{fontSize:12,fontWeight:700,color:'#fbbf24'}}>🛠️ Maintenance Due</div>
                  <span style={{fontSize:10,color:'#94a3b8'}}>Click to manage →</span>
                </div>
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  {maintenanceDue.slice(0,6).map(d=>(
                    <div key={`${d.item_id}_${d.schedule_id}`} style={{padding:'5px 10px',borderRadius:6,background:d.status==='overdue'?'#3b0a0a':'#422006',border:`1px solid ${d.status==='overdue'?'#7f1d1d':'#92400e'}`,display:'flex',alignItems:'center',gap:6}}>
                      <span style={{fontSize:10,fontWeight:700,color:d.status==='overdue'?'#f87171':'#fbbf24'}}>{d.item_name}</span>
                      <span style={{fontSize:9,color:'#94a3b8'}}>{d.schedule_name}</span>
                    </div>
                  ))}
                  {maintenanceDue.length > 6 && <span style={{fontSize:10,color:'#94a3b8',padding:'5px 6px'}}>+{maintenanceDue.length-6} more</span>}
                </div>
              </div>
            )}

            {/* ── Status counters ── */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:16}}>
              {[
                {label:'Active Rentals', val:orders.filter(o=>o.status==='started').length,  color:'#f97316', icon:'🚀', filter:'started'},
                {label:'Reserved',       val:orders.filter(o=>o.status==='reserved').length, color:'#a78bfa', icon:'📌', filter:'reserved'},
                {label:'Overdue',        val:(dash?.late||[]).length,                        color:'#f87171', icon:'⚠️', filter:'late'},
                {label:'Returned Today', val:orders.filter(o=>o.status==='returned'&&o.to_date===new Date().toISOString().slice(0,10)).length, color:'#34d399', icon:'✅', filter:'returned'},
              ].map(({label,val,color,icon,filter})=>(
                <div key={label} data-testid={`dash-tile-${filter}`}
                  style={{background:'#252838',border:'1px solid #1a1d2e',borderRadius:12,padding:'14px 16px',display:'flex',alignItems:'center',gap:12,cursor:'pointer'}}
                  onClick={()=>{if(filter==='late'){setFilterTab('late');setFilterStatus('all');}else{setFilterTab('all');setFilterStatus(filter);}setView('orders');}}
                  onMouseEnter={e=>{e.currentTarget.style.background='#2a2d40';e.currentTarget.style.borderColor=color;}}
                  onMouseLeave={e=>{e.currentTarget.style.background='#252838';e.currentTarget.style.borderColor='#2e3150';}}>
                  <div style={{fontSize:24}}>{icon}</div>
                  <div>
                    <div style={{fontSize:22,fontWeight:800,color}}>{val}</div>
                    <div style={{fontSize:11,color:'#e2e8f0'}}>{label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Revenue chart + Utilization ── */}
            <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:16,marginBottom:16}}>

              {/* Revenue Chart */}
              <div style={S.card}>
                <div style={{fontSize:13,fontWeight:700,color:'#f1f5f9',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span>📈 Revenue — Last 14 Days</span>
                  <span style={{fontSize:11,color:'#e2e8f0',fontWeight:400}}>by order start date</span>
                </div>
                {(()=>{
                  const data = dash?.revenueChart||[];
                  const max  = Math.max(...data.map(d=>d.amount), 1);
                  return (
                    <div style={{display:'flex',alignItems:'flex-end',gap:2,height:100}}>
                      {data.map((d,i)=>{
                        const h = Math.max(3, Math.round((d.amount/max)*96));
                        const isToday = d.date===new Date().toISOString().slice(0,10);
                        const label = new Date(d.date+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'});
                        const showLabel = i===0||i===6||i===13||isToday;
                        return (
                          <div key={d.date} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',cursor:'pointer',position:'relative',height:'100%',justifyContent:'flex-end'}}
                            title={`${label}: ${fmt(d.amount)}`}>
                            <div style={{width:'100%',height:h,background:isToday?'linear-gradient(180deg,#f97316,#fb923c)':d.amount>0?'linear-gradient(180deg,#3b82f6,#2563eb)':'#2e3150',borderRadius:'3px 3px 0 0',transition:'height .4s'}} />
                            {showLabel && (
                              <div style={{fontSize:8,color:isToday?'#f97316':'#94a3b8',whiteSpace:'nowrap',position:'absolute',bottom:-14,textAlign:'center'}}>
                                {isToday?'Today':label}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
                <div style={{display:'flex',justifyContent:'space-between',marginTop:22,paddingTop:10,borderTop:'1px solid #1a1d2e',fontSize:12}}>
                  <span style={{color:'#e2e8f0'}}>14-day total</span>
                  <span style={{fontWeight:700,color:'#34d399'}}>{fmt((dash?.revenueChart||[]).reduce((s,d)=>s+d.amount,0))}</span>
                </div>
              </div>

              {/* Equipment Utilization */}
              <div style={S.card}>
                <div style={{fontSize:13,fontWeight:700,color:'#f1f5f9',marginBottom:14,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span>🔧 Fleet Utilization</span>
                  <span style={{fontSize:11,color:'#e2e8f0',fontWeight:400,cursor:'pointer'}} onClick={()=>setView('categories')}>Manage →</span>
                </div>
                {(dash?.utilization||[]).length===0 ? (
                  <div style={{color:'#e2e8f0',fontSize:12,textAlign:'center',padding:'20px 0'}}>No items in fleet</div>
                ) : (
                  <div style={{display:'flex',flexDirection:'column',gap:8,maxHeight:140,overflowY:'auto'}}>
                    {(dash?.utilization||[]).map(item=>(
                      <div key={item.id}>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:3}}>
                          <span style={{fontSize:11,color:'#e2e8f0',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:'70%'}}>{item.name}</span>
                          <span data-testid="fleet-row-counts" style={{fontSize:11,color:item.pct>=80?'#f87171':item.pct>=50?'#fbbf24':'#4ade80',fontWeight:700,flexShrink:0}}>{item.out} out{(item.reserved||0)>0?` · ${item.reserved} reserved`:''} / {item.total}</span>
                        </div>
                        <div style={{background:'#2e3150',borderRadius:4,height:5,overflow:'hidden'}}>
                          <div style={{width:`${item.pct}%`,height:'100%',background:item.pct>=80?'#ef4444':item.pct>=50?'#f59e0b':'#22c55e',borderRadius:4,transition:'width .4s'}} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{marginTop:10,paddingTop:10,borderTop:'1px solid #1a1d2e',display:'flex',justifyContent:'space-between',fontSize:11,color:'#e2e8f0'}}>
                  <span data-testid="fleet-out">Out: <strong style={{color:'#60a5fa'}}>{(dash?.utilization||[]).reduce((s,i)=>s+i.out,0)}</strong></span>
                  <span data-testid="fleet-reserved">Reserved: <strong style={{color:'#a78bfa'}}>{(dash?.utilization||[]).reduce((s,i)=>s+(i.reserved||0),0)}</strong></span>
                  <span data-testid="fleet-available">Available: <strong style={{color:'#34d399'}}>{(dash?.utilization||[]).reduce((s,i)=>s+i.available,0)}</strong></span>
                </div>
              </div>
            </div>

            {/* ── Today's activity ── */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>

              {/* Going Out */}
              <div style={S.card}>
                <div style={{fontSize:13,fontWeight:700,color:'#f97316',marginBottom:12,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  🚀 Going Out Today
                  <span style={{fontSize:11,color:'#e2e8f0',fontWeight:400}}>{new Date().toLocaleDateString('en-US',{month:'short',day:'numeric'})}</span>
                </div>
                {(dash?.goingOut||[]).length===0
                  ? <div style={{color:'#e2e8f0',fontSize:12,textAlign:'center',padding:'16px 0'}}>No pickups scheduled today</div>
                  : (dash?.goingOut||[]).map(o=>(
                    <div key={o.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid #13141f',cursor:'pointer'}}
                      onMouseEnter={e=>e.currentTarget.style.background='#2a2d40'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                      onClick={()=>{setOpenPanels(p=>p.find(x=>x.id===o.id)?p:[...p,o].slice(-4));}}>
                      <div>
                        <div style={{fontWeight:600,color:'#60a5fa',fontSize:12,cursor:'pointer',textDecoration:'underline',textDecorationStyle:'dotted'}} onClick={(e)=>{e.stopPropagation();goToClientProfile(o,'dashboard')}}>{o.client_name}</div>
                        <div style={{color:'#e2e8f0',fontSize:10,textTransform:'capitalize'}}>{o.fulfillment_type} · {o.order_number||'#'+o.id}</div>
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:6}}>
                        <div style={{color:'#f97316',fontWeight:700,fontSize:12}}>{fmt(o.total)}</div>
                        <button data-testid="dash-checkout" data-order-id={o.id} style={{...S.btn('sm'),fontSize:10,padding:'3px 8px',background:'#1a2e1a',color:'#4ade80'}}
                          onClick={e=>{e.stopPropagation();actionsRef.current?.openAction(o,'checkout');}}>
                          ▶ Check Out
                        </button>
                      </div>
                    </div>
                  ))
                }
                <div style={{marginTop:8}}><span style={{fontSize:11,color:'#e2e8f0',cursor:'pointer'}} onClick={()=>setView('orders')}>View all orders →</span></div>
              </div>

              {/* Coming Back */}
              <div style={S.card}>
                <div style={{fontSize:13,fontWeight:700,color:'#60a5fa',marginBottom:12,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  📦 Coming Back Today
                  <span style={{fontSize:11,color:'#e2e8f0',fontWeight:400}}>{new Date().toLocaleDateString('en-US',{month:'short',day:'numeric'})}</span>
                </div>
                {(dash?.comingBack||[]).length===0
                  ? <div style={{color:'#e2e8f0',fontSize:12,textAlign:'center',padding:'16px 0'}}>No returns scheduled today</div>
                  : (dash?.comingBack||[]).map(o=>(
                    <div key={o.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid #13141f',cursor:'pointer'}}
                      onMouseEnter={e=>e.currentTarget.style.background='#2a2d40'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                      onClick={()=>{setOpenPanels(p=>p.find(x=>x.id===o.id)?p:[...p,o].slice(-4));}}>
                      <div>
                        <div style={{fontWeight:600,color:'#60a5fa',fontSize:12,cursor:'pointer',textDecoration:'underline',textDecorationStyle:'dotted'}} onClick={(e)=>{e.stopPropagation();goToClientProfile(o,'dashboard')}}>{o.client_name}</div>
                        <div style={{color:'#e2e8f0',fontSize:10}}>{PAYMENT_LABEL[o.payment_status]||o.payment_status} · {o.order_number||'#'+o.id}</div>
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:6}}>
                        <div style={{color:'#60a5fa',fontWeight:700,fontSize:12}}>{fmt(o.total)}</div>
                        <button style={{...S.btn('sm'),fontSize:10,padding:'3px 8px',background:'#0a1a2e',color:'#60a5fa'}}
                          onClick={e=>{e.stopPropagation();setReturnOrder(o);}}>
                          ↩ Return
                        </button>
                      </div>
                    </div>
                  ))
                }
                <div style={{marginTop:8}}><span style={{fontSize:11,color:'#e2e8f0',cursor:'pointer'}} onClick={()=>setView('orders')}>View all orders →</span></div>
              </div>
            </div>

            {/* ── Overdue ── */}
            {(dash?.late||[]).length>0 && (
              <div style={{...S.card,borderColor:'#4a2020',marginTop:0}}>
                <div style={{fontSize:13,fontWeight:700,color:'#f87171',marginBottom:12,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  ⚠ Overdue — {dash.late.length} order{dash.late.length>1?'s':''}
                  <span style={{fontSize:11,color:'#e2e8f0',fontWeight:400,cursor:'pointer'}} onClick={()=>{setFilterTab('late');setView('orders');}}>View all →</span>
                </div>
                {(dash?.late||[]).map(o=>(
                  <div key={o.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'7px 0',borderBottom:'1px solid #13141f',cursor:'pointer'}}
                    onMouseEnter={e=>e.currentTarget.style.background='#1a0a00'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                    onClick={()=>{setOpenPanels(p=>p.find(x=>x.id===o.id)?p:[...p,o].slice(-4));}}>
                    <div>
                      <span style={{fontWeight:600,color:'#60a5fa',fontSize:12,cursor:'pointer',textDecoration:'underline',textDecorationStyle:'dotted'}} onClick={(e)=>{e.stopPropagation();goToClientProfile(o,'dashboard')}}>{o.client_name}</span>
                      <span style={{color:'#e2e8f0',marginLeft:8,fontSize:11}}>due {fmtDate(o.to_date)}</span>
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <span style={{color:'#f87171',fontWeight:700,fontSize:12}}>{fmt(o.total)}</span>
                      <button style={{...S.btn('sm'),fontSize:10,padding:'3px 8px',background:'#1a2e1a',color:'#4ade80'}}
                        onClick={e=>{e.stopPropagation();setCollectModal_dash(o);}}>
                        💰 Collect
                      </button>
                      <button style={{...S.btn('sm'),fontSize:10,padding:'3px 8px',background:'#0a1a2e',color:'#60a5fa'}}
                        onClick={e=>{e.stopPropagation();setReturnOrder(o);}}>
                        ↩ Return
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}


        {/* ── ORDERS ── */}
        {view==='orders' && selectedOrderId && (
          <OrderDetailPage
            orderId={selectedOrderId}
            onBack={() => setSelectedOrderId(null)}
            onEdit={(o, opts) => { setEditOrder(o); setEditOpts(opts || {}); setShowForm(true); setSelectedOrderId(null); }}
            fmt={fmt} fmtDate={fmtDate} showFlash={showFlash}
            includeArchived={showArchived && isOwner} onChanged={loadOrders}
          />
        )}
        {view==='orders' && !selectedOrderId && (
          <div>
            <div style={S.hdr}>
              <div style={S.title}>Orders</div>
              <div style={{display:'flex',gap:10,alignItems:'center'}}>
                <button style={S.btn('primary')} onClick={()=>{setShowForm(true);setEditOrder(null);}}>+ Add Order</button>
              </div>
            </div>
            <div style={{display:'flex',gap:14,marginBottom:22}}>
              <div style={S.metric} data-testid="orders-card-count"><div style={S.mVal}>{cardStats.count}</div><div style={S.mLbl}>Orders</div></div>
              <div style={S.metric} data-testid="orders-card-items"><div style={S.mVal}>{cardStats.items}</div><div style={S.mLbl}>Items</div></div>
              <div style={S.metric} data-testid="orders-card-revenue"><div style={{...S.mVal,color:'#34d399'}}>{fmt(cardStats.revenue)}</div><div style={S.mLbl}>Revenue</div></div>
              <div style={S.metric} data-testid="orders-card-due"><div style={{...S.mVal,color:'#f87171'}}>{fmt(cardStats.due)}</div><div style={S.mLbl}>Due</div></div>
            </div>
            <TestOrdersNote rows={displayOrders} />
            <div style={{display:'flex',gap:8,marginBottom:8,flexWrap:'wrap',alignItems:'center'}}>
              <input value={orderClientFilter} onChange={e=>setOrderClientFilter(e.target.value)}
                placeholder="Filter by client..."
                style={{...S.input,width:180,fontSize:12}} />
              <input type="date" value={orderDateFrom} onChange={e=>setOrderDateFrom(e.target.value)}
                style={{...S.input,width:140,fontSize:12,colorScheme:'dark'}} />
              <span style={{fontSize:11,color:'#555'}}>to</span>
              <input type="date" value={orderDateTo} onChange={e=>setOrderDateTo(e.target.value)}
                style={{...S.input,width:140,fontSize:12,colorScheme:'dark'}} />
              {isOwner && (
                <label data-testid="orders-show-archived" style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:'#94a3b8',cursor:'pointer'}} title="Archived parallel-run test orders (owner only)">
                  <input type='checkbox' checked={showArchived} onChange={e=>setShowArchived(e.target.checked)} style={{accentColor:'#f97316'}} />
                  Show archived
                </label>
              )}
              {activeFilters.length > 0 &&
                <button data-testid="filters-clear-all" onClick={clearAllFilters}
                  style={{...S.btn('sm'),fontSize:11,padding:'5px 10px',background:'#2e3150',color:'#f87171'}}>✕ Clear all</button>}
            </div>
            {activeFilters.length > 0 && (
              <div data-testid="filter-chips" style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center',marginBottom:8}}>
                <span style={{fontSize:11,color:'#94a3b8'}}>Showing only:</span>
                {activeFilters.map(f => (
                  <span key={f.key} data-testid="filter-chip" data-filter={f.key} style={{display:'inline-flex',alignItems:'center',gap:6,padding:'4px 8px 4px 10px',borderRadius:14,background:'rgba(249,115,22,0.12)',border:'1px solid #92400e',color:'#fbbf24',fontSize:11,fontWeight:700}}>
                    {f.label}
                    <button type="button" data-testid="filter-chip-clear" aria-label={`Clear ${f.label}`} onClick={f.clear} style={{background:'none',border:'none',color:'#fbbf24',cursor:'pointer',fontSize:12,lineHeight:1,padding:0}}>✕</button>
                  </span>
                ))}
              </div>
            )}
            <div style={S.tabs}>
              {[['all','All'],['upcoming','Upcoming'],['late','Late'],['shortage','With Shortage']].map(([t,l])=>(
                <div key={t} data-testid={`orders-tab-${t}`} style={S.tab(filterTab===t)} onClick={()=>{ if (t==='all') clearAllFilters(); else setFilterTab(t); }}>{l}</div>
              ))}
              <div style={{marginLeft:'auto',padding:'8px 0',fontSize:11,color:'#e2e8f0'}}>{displayOrders.length} orders</div>
            </div>
            <div style={{background:'#252838',border:'1px solid #1a1d2e',borderTop:'none',borderRadius:'0 0 10px 10px',overflow:'hidden'}}>
              <div style={{overflowX:'auto'}}>
              <table style={{...S.table,minWidth:900}}>
                <thead><tr style={{background:'#1e2235'}}>
{(() => { const th = (key, label) => { const on = orderSort.key===key; return (
                    <th key={key} style={{...S.th,cursor:'pointer',userSelect:'none',color:on?'#f97316':S.th.color}} onClick={()=>setOrderSort(p=>({ key, dir: p.key===key && p.dir==='desc' ? 'asc' : 'desc' }))} title="Sort">
                      {label}{on ? (orderSort.dir==='desc' ? ' ↓' : ' ↑') : ''}
                    </th>); }; return (<>
                  <th style={S.th}>#</th>{th('created_at','Created')}<th style={S.th}>Client</th><th style={S.th}>Fulfillment</th>
                  <th style={S.th}>Status</th>{th('from_date','From')}{th('to_date','Until')}
                  {th('total','Price')}<th style={S.th}>Payment</th><th style={S.th}></th>
                  </>); })()}
                </tr></thead>
                <tbody>
                  {displayOrders.map(o=>(
                    <tr key={o.id} style={{cursor:'pointer'}} onMouseEnter={e=>e.currentTarget.style.background='#2a2d40'} onMouseLeave={e=>e.currentTarget.style.background='transparent'} onClick={()=>setSelectedOrderId(o.id)}>
                      <td style={S.td}><span style={{fontSize:11,color:'#e2e8f0',fontWeight:700,fontFamily:'monospace'}}>{o.order_number||'#'+o.id}</span></td>
                      <td style={S.td} data-testid="order-created"><span style={{fontSize:11,color:'#e2e8f0',whiteSpace:'nowrap'}}>{fmtCreated(o)}</span></td>
                      <td style={S.td} onClick={(e)=>{e.stopPropagation();goToClientProfile(o,'orders')}}>
                        <div style={{fontWeight:600,color:'#60a5fa',fontSize:13,cursor:'pointer',textDecoration:'underline',textDecorationStyle:'dotted'}}>{o.client_name}</div>
                        {o.client_company&&<div style={{fontSize:10,color:'#e2e8f0',cursor:'pointer'}}>{o.client_company}</div>}
                      </td>
                      <td style={S.td}><span style={{fontSize:11,color:'#e2e8f0',textTransform:'capitalize'}}>{o.fulfillment_type}</span></td>
                      <td style={S.td}>
                        <span data-testid="order-status-badge" style={{display:'inline-block',padding:'3px 9px',borderRadius:12,fontSize:11,fontWeight:700,background:STATUS_COLOR[o.status]?.bg||'#1e2235',color:STATUS_COLOR[o.status]?.color||'#888',border:`1px solid ${STATUS_COLOR[o.status]?.border||'#2a2d3e'}`,whiteSpace:'nowrap'}}>
                          {o.next_action?.status_label||STATUS_LABEL[o.status]||o.status}
                        </span>
                        {o.archived_at && <span data-testid="order-archived-badge" title={'Archived ' + new Date(o.archived_at).toLocaleString()} style={{display:'inline-block',marginLeft:6,padding:'3px 9px',borderRadius:12,fontSize:11,fontWeight:700,background:'#1e2235',color:'#94a3b8',border:'1px solid #2a2d3e',whiteSpace:'nowrap'}}>Archived</span>}
                      </td>
                      <td style={S.td}><span style={{fontSize:11,color:'#e2e8f0'}}>{fmtDate(o.from_date)}</span></td>
                      <td style={S.td}><span style={{fontSize:11,color:'#e2e8f0'}}>{fmtDate(o.to_date)}</span></td>
                      <td style={S.td}><span style={{fontSize:13,fontWeight:700,color:'#f1f5f9'}}>{fmt(o.total)}</span></td>
                      <td style={S.td}>
                        <span data-testid="row-payment-badge" style={{fontSize:11,fontWeight:700,color:PAYMENT_COLOR[o.payment_status]||'#888',whiteSpace:'nowrap'}}>{PAYMENT_LABEL[o.payment_status]||o.payment_status}</span>
                      </td>
                      <td style={S.td} onClick={e=>e.stopPropagation()}>
                        {/* One primary action per order (server-decided) + the ⋯ menu. No status <select>. */}
                        <div style={{display:'flex',gap:6,alignItems:'center',justifyContent:'flex-end'}}>
                          {o.archived_at ? (
                            // An archived order is frozen server-side (every mutation 409s); the only action is to restore it.
                            <button type="button" data-testid="order-unarchive" onClick={()=>unarchive(o)}
                              style={{padding:'5px 10px',background:'#252840',color:'#fbbf24',border:'none',borderRadius:6,fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>↩ Un-archive</button>
                          ) : (<>
                          <NextStepButton order={o} size="sm" onBlocked={(na) => { if (na.reason === 'Add at least one item') { setEditOrder(o); setEditOpts({ openPicker: true }); setShowForm(true); } }} />
                          <OrderMoreMenu order={o} actions={{
                            edit: ()=>{setEditOrder(o);setShowForm(true);},
                            collect: ()=>setCollectOrder(o),
                            duplicate: ()=>duplicateOrder(o),
                            delete: ()=>deleteOrder(o),
                          }} />
                          </>)}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {displayOrders.length===0&&<tr><td colSpan={9} style={{...S.td,textAlign:'center',color:'#94a3b8',padding:40}}>No orders match filters</td></tr>}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        )}

        {/* ── DOCUMENTS ── */}
        {view==='documents' && (
          <div>
            <div style={S.hdr}><div style={S.title}>Documents</div></div>
            <div style={S.tabs}>
              {[['invoices','Invoices'],['contracts','Contracts'],['quotes','Quotes']].map(([t,l])=>(
                <div key={t} style={S.tab(docTab===t)} onClick={()=>setDocTab(t)}>
                  {l}
                  {t==='quotes' && pendingQuotes.length > 0 && (
                    <span style={{display:'inline-flex',alignItems:'center',justifyContent:'center',marginLeft:6,background:'#f97316',color:'#fff',borderRadius:20,padding:'1px 6px',fontSize:10,fontWeight:800,lineHeight:'15px',animation:'pulse 1.5s ease-in-out infinite'}}>
                      {pendingQuotes.length}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div style={{background:'#252838',border:'1px solid #1a1d2e',borderTop:'none',borderRadius:'0 0 10px 10px'}}>

              {/* INVOICES TAB */}
              {docTab==='invoices' && (<>
                <div style={{display:'flex',gap:14,padding:'16px 16px 0'}}>
                  {(() => { const inv = liveOnly(orders); return (<>
                  <div style={S.metric}><div style={S.mVal}>{inv.length}</div><div style={S.mLbl}>Invoices</div></div>
                  <div style={S.metric}><div style={{...S.mVal,color:'#34d399'}}>{fmt(inv.reduce((s,o)=>s+Number(o.amount_paid||0),0))}</div><div style={S.mLbl}>Collected</div></div>
                  <div style={S.metric}><div style={{...S.mVal,color:'#f1f5f9'}}>{fmt(inv.reduce((s,o)=>s+Number(o.total||0),0))}</div><div style={S.mLbl}>Billed</div></div>
                  <div style={S.metric}><div style={{...S.mVal,color:'#f87171'}}>{fmt(inv.filter(o=>['payment_due','partially_paid'].includes(o.payment_status)).reduce((s,o)=>s+Math.max(0,Number(o.total||0)-Number(o.amount_paid||0)),0))}</div><div style={S.mLbl}>Due</div></div>
                  </>); })()}
                </div>
                <table style={{...S.table,margin:'16px 0'}}>
                  <thead><tr style={{background:'#1e2235'}}>
                    <th style={S.th}>#</th><th style={S.th}>Order</th><th style={S.th}>Client</th>
                    <th style={S.th}>Fulfillment</th><th style={S.th}>Invoice Date</th><th style={S.th}>Due Date</th>
                    <th style={S.th}>Excl. Tax</th><th style={S.th}>Incl. Tax</th><th style={S.th}>Deposit</th>
                    <th style={S.th}>Due</th><th style={S.th}>Payment</th>
                  </tr></thead>
                  <tbody>
                    {orders.map(o=>(
                      <tr key={o.id} style={{cursor:'pointer'}} onMouseEnter={e=>e.currentTarget.style.background='#2a2d40'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                        <td style={S.td}><span style={{fontSize:10,color:'#e2e8f0',fontFamily:'monospace'}}>{o.order_number||'#'+o.id}</span></td>
                        <td style={S.td}><span style={{fontSize:11,color:'#60a5fa',fontFamily:'monospace'}}>{o.order_number||'#'+o.id}</span></td>
                        <td style={S.td}><span style={{fontSize:12,fontWeight:600,color:'#e2e8f0'}}>{o.client_name}</span></td>
                        <td style={S.td}><span style={{fontSize:11,color:'#e2e8f0',textTransform:'capitalize'}}>{o.fulfillment_type}</span></td>
                        <td style={S.td}><span style={{fontSize:11,color:'#e2e8f0'}}>{fmtDate(o.from_date)}</span></td>
                        <td style={S.td}><span style={{fontSize:11,color:o.to_date&&new Date(o.to_date)<new Date()&&o.payment_status==='payment_due'?'#f87171':'#94a3b8'}}>{fmtDate(o.to_date)}</span></td>
                        <td style={S.td}><span style={{fontSize:12,color:'#f1f5f9'}}>{fmt(o.subtotal ?? (Number(o.total)-Number(o.tax_amount||0)))}</span></td>
                        <td style={S.td}><span style={{fontSize:12,color:'#f1f5f9'}}>{fmt(o.total)}</span></td>
                        <td style={S.td}><span style={{fontSize:12,color:'#fbbf24'}}>{fmt(o.deposit_amount)}</span></td>
                        <td style={S.td}><span style={{fontSize:12,color:'#f87171'}}>{fmt(Number(o.total)-Number(o.amount_paid||0))}</span></td>
                        <td style={S.td}><span style={{...S.badge('payment',o.payment_status)}}>{PAYMENT_LABEL[o.payment_status]}</span></td>
                        <td style={S.td}><button onClick={()=>generateInvoicePDF(o)} style={{padding:'4px 10px',borderRadius:6,border:'none',background:'#f97316',color:'#fff',fontSize:11,fontWeight:700,cursor:'pointer'}}>PDF</button></td>
                      </tr>
                    ))}
                    {orders.length===0&&<tr><td colSpan={12} style={{...S.td,textAlign:'center',color:'#94a3b8',padding:40}}>No invoices yet</td></tr>}
                  </tbody>
                </table>
              </>)}

              {/* CONTRACTS TAB */}
              {docTab==='contracts' && (
                <RentalContractAdmin orders={orders} />
              )}

              {/* QUOTES TAB */}
              {docTab==='quotes' && (
                <div style={{padding:20}}>
                  <RentalQuotes inventoryItems={items} />
                </div>
              )}

            </div>
          </div>
        )}

        {/* ── CLIENTS ── */}
        {view==='clients' && (
          <ClientsView
            clients={clients}
            orders={orders}
            onRefresh={loadClients}
            loading={clientsLoading}
            onAddClient={()=>setShowClientForm(true)}
            onOpenPanel={(c)=>setOpenClientPanels(p=>p.find(x=>x.id===c.id)?p:[...p,c].slice(-4))}
            onOpenProfile={(c)=>{ setSelectedClientId(c.id); setProfileReturnView('clients'); setView('clientProfile'); }}
            onNewOrder={(c)=>{ setEditOrder({ client_id:c.id, client_name:c.name, client_email:c.email, client_phone:c.phone, client_company:c.company }); setShowForm(true); }}
            fmt={fmt}
            fmtDate={fmtDate}
            axiosClient={axios}
            showFlash={showFlash}
            showInactiveClients={showInactiveClients}
            setShowInactiveClients={setShowInactiveClients}
          />
        )}

        {view==='clientProfile' && (
          <ClientProfilePage
            clientId={selectedClientId}
            onBack={()=>{ setView(profileReturnView); setSelectedClientId(null); }}
            onNewOrder={(c)=>{ setEditOrder({ client_id:c.id, client_name:c.name, client_email:c.email, client_phone:c.phone, client_company:c.company }); setShowForm(true); }}
            showFlash={showFlash}
            onCheckout={(o)=>actionsRef.current?.openAction(o,'checkout')}
            onReturn={(o)=>setReturnOrder(o)}
            onCollect={(o)=>setCollectModal_dash(o)}
            onOpenOrder={id => { setSelectedOrderId(id); setView('orders'); }}
          />
        )}

        {/* ── REPORTS ── */}
        {view==='calendar' && (
          <CalendarView orders={orders} items={items} setSelectedOrderId={setSelectedOrderId} setView={setView} />
        )}
        {view==='reports' && (
          <div>
            <div style={S.hdr}><div style={S.title}>Reports</div></div>

            {/* Summary cards + report cards — period-aware, see ReportSection */}
            <ReportSection orders={orders} items={items} />
          </div>
        )}

        {view==='import' && (
          <ImportExport orders={orders} items={items} clients={clients} axiosClient={axios} onRefresh={()=>{ loadOrders(); loadItems(); loadClients(); }} />
        )}
        {view==='billing' && (
          <RentalBilling orders={orders} onOrderUpdate={()=>{ loadOrders(); }} />
        )}
        
        {view==='recurring' && <RecurringView />}
        {view==='settings' && <RentalSettings initialTab={settingsTab} />}
        {view==='repairs' && <RepairModule />}
        {view==='maintenance' && <MaintenanceSchedules />}
        {view==='sales' && <SalesModule />}
        {view==='retainers' && <RetainerContracts />}
        {view==='categories' && <InventoryBrowser />}
        {view==='po' && <PurchaseOrdersView onVendorClick={(vendorId) => { setView('settings'); setSettingsTab('vendors'); }} />}
        {view==='supplychain' && <SupplyChainView />}
        {view==='account' && (
          <div style={{maxWidth:900,margin:'0 auto'}}>
            <div style={{padding:'24px 24px 0',display:'flex',alignItems:'center',gap:12}}>
              <button onClick={()=>setView('dashboard')} style={{background:'none',border:'none',color:'#e2e8f0',cursor:'pointer',fontSize:13}}>← Back</button>
              <span style={{fontSize:12,color:'#e2e8f0'}}>iVision CRM Subscription</span>
            </div>
            <Billing />
          </div>
        )}
        </>)}
      </div>


      {/* ── CLIENT QUICK-VIEW PANELS (hidden while a page is open) ── */}
      {!activePage && openClientPanels.map((client, idx) => (
        <ClientPanel
          key={client.id}
          client={client}
          offset={idx}
          onClose={()=>setOpenClientPanels(p=>p.filter(x=>x.id!==client.id))}
          onNewOrder={()=>{ setShowForm(true); setEditOrder({client_name:client.name,client_email:client.email,client_phone:client.phone,client_company:client.company}); }}
          fmt={fmt}
          fmtDate={fmtDate}
          onCheckout={(o)=>actionsRef.current?.openAction(o,'checkout')}
          onReturn={(o)=>setReturnOrder(o)}
          onCollect={(o)=>setCollectModal_dash(o)}
        />
      ))}

      {/* ── ADD CLIENT FORM ── */}
      {showClientForm && <ClientForm
        onClose={()=>setShowClientForm(false)}
        onSave={async (data, sendPortalInvite, saveOnly)=>{
          try {
            const r = await axios.post('/api/rentals/clients', { name:data.name, email:data.email, phone:data.phone, company:data.company, address:data.address, city:data.city, state:data.state, zip:data.zip, dl_number:data.dl_number });
            if (sendPortalInvite && data.email && r.data.client?.id) {
              try {
                await axios.post(`/api/rentals/clients/${r.data.client.id}/portal-invite`);
                showFlash('Client saved & portal invite sent');
              } catch(e) { showFlash('Client saved but invite failed: ' + (e.response?.data?.error || e.message), 'error'); }
            } else {
              showFlash('Client added');
            }
            loadClients();
            if (!saveOnly) {
              setShowForm(true);
              setEditOrder({ client_name:data.name, client_email:data.email, client_phone:data.phone, client_company:data.company });
            }
          } catch(e) { showFlash('Failed to save client','error'); }
          setShowClientForm(false);
        }}
        onSaveBusiness={(data)=>{
          showFlash('Business account created');
          loadClients();
          setShowClientForm(false);
        }}
      />}

      {/* ── ORDER QUICK-VIEW PANELS (lookup only; hidden while a page is open) ── */}
      {!activePage && openPanels.map((order, idx) => (
        <OrderPanel
          key={order.id}
          order={order}
          offset={idx}
          onClose={()=>setOpenPanels(p=>p.filter(x=>x.id!==order.id))}
          onEdit={()=>{ setEditOrder(order); setShowForm(true); setOpenPanels(p=>p.filter(x=>x.id!==order.id)); }}
          patchOrder={patchOrder}
          onCollect={setCollectOrder}
          fmt={fmt}
          fmtDate={fmtDate}
        />
      ))}

      {/* ── ORDER FORM MODAL ── */}
      {showForm && <OrderForm order={editOrder} items={items} clients={clients} categories={categories} autoOpenPicker={!!editOpts.openPicker} onSave={saveOrder} onClose={()=>{setShowForm(false);setEditOrder(null);setEditOpts({});}} />}

      {/* ── ITEM FORM MODAL ── */}
      {showItemForm && <ItemForm item={editItem} categories={categories} defaultCategoryId={defaultCategoryId} onSave={saveItem} onClose={()=>{setShowItemForm(false);setEditItem(null);setDefaultCategoryId(null);}} />}

      {showScanner && (
        <BarcodeScanner
          items={items}
          onFound={item => {
            if (scanTarget === 'inventory') {
              // Open item detail / edit
              setEditItem(item);
              setShowItemForm(true);
            }
            setShowScanner(false);
          }}
          onClose={() => setShowScanner(false)}
        />
      )}
    </div>
    </OrderActionProvider>
  );
}

// ── CALENDAR VIEW ────────────────────────────────────────────────────────────

// ── ItemInsuranceTab ──────────────────────────────────────────────────────────
function ItemInsuranceTab({ itemId, fmt }) {
  const [records, setRecords] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [adding, setAdding] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const blank = { carrier:'', policy_number:'', coverage_amount:'', premium:'', expiration_date:'', notes:'' };
  const [form, setForm] = React.useState(blank);

  React.useEffect(() => {
    axios.get('/api/rental-assets/insurance?item_id=' + itemId)
      .then(r => setRecords(r.data.insurance || []))
      .finally(() => setLoading(false));
  }, [itemId]);

  const save = async () => {
    setSaving(true);
    const r = await axios.post('/api/rental-assets/insurance', { ...form, rental_item_id: itemId });
    setRecords(p => [...p, r.data.insurance]);
    setForm(blank); setAdding(false); setSaving(false);
  };

  const del = async (id) => {
    await axios.delete('/api/rental-assets/insurance/' + id);
    setRecords(p => p.filter(x => x.id !== id));
  };

  const daysUntil = (date) => {
    const diff = Math.ceil((new Date(date) - new Date()) / 86400000);
    return diff;
  };

  const expiryColor = (date) => {
    const d = daysUntil(date);
    if (d < 0) return '#f87171';
    if (d <= 30) return '#fbbf24';
    return '#34d399';
  };

  if (loading) return <div style={{color:'#e2e8f0',fontSize:12,padding:'10px 0'}}>Loading...</div>;

  return (
    <div>
      {records.length === 0 && !adding && <div style={{color:'#e2e8f0',fontSize:12,fontStyle:'italic',marginBottom:10}}>No insurance records</div>}
      {records.map(r => (
        <div key={r.id} style={{background:'#1e2235',borderRadius:8,padding:'10px 12px',marginBottom:8,border:'1px solid #1a1d2e'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
            <div style={{fontSize:12,fontWeight:700,color:'#e2e8f0'}}>{r.carrier || 'Unknown Carrier'}</div>
            <div style={{display:'flex',gap:6,alignItems:'center'}}>
              {r.expiration_date && <span style={{fontSize:10,fontWeight:700,color:expiryColor(r.expiration_date)}}>{daysUntil(r.expiration_date) < 0 ? 'EXPIRED' : daysUntil(r.expiration_date) + 'd left'}</span>}
              <button onClick={() => del(r.id)} style={{background:'none',border:'none',color:'#f87171',cursor:'pointer',fontSize:12}}>✕</button>
            </div>
          </div>
          <div style={{fontSize:11,color:'#e2e8f0'}}>Policy: {r.policy_number || '—'}</div>
          {r.coverage_amount && <div style={{fontSize:11,color:'#e2e8f0'}}>Coverage: {fmt(r.coverage_amount)}</div>}
          {r.expiration_date && <div style={{fontSize:11,color:'#e2e8f0'}}>Expires: {r.expiration_date}</div>}
          {r.notes && <div style={{fontSize:11,color:'#e2e8f0',marginTop:4}}>{r.notes}</div>}
        </div>
      ))}

      {adding ? (
        <div style={{background:'#1e2235',borderRadius:8,padding:'12px',border:'1px solid #1a1d2e'}}>
          {[['carrier','Carrier / Insurer'],['policy_number','Policy Number'],['coverage_amount','Coverage Amount'],['premium','Annual Premium'],['expiration_date','Expiration Date'],['notes','Notes']].map(([k,l]) => (
            <div key={k} style={{marginBottom:8}}>
              <div style={{fontSize:10,color:'#e2e8f0',marginBottom:3,textTransform:'uppercase'}}>{l}</div>
              <input type={k==='expiration_date'?'date':'text'} value={form[k]} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))}
                style={{width:'100%',padding:'6px 8px',background:'#1e2030',border:'1px solid #1e2235',borderRadius:5,color:'#e2e8f0',fontSize:12,boxSizing:'border-box'}} />
            </div>
          ))}
          <div style={{display:'flex',gap:6,marginTop:8}}>
            <button onClick={save} disabled={saving} style={{padding:'5px 14px',borderRadius:5,border:'none',background:'#f97316',color:'#fff',fontSize:11,fontWeight:700,cursor:'pointer'}}>{saving?'Saving...':'Save'}</button>
            <button onClick={()=>setAdding(false)} style={{padding:'5px 10px',borderRadius:5,border:'1px solid #2a3050',background:'transparent',color:'#e2e8f0',fontSize:11,cursor:'pointer'}}>Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={()=>setAdding(true)} style={{width:'100%',padding:'6px',borderRadius:6,border:'1px dashed #2a3050',background:'transparent',color:'#e2e8f0',fontSize:11,cursor:'pointer',marginTop:4}}>+ Add Insurance Record</button>
      )}
    </div>
  );
}

// ── ItemRegistrationTab ───────────────────────────────────────────────────────
function ItemRegistrationTab({ itemId }) {
  const [records, setRecords] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [adding, setAdding] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const blank = { plate_number:'', vin:'', state:'UT', expiration_date:'', notes:'' };
  const [form, setForm] = React.useState(blank);

  React.useEffect(() => {
    axios.get('/api/rental-assets/registration?item_id=' + itemId)
      .then(r => setRecords(r.data.registration || []))
      .finally(() => setLoading(false));
  }, [itemId]);

  const save = async () => {
    setSaving(true);
    const r = await axios.post('/api/rental-assets/registration', { ...form, rental_item_id: itemId });
    setRecords(p => [...p, r.data.registration]);
    setForm(blank); setAdding(false); setSaving(false);
  };

  const del = async (id) => {
    await axios.delete('/api/rental-assets/registration/' + id);
    setRecords(p => p.filter(x => x.id !== id));
  };

  const daysUntil = (date) => Math.ceil((new Date(date) - new Date()) / 86400000);
  const expiryColor = (date) => { const d = daysUntil(date); return d < 0 ? '#f87171' : d <= 30 ? '#fbbf24' : '#34d399'; };

  if (loading) return <div style={{color:'#e2e8f0',fontSize:12,padding:'10px 0'}}>Loading...</div>;

  return (
    <div>
      {records.length === 0 && !adding && <div style={{color:'#e2e8f0',fontSize:12,fontStyle:'italic',marginBottom:10}}>No registration records</div>}
      {records.map(r => (
        <div key={r.id} style={{background:'#1e2235',borderRadius:8,padding:'10px 12px',marginBottom:8,border:'1px solid #1a1d2e'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
            <div style={{fontSize:12,fontWeight:700,color:'#e2e8f0'}}>{r.plate_number || 'No Plate'} {r.state ? '· '+r.state : ''}</div>
            <div style={{display:'flex',gap:6,alignItems:'center'}}>
              {r.expiration_date && <span style={{fontSize:10,fontWeight:700,color:expiryColor(r.expiration_date)}}>{daysUntil(r.expiration_date) < 0 ? 'EXPIRED' : daysUntil(r.expiration_date) + 'd left'}</span>}
              <button onClick={() => del(r.id)} style={{background:'none',border:'none',color:'#f87171',cursor:'pointer',fontSize:12}}>✕</button>
            </div>
          </div>
          {r.vin && <div style={{fontSize:11,color:'#e2e8f0'}}>VIN: {r.vin}</div>}
          {r.expiration_date && <div style={{fontSize:11,color:'#e2e8f0'}}>Expires: {r.expiration_date}</div>}
          {r.notes && <div style={{fontSize:11,color:'#e2e8f0',marginTop:4}}>{r.notes}</div>}
        </div>
      ))}

      {adding ? (
        <div style={{background:'#1e2235',borderRadius:8,padding:'12px',border:'1px solid #1a1d2e'}}>
          {[['plate_number','Plate Number'],['vin','VIN'],['state','State'],['expiration_date','Expiration Date'],['notes','Notes']].map(([k,l]) => (
            <div key={k} style={{marginBottom:8}}>
              <div style={{fontSize:10,color:'#e2e8f0',marginBottom:3,textTransform:'uppercase'}}>{l}</div>
              <input type={k==='expiration_date'?'date':'text'} value={form[k]} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))}
                style={{width:'100%',padding:'6px 8px',background:'#1e2030',border:'1px solid #1e2235',borderRadius:5,color:'#e2e8f0',fontSize:12,boxSizing:'border-box'}} />
            </div>
          ))}
          <div style={{display:'flex',gap:6,marginTop:8}}>
            <button onClick={save} disabled={saving} style={{padding:'5px 14px',borderRadius:5,border:'none',background:'#f97316',color:'#fff',fontSize:11,fontWeight:700,cursor:'pointer'}}>{saving?'Saving...':'Save'}</button>
            <button onClick={()=>setAdding(false)} style={{padding:'5px 10px',borderRadius:5,border:'1px solid #2a3050',background:'transparent',color:'#e2e8f0',fontSize:11,cursor:'pointer'}}>Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={()=>setAdding(true)} style={{width:'100%',padding:'6px',borderRadius:6,border:'1px dashed #2a3050',background:'transparent',color:'#e2e8f0',fontSize:11,cursor:'pointer',marginTop:4}}>+ Add Registration Record</button>
      )}
    </div>
  );
}


// ── PurchaseOrdersView ────────────────────────────────────────────────────────
function PurchaseOrdersView({ onVendorClick }) {
  const [orders, setOrders] = React.useState([]);
  const [vendors, setVendors] = React.useState([]);
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [openPO, setOpenPO] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const blank = { vendor_id:'', po_number:'', order_date:new Date().toISOString().split('T')[0], received_date:'', status:'pending', subtotal:0, tax:0, total:0, notes:'' };
  const [form, setForm] = React.useState(blank);
  const [lineItems, setLineItems] = React.useState([]);
  const newLine = { description:'', quantity:1, unit_cost:0, rental_item_id:'' };
  const [newLI, setNewLI] = React.useState(newLine);

  const STATUS_COLOR = { pending:'#fbbf24', ordered:'#60a5fa', received:'#34d399', cancelled:'#f87171' };

  const fmt = (n) => '$' + Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});

  const load = async () => {
    setLoading(true);
    const [po, vend, inv] = await Promise.all([
      axios.get('/api/rental-assets/po'),
      axios.get('/api/rental-assets/vendors'),
      axios.get('/api/rentals/items'),
    ]);
    setOrders(po.data.orders || []);
    setVendors(vend.data.vendors || []);
    setItems(inv.data.items || []);
    setLoading(false);
  };

  React.useEffect(() => { load(); }, []);

  const recalc = (lis) => {
    const sub = lis.reduce((s,l) => s + (Number(l.quantity||1) * Number(l.unit_cost||0)), 0);
    setForm(p => ({ ...p, subtotal: sub, total: sub + Number(p.tax||0) }));
  };

  const addLine = () => {
    if (!newLI.description) return;
    const updated = [...lineItems, { ...newLI, id: Date.now() }];
    setLineItems(updated);
    recalc(updated);
    setNewLI(newLine);
  };

  const removeLine = (id) => {
    const updated = lineItems.filter(l => l.id !== id);
    setLineItems(updated);
    recalc(updated);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post('/api/rental-assets/po', { ...form, items: lineItems });
      setShowForm(false); setForm(blank); setLineItems([]);
      await load();
    } catch(e) { alert('Error saving PO'); }
    setSaving(false);
  };

  const updateStatus = async (id, status) => {
    await axios.patch('/api/rental-assets/po/' + id, { status });
    setOrders(p => p.map(o => o.id === id ? { ...o, status } : o));
  };

  const deletePO = async (id) => {
    if (!window.confirm('Delete this purchase order?')) return;
    await axios.delete('/api/rental-assets/po/' + id);
    setOrders(p => p.filter(o => o.id !== id));
  };

  const inp = { padding:'8px 10px', borderRadius:6, border:'1px solid #1e2235', background:'#1e2030', color:'#e2e8f0', fontSize:12, width:'100%', boxSizing:'border-box' };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <div style={{ fontSize:20, fontWeight:700, color:'#f1f5f9' }}>📄 Purchase Orders</div>
          <div style={{ fontSize:12, color:'#e2e8f0', marginTop:2 }}>Track equipment purchases from vendors</div>
        </div>
        <button onClick={()=>{ setShowForm(true); setForm(blank); setLineItems([]); }} style={{ padding:'9px 18px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#f97316,#ea580c)', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>+ New PO</button>
      </div>

      {/* Summary cards */}
      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        {[
          ['Total POs', orders.length, '#f1f5f9'],
          ['Pending', orders.filter(o=>o.status==='pending').length, '#fbbf24'],
          ['Ordered', orders.filter(o=>o.status==='ordered').length, '#60a5fa'],
          ['Received', orders.filter(o=>o.status==='received').length, '#34d399'],
          ['Total Spent', fmt(orders.filter(o=>o.status==='received').reduce((s,o)=>s+Number(o.total||0),0)), '#f97316'],
        ].map(([l,v,c]) => (
          <div key={l} style={{ background:'#252838', border:'1px solid #1a1d2e', borderRadius:10, padding:'12px 18px', flex:1, minWidth:120 }}>
            <div style={{ fontSize:10, color:'#e2e8f0', textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>{l}</div>
            <div style={{ fontSize:18, fontWeight:800, color:c }}>{v}</div>
          </div>
        ))}
      </div>

      {/* New PO Form */}
      {showForm && (
        <div style={{ background:'#252838', border:'1px solid #1a1d2e', borderRadius:12, padding:20, marginBottom:20 }}>
          <div style={{ fontSize:14, fontWeight:700, color:'#f1f5f9', marginBottom:16 }}>New Purchase Order</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:16 }}>
            <div>
              <div style={{ fontSize:10, color:'#e2e8f0', marginBottom:4, textTransform:'uppercase' }}>Vendor</div>
              <select value={form.vendor_id} onChange={e=>setForm(p=>({...p,vendor_id:e.target.value}))} style={inp}>
                <option value=''>Select vendor...</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:10, color:'#e2e8f0', marginBottom:4, textTransform:'uppercase' }}>PO Number</div>
              <input value={form.po_number} onChange={e=>setForm(p=>({...p,po_number:e.target.value}))} style={inp} placeholder='PO-2026-001' />
            </div>
            <div>
              <div style={{ fontSize:10, color:'#e2e8f0', marginBottom:4, textTransform:'uppercase' }}>Order Date</div>
              <input type='date' value={form.order_date} onChange={e=>setForm(p=>({...p,order_date:e.target.value}))} style={inp} />
            </div>
            <div>
              <div style={{ fontSize:10, color:'#e2e8f0', marginBottom:4, textTransform:'uppercase' }}>Status</div>
              <select value={form.status} onChange={e=>setForm(p=>({...p,status:e.target.value}))} style={inp}>
                {['pending','ordered','received','cancelled'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:10, color:'#e2e8f0', marginBottom:4, textTransform:'uppercase' }}>Expected Delivery</div>
              <input type='date' value={form.received_date} onChange={e=>setForm(p=>({...p,received_date:e.target.value}))} style={inp} />
            </div>
            <div>
              <div style={{ fontSize:10, color:'#e2e8f0', marginBottom:4, textTransform:'uppercase' }}>Notes</div>
              <input value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} style={inp} placeholder='Optional notes...' />
            </div>
          </div>

          {/* Line items */}
          <div style={{ fontSize:12, fontWeight:700, color:'#e2e8f0', marginBottom:10, textTransform:'uppercase' }}>Line Items</div>
          {lineItems.length > 0 && (
            <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:12, fontSize:12 }}>
              <thead><tr style={{ background:'#1e2235' }}>
                {['Description','Inventory Item','Qty','Unit Cost','Total',''].map(h => <th key={h} style={{ padding:'7px 10px', textAlign:'left', fontSize:10, color:'#e2e8f0', textTransform:'uppercase' }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {lineItems.map(l => (
                  <tr key={l.id} style={{ borderTop:'1px solid #1a1d2e' }}>
                    <td style={{ padding:'8px 10px', color:'#e2e8f0' }}>{l.description}</td>
                    <td style={{ padding:'8px 10px', color:'#e2e8f0' }}>{items.find(i=>i.id===Number(l.rental_item_id))?.name || '—'}</td>
                    <td style={{ padding:'8px 10px', color:'#e2e8f0' }}>{l.quantity}</td>
                    <td style={{ padding:'8px 10px', color:'#e2e8f0' }}>{fmt(l.unit_cost)}</td>
                    <td style={{ padding:'8px 10px', color:'#f97316', fontWeight:700 }}>{fmt(Number(l.quantity)*Number(l.unit_cost))}</td>
                    <td style={{ padding:'8px 10px' }}><button onClick={()=>removeLine(l.id)} style={{ background:'none', border:'none', color:'#f87171', cursor:'pointer' }}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
            <input style={{ ...inp, flex:3 }} placeholder='Description' value={newLI.description} onChange={e=>setNewLI(p=>({...p,description:e.target.value}))} />
            <select style={{ ...inp, flex:2 }} value={newLI.rental_item_id} onChange={e=>setNewLI(p=>({...p,rental_item_id:e.target.value}))}>
              <option value=''>Link to inventory item (optional)</option>
              {items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
            <input type='number' style={{ ...inp, width:60 }} placeholder='Qty' value={newLI.quantity} onChange={e=>setNewLI(p=>({...p,quantity:e.target.value}))} />
            <input type='number' style={{ ...inp, width:90 }} placeholder='$ cost' value={newLI.unit_cost} onChange={e=>setNewLI(p=>({...p,unit_cost:e.target.value}))} />
            <button onClick={addLine} style={{ padding:'8px 14px', borderRadius:6, border:'none', background:'#1a2e1a', color:'#4ade80', fontSize:12, fontWeight:700, cursor:'pointer' }}>+ Add</button>
          </div>

          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ fontSize:14, fontWeight:700, color:'#f97316' }}>Total: {fmt(form.total)}</div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={()=>setShowForm(false)} style={{ padding:'7px 16px', borderRadius:7, border:'1px solid #2a3050', background:'transparent', color:'#e2e8f0', fontSize:12, cursor:'pointer' }}>Cancel</button>
              <button onClick={save} disabled={saving} style={{ padding:'7px 18px', borderRadius:7, border:'none', background:'linear-gradient(135deg,#f97316,#ea580c)', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>{saving?'Saving...':'Create PO'}</button>
            </div>
          </div>
        </div>
      )}

      {/* PO List */}
      {loading ? <div style={{ textAlign:'center', padding:40, color:'#e2e8f0' }}>Loading...</div> : orders.length === 0 ? (
        <div style={{ textAlign:'center', padding:40, color:'#e2e8f0', fontSize:13 }}>No purchase orders yet. Create your first PO above.</div>
      ) : (
        <div style={{ background:'#252838', border:'1px solid #1a1d2e', borderRadius:10, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', tableLayout:'fixed' }}>
            <thead><tr style={{ background:'#1e2235' }}>
              {['PO #','Vendor','Date','Items','Total','Status',''].map(h => <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontSize:10, color:'#e2e8f0', fontWeight:700, textTransform:'uppercase', borderBottom:'1px solid #1a1d2e' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id} style={{ borderBottom:'1px solid #13141f', cursor:'pointer' }}
                  onMouseEnter={e=>e.currentTarget.style.background='#2a2d40'}
                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <td style={{ padding:'10px 12px', fontSize:12, fontWeight:700, color:'#60a5fa' }}>{o.po_number||'#'+o.id}</td>
                  <td style={{ padding:'10px 12px', fontSize:12, color:'#e2e8f0' }}>
                    {o.rental_vendors?.name && onVendorClick ? (
                      <span onClick={e=>{ e.stopPropagation(); onVendorClick(o.vendor_id); }}
                        style={{ color:'#60a5fa', cursor:'pointer', textDecoration:'underline' }}>
                        {o.rental_vendors.name}
                      </span>
                    ) : o.rental_vendors?.name || '—'}
                  </td>
                  <td style={{ padding:'10px 12px', fontSize:12, color:'#e2e8f0' }}>{fmtDate(o.order_date)}</td>
                  <td style={{ padding:'10px 12px', fontSize:12, color:'#e2e8f0' }}>{(o.rental_po_items||[]).length} items</td>
                  <td style={{ padding:'10px 12px', fontSize:13, fontWeight:700, color:'#f97316' }}>{fmt(o.total)}</td>
                  <td style={{ padding:'10px 12px' }}>
                    <select value={o.status} onChange={e=>updateStatus(o.id,e.target.value)}
                      style={{ background:'transparent', border:'none', fontSize:11, fontWeight:700, cursor:'pointer', outline:'none', color:STATUS_COLOR[o.status]||'#94a3b8' }}>
                      {['pending','ordered','received','cancelled'].map(s => <option key={s} value={s} style={{ background:'#252838', color:'#e2e8f0' }}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                    </select>
                  </td>
                  <td style={{ padding:'10px 12px' }}>
                    <button onClick={()=>deletePO(o.id)} style={{ background:'none', border:'none', color:'#f87171', cursor:'pointer', fontSize:12 }}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CalendarView({ orders = [], items = [], onOpenOrder, setSelectedOrderId, setView: setViewProp }) {
  const today = new Date(); today.setHours(0,0,0,0);
  const [offset, setOffset] = React.useState(0);
  const [viewMode, setViewMode] = React.useState('week');
  const [expandedOrder, setExpandedOrder] = React.useState(null);
  const [collapsed, setCollapsed] = React.useState({});
  const [tooltip, setTooltip] = React.useState(null);
  const [downtime, setDowntime] = React.useState([]);
  const [catFilter, setCatFilter] = React.useState('all');

  React.useEffect(() => {
    axios.get('/api/rentals/repairs/downtime/all').then(r => setDowntime(r.data.downtime || [])).catch(()=>{});
  }, []);

  const DAYS   = viewMode === 'week' ? 7 : 28;
  const DAY_W  = viewMode === 'week' ? 80 : 36;
  const ROW_H  = 44;
  const LABEL_W = 220;

  const startDate = new Date(today);
  startDate.setDate(startDate.getDate() + offset * DAYS);
  const dates = Array.from({length: DAYS}, (_, i) => {
    const d = new Date(startDate); d.setDate(d.getDate() + i); return d;
  });
  const fmt0 = d => d.toISOString().split('T')[0];
  const todayStr   = fmt0(today);
  const rangeStart = fmt0(startDate);
  const rangeEnd   = fmt0(dates[dates.length - 1]);

  const statusColor = { draft:'#94a3b8', reserved:'#f97316', started:'#3b82f6', returned:'#34d399', cancelled:'#ef4444' };
  const statusLabel = { draft:'Draft', reserved:'Reserved', started:'Active', returned:'Returned', cancelled:'Cancelled' };

  // Orders visible in this date range
  const visibleOrders = orders.filter(o => {
    if (!o.from_date || !o.to_date) return false;
    if (['cancelled','returned','archived'].includes(o.status)) return false;
    return fmt0(new Date(o.from_date)) <= rangeEnd && fmt0(new Date(o.to_date)) >= rangeStart;
  });

  // Overdue orders
  const overdueOrders = orders.filter(o =>
    o.status === 'started' && o.to_date && fmt0(new Date(o.to_date)) < todayStr
  );

  // Categories for filter
  const allCats = [...new Set(items.map(i => i.category).filter(Boolean))].sort();
  const filteredItems = catFilter === 'all' ? items : items.filter(i => i.category === catFilter);

  const categories = React.useMemo(() => {
    const cats = {};
    filteredItems.forEach(item => {
      const cat = item.category || 'Uncategorized';
      if (!cats[cat]) cats[cat] = [];
      cats[cat].push(item);
    });
    return cats;
  }, [filteredItems]);

  const getBar = (fromStr, toStr) => {
    const from = new Date(fromStr); from.setHours(0,0,0,0);
    const to   = new Date(toStr);   to.setHours(0,0,0,0);
    const startDiff = Math.max(0, Math.round((from - startDate) / 86400000));
    const endDiff   = Math.min(DAYS, Math.round((to - startDate) / 86400000) + 1);
    return { left: startDiff * DAY_W, width: Math.max(1, endDiff - startDiff) * DAY_W - 3 };
  };

  const getOrdersForItem = (itemId) => orders.filter(o => {
    if (!o.from_date || !o.to_date) return false;
    if (!o.rental_order_items?.some(i => i.rental_item_id === itemId)) return false;
    return fmt0(new Date(o.from_date)) <= rangeEnd && fmt0(new Date(o.to_date)) >= rangeStart;
  });

  const getDowntimeForItem = (itemId) => downtime.filter(d => {
    if (d.rental_item_id !== itemId) return false;
    return d.start_date <= rangeEnd && d.end_date >= rangeStart;
  });

  const dayLabel  = d => ['Su','Mo','Tu','We','Th','Fr','Sa'][d.getDay()];
  const isWeekend = d => d.getDay() === 0 || d.getDay() === 6;
  const todayOffset  = Math.round((today - startDate) / 86400000);
  const showTodayLine = todayOffset >= 0 && todayOffset < DAYS;

  const openOrder = (orderId) => {
    if (setSelectedOrderId && setViewProp) {
      setSelectedOrderId(orderId);
      setViewProp('orders');
    }
  };

  return (
    <div style={{ padding: '0 0 40px' }}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10,marginBottom:16}}>
        <div style={{fontSize:16,fontWeight:700,color:'#f1f5f9'}}>📅 Availability Calendar</div>
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          {/* Category filter */}
          <select value={catFilter} onChange={e=>setCatFilter(e.target.value)}
            style={{background:'#1e2235',border:'1px solid #1a1d2e',color:'#e2e8f0',borderRadius:7,padding:'5px 10px',fontSize:12,cursor:'pointer'}}>
            <option value="all">All Categories</option>
            {allCats.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {/* Week/Month toggle */}
          <div style={{display:'flex',background:'#1e2235',borderRadius:7,border:'1px solid #1a1d2e',overflow:'hidden'}}>
            {[['week','Week'],['month','Month']].map(([v,l])=>(
              <button key={v} onClick={()=>{setViewMode(v);setOffset(0);}} style={{padding:'5px 14px',border:'none',background:viewMode===v?'#f97316':'transparent',color:viewMode===v?'#fff':'#94a3b8',fontSize:12,fontWeight:700,cursor:'pointer'}}>{l}</button>
            ))}
          </div>
          <button onClick={()=>setOffset(o=>o-1)} style={{padding:'6px 12px',borderRadius:7,border:'1px solid #1a1d2e',background:'#1e2235',color:'#f1f5f9',cursor:'pointer',fontSize:12}}>←</button>
          <button onClick={()=>setOffset(0)} style={{padding:'6px 12px',borderRadius:7,border:'1px solid #1a1d2e',background:'#1e2235',color:'#f1f5f9',cursor:'pointer',fontSize:12}}>Today</button>
          <button onClick={()=>setOffset(o=>o+1)} style={{padding:'6px 12px',borderRadius:7,border:'1px solid #1a1d2e',background:'#1e2235',color:'#f1f5f9',cursor:'pointer',fontSize:12}}>→</button>
        </div>
      </div>

      {/* Date range label */}
      <div style={{fontSize:12,color:'#94a3b8',marginBottom:16}}>
        {startDate.toLocaleDateString('en-US',{month:'long',day:'numeric'})} — {dates[dates.length-1].toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}
      </div>

      {/* ── SECTION 1: OVERDUE ── */}
      {overdueOrders.length > 0 && (
        <div style={{background:'#1a0a0a',border:'1px solid #5a1a1a',borderRadius:10,padding:'12px 16px',marginBottom:16}}>
          <div style={{fontSize:11,fontWeight:700,color:'#f87171',textTransform:'uppercase',letterSpacing:1,marginBottom:10}}>⚠️ Overdue — Not Returned ({overdueOrders.length})</div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            {overdueOrders.map(o => (
              <div key={o.id} style={{display:'flex',alignItems:'center',gap:12,padding:'8px 12px',background:'rgba(248,113,113,0.08)',borderRadius:6,border:'1px solid rgba(248,113,113,0.2)'}}>
                <span onClick={()=>openOrder(o.id)} style={{fontSize:12,fontWeight:700,color:'#f87171',fontFamily:'monospace',cursor:'pointer'}}>{o.order_number||'#'+o.id}</span>
                <span onClick={()=>openOrder(o.id)} style={{fontSize:12,color:'#e2e8f0',flex:1,cursor:'pointer'}}>{o.client_name}</span>
                <span style={{fontSize:11,color:'#f87171'}}>Due: {o.to_date?.slice(0,10)}</span>
                <span style={{fontSize:11,color:'#e2e8f0'}}>${Number(o.total||0).toFixed(2)}</span>
                <button onClick={async(e)=>{e.stopPropagation();
                  if(!window.confirm('Send overdue reminder to '+o.client_name+'?')) return;
                  try{ const r=await axios.post('/api/rentals/orders/'+o.id+'/send-overdue-reminder');
                    alert('Sent! SMS: '+(r.data.smsSent?'✓':'—')+' Email: '+(r.data.emailSent?'✓':'—'));
                  }catch(err){alert('Failed: '+err.response?.data?.error);}}}
                  style={{padding:'4px 10px',borderRadius:6,border:'1px solid #f97316',background:'transparent',color:'#f97316',fontSize:11,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>
                  📨 Remind
                </button>
                <NextStepButton order={o} size="sm" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── SECTION 2: ORDERS GOING OUT ── */}
      <div style={{background:'#0f0f1a',border:'1px solid #1a1a2e',borderRadius:10,marginBottom:16,overflow:'hidden'}}>
        <div style={{padding:'10px 16px',borderBottom:'1px solid #1a1a2e',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{fontSize:12,fontWeight:700,color:'#e2e8f0',textTransform:'uppercase',letterSpacing:1}}>
            🚀 Active & Upcoming Orders ({visibleOrders.length})
          </div>
          <div style={{display:'flex',gap:8}}>
            {Object.entries(statusLabel).filter(([s])=>s!=='cancelled'&&s!=='returned').map(([s,l])=>(
              <div key={s} style={{display:'flex',alignItems:'center',gap:4,fontSize:10,color:'#94a3b8'}}>
                <div style={{width:8,height:8,borderRadius:2,background:statusColor[s]}}/>
                {l}
              </div>
            ))}
          </div>
        </div>

        {visibleOrders.length === 0 ? (
          <div style={{padding:'20px',textAlign:'center',color:'#555',fontSize:13}}>No active or upcoming orders in this period</div>
        ) : (
          <div style={{overflowX:'auto'}}>
            {/* Date header */}
            <div style={{display:'flex',borderBottom:'1px solid #1a1a2e',background:'#0a0a14'}}>
              <div style={{minWidth:LABEL_W,width:LABEL_W,padding:'8px 12px',fontSize:10,fontWeight:700,color:'#555',borderRight:'1px solid #1a1a2e',flexShrink:0}}>ORDER / CLIENT</div>
              {dates.map((d,i) => {
                const isToday = fmt0(d) === todayStr;
                return (
                  <div key={i} style={{minWidth:DAY_W,width:DAY_W,textAlign:'center',padding:'4px 0',background:isToday?'rgba(249,115,22,0.15)':isWeekend(d)?'rgba(255,255,255,0.01)':'transparent',borderRight:'1px solid rgba(255,255,255,0.03)',flexShrink:0}}>
                    <div style={{fontSize:9,color:isToday?'#f97316':'#555',fontWeight:700}}>{dayLabel(d)}</div>
                    <div style={{fontSize:viewMode==='week'?12:10,fontWeight:isToday?800:400,color:isToday?'#f97316':'#666'}}>{d.getDate()}</div>
                  </div>
                );
              })}
            </div>
            {/* Order rows */}
            {visibleOrders.map((order, oi) => {
              const bar = getBar(order.from_date, order.to_date);
              const color = statusColor[order.status] || '#94a3b8';
              return (
                <div key={order.id} style={{display:'flex',flexDirection:'column',borderBottom:'1px solid rgba(255,255,255,0.03)'}}>
                <div style={{display:'flex',minHeight:ROW_H,position:'relative',background:oi%2===0?'transparent':'rgba(255,255,255,0.01)'}}>
                  <div onClick={()=>setExpandedOrder(expandedOrder===order.id?null:order.id)} style={{minWidth:LABEL_W,width:LABEL_W,padding:'0 12px',display:'flex',flexDirection:'column',justifyContent:'center',borderRight:'1px solid #1a1a2e',flexShrink:0,cursor:'pointer'}}>
                    <div style={{display:'flex',alignItems:'center',gap:6}}>
                      <span style={{fontSize:11,fontWeight:700,color:'#60a5fa',fontFamily:'monospace'}}>{order.order_number||'#'+order.id}</span>
                      <span style={{fontSize:10,color:'#f97316',fontWeight:700}}>${Number(order.total||0).toFixed(0)}</span>
                    </div>
                    <div style={{fontSize:10,color:'#94a3b8',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{order.client_name}</div>
                  </div>
                  <div style={{position:'relative',display:'flex',flex:1}}>
                    {dates.map((d,i)=>(
                      <div key={i} style={{minWidth:DAY_W,width:DAY_W,height:'100%',background:fmt0(d)===todayStr?'rgba(249,115,22,0.06)':isWeekend(d)?'rgba(255,255,255,0.01)':'transparent',borderRight:'1px solid rgba(255,255,255,0.02)',flexShrink:0}}/>
                    ))}
                    {showTodayLine && <div style={{position:'absolute',left:todayOffset*DAY_W+DAY_W/2,top:0,bottom:0,width:2,background:'rgba(249,115,22,0.5)',zIndex:3,pointerEvents:'none'}}/>}
                    {bar.width > 0 && (
                      <div onClick={()=>openOrder(order.id)}
                        onMouseEnter={e=>setTooltip({order,x:e.clientX,y:e.clientY})}
                        onMouseLeave={()=>setTooltip(null)}
                        style={{position:'absolute',left:bar.left+1,width:bar.width,top:8,height:24,background:color,borderRadius:4,display:'flex',alignItems:'center',paddingLeft:8,overflow:'hidden',cursor:'pointer',zIndex:2,opacity:0.92,boxShadow:'0 1px 4px rgba(0,0,0,0.3)'}}>
                        <span style={{fontSize:10,fontWeight:700,color:'#fff',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                          {viewMode==='week' ? `${order.order_number||'#'+order.id} · ${order.client_name}` : (order.order_number||'#'+order.id)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                {expandedOrder===order.id && (
                  <div style={{background:'#0a0a14',borderTop:'1px solid #1a1a2e',padding:'10px 14px',display:'flex',gap:16,flexWrap:'wrap',alignItems:'center'}}>
                    <button onClick={()=>openOrder(order.id)} style={{background:'#1a1a3a',border:'1px solid #2a2a4a',color:'#60a5fa',borderRadius:5,padding:'5px 12px',fontSize:11,cursor:'pointer',fontWeight:600}}>Open Order</button>
                    <span style={{fontSize:11,color:'#94a3b8'}}>From: <span style={{color:'#fff'}}>{order.from_date?.slice(0,10)}</span></span>
                    <span style={{fontSize:11,color:'#94a3b8'}}>To: <span style={{color:'#fff'}}>{order.to_date?.slice(0,10)}</span></span>
                    <span style={{fontSize:11,color:statusColor[order.status]||'#888',fontWeight:700,textTransform:'capitalize'}}>{order.status}</span>
                    <span style={{fontSize:11,color:order.payment_status==='paid'?'#4ade80':'#f87171',fontWeight:700}}>{order.payment_status?.replace(/_/g,' ')}</span>
                    <span style={{fontSize:11,color:'#4ade80',fontWeight:700}}>${Number(order.total||0).toFixed(2)}</span>
                    {(order.rental_order_items||[]).length > 0 && (
                      <div style={{width:'100%',marginTop:4,display:'flex',gap:8,flexWrap:'wrap'}}>
                        {(order.rental_order_items||[]).map(i=>(
                          <span key={i.id} style={{background:'#252840',borderRadius:4,padding:'2px 8px',fontSize:10,color:'#e2e8f0'}}>{i.item_name}{i.variant_label?` · ${i.variant_label}`:''}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── SECTION 3: EQUIPMENT AVAILABILITY ── */}
      <div style={{background:'#0f0f1a',border:'1px solid #1a1a2e',borderRadius:10,overflow:'hidden'}}>
        <div style={{padding:'10px 16px',borderBottom:'1px solid #1a1a2e',cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center'}}
          onClick={()=>setCollapsed(p=>({...p,__equipSection:!p.__equipSection}))}>
          <div style={{fontSize:12,fontWeight:700,color:'#e2e8f0',textTransform:'uppercase',letterSpacing:1}}>
            📦 Equipment Availability
          </div>
          <span style={{color:'#555',fontSize:12}}>{collapsed.__equipSection ? '▶ Show' : '▼ Hide'}</span>
        </div>

        {!collapsed.__equipSection && (
          <div style={{overflowX:'auto'}}>
            {/* Sticky date header */}
            <div style={{display:'flex',borderBottom:'1px solid #1a1a2e',background:'#0a0a14',position:'sticky',top:0,zIndex:10}}>
              <div style={{minWidth:LABEL_W,width:LABEL_W,padding:'8px 12px',fontSize:10,fontWeight:700,color:'#555',borderRight:'1px solid #1a1a2e',flexShrink:0,letterSpacing:1}}>EQUIPMENT</div>
              {dates.map((d,i) => {
                const isToday = fmt0(d) === todayStr;
                return (
                  <div key={i} style={{minWidth:DAY_W,width:DAY_W,textAlign:'center',padding:'4px 0',background:isToday?'rgba(249,115,22,0.15)':isWeekend(d)?'rgba(255,255,255,0.01)':'transparent',borderRight:'1px solid rgba(255,255,255,0.03)',flexShrink:0}}>
                    <div style={{fontSize:9,color:isToday?'#f97316':'#555',fontWeight:700}}>{dayLabel(d)}</div>
                    <div style={{fontSize:viewMode==='week'?12:10,fontWeight:isToday?800:400,color:isToday?'#f97316':'#666'}}>{d.getDate()}</div>
                  </div>
                );
              })}
            </div>

            {Object.entries(categories).map(([cat, catItems]) => (
              <React.Fragment key={cat}>
                <div onClick={()=>setCollapsed(p=>({...p,[cat]:!p[cat]}))}
                  style={{display:'flex',alignItems:'center',gap:8,padding:'6px 12px',background:'rgba(255,255,255,0.03)',borderBottom:'1px solid rgba(255,255,255,0.05)',cursor:'pointer',userSelect:'none'}}>
                  <span style={{fontSize:10,color:'#94a3b8'}}>{collapsed[cat]?'▶':'▼'}</span>
                  <span style={{fontSize:11,fontWeight:700,color:'#e2e8f0',textTransform:'uppercase',letterSpacing:1}}>{cat}</span>
                  <span style={{fontSize:10,color:'#555'}}>({catItems.length})</span>
                </div>

                {!collapsed[cat] && catItems.map((item, rowIdx) => {
                  const rowOrders  = getOrdersForItem(item.id);
                  const rowDowntime = getDowntimeForItem(item.id);
                  const allBars    = rowOrders.length + rowDowntime.length;
                  const rowHeight  = Math.max(ROW_H, allBars * 24 + 12);
                  return (
                    <div key={item.id} style={{display:'flex',minHeight:rowHeight,position:'relative',borderBottom:'1px solid rgba(255,255,255,0.03)',background:rowIdx%2===0?'transparent':'rgba(255,255,255,0.01)'}}>
                      <div style={{minWidth:LABEL_W,width:LABEL_W,padding:'0 12px',display:'flex',flexDirection:'column',justifyContent:'center',borderRight:'1px solid rgba(255,255,255,0.08)',flexShrink:0}}>
                        <div style={{fontSize:12,fontWeight:600,color:'#e2e8f0',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{item.name}</div>
                        <div style={{fontSize:10,color:(item.quantity_available??item.quantity_total)===0?'#ef4444':(item.quantity_available??item.quantity_total)<item.quantity_total?'#fb923c':'#4ade80'}}>
                          {item.quantity_available??item.quantity_total} / {item.quantity_total} avail
                        </div>
                      </div>
                      <div style={{position:'relative',display:'flex',flex:1}}>
                        {dates.map((d,i)=>(
                          <div key={i} style={{minWidth:DAY_W,width:DAY_W,height:'100%',background:fmt0(d)===todayStr?'rgba(249,115,22,0.06)':isWeekend(d)?'rgba(255,255,255,0.01)':'transparent',borderRight:'1px solid rgba(255,255,255,0.02)',flexShrink:0}}/>
                        ))}
                        {showTodayLine && <div style={{position:'absolute',left:todayOffset*DAY_W+DAY_W/2,top:0,bottom:0,width:2,background:'rgba(249,115,22,0.5)',zIndex:3,pointerEvents:'none'}}/>}
                        {rowOrders.map((order, oi) => {
                          const bar = getBar(order.from_date, order.to_date);
                          if (bar.width <= 0) return null;
                          const color = statusColor[order.status] || '#94a3b8';
                          return (
                            <div key={order.id}
                              onClick={()=>openOrder(order.id)}
                              onMouseEnter={e=>setTooltip({order,x:e.clientX,y:e.clientY})}
                              onMouseLeave={()=>setTooltip(null)}
                              style={{position:'absolute',left:bar.left+1,width:bar.width,top:6+oi*24,height:20,background:color,borderRadius:4,display:'flex',alignItems:'center',paddingLeft:6,overflow:'hidden',cursor:'pointer',zIndex:2,opacity:order.status==='cancelled'?0.35:0.92,boxShadow:'0 1px 4px rgba(0,0,0,0.3)'}}>
                              <span style={{fontSize:10,fontWeight:700,color:'#fff',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                                {viewMode==='week'?`${order.order_number||'#'+order.id} · ${order.client_company||order.client_name||''}`:(order.order_number||'#'+order.id)}
                              </span>
                            </div>
                          );
                        })}
                        {rowDowntime.map((dt, di) => {
                          const bar = getBar(dt.start_date, dt.end_date);
                          if (bar.width <= 0) return null;
                          return (
                            <div key={'dt-'+dt.id} style={{position:'absolute',left:bar.left+1,width:bar.width,top:6+(rowOrders.length+di)*24,height:20,background:'#4b3a2a',border:'1px solid #92400e',borderRadius:4,display:'flex',alignItems:'center',paddingLeft:6,overflow:'hidden',zIndex:2,opacity:0.85}}>
                              <span style={{fontSize:10,fontWeight:700,color:'#fb923c',whiteSpace:'nowrap'}}>🔧 {dt.reason||'Repair'}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div style={{position:'fixed',left:tooltip.x+12,top:tooltip.y-10,zIndex:9999,background:'#252838',border:'1px solid #1a1d2e',borderRadius:8,padding:'10px 14px',pointerEvents:'none',boxShadow:'0 8px 24px rgba(0,0,0,0.5)',minWidth:200}}>
          <div style={{fontSize:12,fontWeight:700,color:'#f1f5f9',marginBottom:4}}>{tooltip.order.order_number||'#'+tooltip.order.id}</div>
          <div style={{fontSize:11,color:'#94a3b8',marginBottom:2}}>{tooltip.order.client_company||tooltip.order.client_name}</div>
          <div style={{fontSize:11,color:'#94a3b8',marginBottom:2}}>{tooltip.order.from_date?.slice(0,10)} → {tooltip.order.to_date?.slice(0,10)}</div>
          <div style={{display:'flex',justifyContent:'space-between',marginTop:6}}>
            <span style={{fontSize:11,fontWeight:700,color:statusColor[tooltip.order.status]||'#94a3b8'}}>{statusLabel[tooltip.order.status]||tooltip.order.status}</span>
            <span style={{fontSize:12,fontWeight:700,color:'#f97316'}}>${Number(tooltip.order.total||0).toLocaleString('en-US',{minimumFractionDigits:2})}</span>
          </div>
        </div>
      )}
    </div>
  );
}
// CollectPaymentModal moved to components/orderPipeline/CollectPaymentModal.jsx (rendered by the pipeline host, above any step).

// ── RETURN ORDER MODAL ────────────────────────────────────────────────────────
// ReturnOrderModal moved to components/orderPipeline/ReturnPage.jsx (the pipeline's Return page).

// ── ORDER FORM ────────────────────────────────────────────────────────────────
function OrderForm({ order, items, clients: allClients = [], categories: catProp = [], onSave, onClose, autoOpenPicker = false }) {
  const [f, setF] = useState({
    client_name: order?.client_name||'', client_email: order?.client_email||'',
    client_phone: order?.client_phone||'', client_company: order?.client_company||'',
    client_id: order?.client_id||null,
    fulfillment_type: order?.fulfillment_type||'pickup', delivery_address: order?.delivery_address||'',
    delivery_fee: order?.delivery_fee??'', status: order?.status||'draft',
    payment_status: order?.payment_status||'payment_due', from_date: order?.from_date||'',
    to_date: order?.to_date||'', notes: order?.notes||'',
    discount_amount: order?.discount_amount||0,
    tax_exempt: !!(order?.tax_exempt && order?.tax_exempt_source === 'staff'),
    tax_exempt_reason: (order?.tax_exempt_source === 'staff' ? order?.tax_exempt_reason : '') || '',
    deposit_amount: order?.deposit_amount??'',
    // Existing lines carry product_group_id (rental_item_id is a legacy alias, not a column) and
    // their assigned unit; both must ride along or an edit rebuilds the lines with no product
    // and no unit (destructive-edit bug, 2026-09-25).
    selectedItems: (order?.rental_order_items||[]).map(i=>({
      rental_item_id:i.product_group_id||i.rental_item_id, variant_id:i.variant_id||null,
      item_name:i.item_name, quantity:i.quantity||1,
      rate_type:i.rate_type||'day', unit_price:i.unit_price||0, total_price:i.total_price||0
    })),
    ...(order?.id?{id:order.id}:{})
  });

  // Client autocomplete
  const [clientSearch, setClientSearch]   = useState(f.client_name || '');
  const [clientResults, setClientResults] = useState([]);
  const [showDropdown,  setShowDropdown]  = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDiscount, setClientDiscount] = useState(0);  // insurance discount %
  const clientRef = React.useRef(null);

  // Search clients as user types
  React.useEffect(() => {
    if (!clientSearch || clientSearch.length < 1) { setClientResults(allClients.slice(0,200)); return; }
    const q = clientSearch.toLowerCase();
    setClientResults(allClients.filter(c =>
      (c.name||'').toLowerCase().includes(q) ||
      (c.company||'').toLowerCase().includes(q) ||
      (c.email||'').toLowerCase().includes(q)
    ).slice(0, 8));
  }, [clientSearch, allClients]);

  const pickClient = (c) => {
    setSelectedClient(c);
    setClientSearch(c.name);
    setClientDiscount(Number(c.insurance_discount||0));
    setF(p => ({
      ...p,
      client_id:      c.id,
      client_name:    c.name,
      client_email:   c.email||'',
      client_phone:   c.phone||'',
      client_company: c.company||'',
    }));
    setShowDropdown(false);
  };

  // Close dropdown on outside click
  React.useEffect(() => {
    const handler = (e) => { if (clientRef.current && !clientRef.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const [ratePresets, setRatePresets] = React.useState([]);
  const [catOpen, setCatOpen] = React.useState({});  // { category: bool }
  const [itemSearch, setItemSearch] = React.useState('');
  const [showOrderScanner, setShowOrderScanner] = React.useState(false);
  const [showItemPicker, setShowItemPicker] = React.useState(!!autoOpenPicker);   // "+ Add equipment" opens the form with the picker already up
  const itemSearchRef = React.useRef(null);
  React.useEffect(() => {
    axios.get('/api/rentals/settings').then(r => {
      setRatePresets(r.data.settings?.rate_presets || []);
    }).catch(()=>{});
  }, []);

  const applyPreset = (preset) => {
    setF(p => ({...p, selectedItems: p.selectedItems.map(i => {
      const eq = items.find(x => x.id === i.rental_item_id);
      const basePrice = eq ? (i.rate_type==='hour'?eq.price_hour:i.rate_type==='week'?eq.price_week:i.rate_type==='month'?eq.price_month:eq.price_day) : parseFloat(i.unit_price)||0;
      const newPrice = Math.round(basePrice * parseFloat(preset.multiplier) * 100) / 100;
      const qty = parseInt(i.quantity)||1;
      const d = daysBetween(f.from_date, f.to_date)||1;
      const total = (i.rate_type==='day'||i.rate_type==='hour') ? newPrice*d*qty : newPrice*qty;
      return {...i, unit_price: newPrice, total_price: total, _priceOverridden: true};
    })}));
  };

  // Auto-apply insurance discount when client selected
  const applyInsuranceDiscount = () => {
    if (!clientDiscount) return;
    setF(p => ({...p, selectedItems: p.selectedItems.map(i => {
      const eq = items.find(x => x.id === i.rental_item_id);
      const basePrice = eq ? (i.rate_type==='hour'?eq.price_hour:i.rate_type==='week'?eq.price_week:i.rate_type==='month'?eq.price_month:eq.price_day) : parseFloat(i.unit_price)||0;
      const discounted = Math.round(basePrice * (1 - clientDiscount/100) * 100) / 100;
      const qty = parseInt(i.quantity)||1;
      const d = daysBetween(f.from_date, f.to_date)||1;
      const total = (i.rate_type==='day'||i.rate_type==='hour') ? discounted*d*qty : discounted*qty;
      return {...i, unit_price: discounted, total_price: total, _priceOverridden: true};
    })}));
  };

  const [distLoading, setDistLoading] = React.useState(false);
  const [distError,   setDistError]   = React.useState('');
  const distTimer = React.useRef(null);

  const days = daysBetween(f.from_date, f.to_date);
  const datesReversed = !!(f.from_date && f.to_date && f.to_date < f.from_date);
  // "Past" is judged by the location's clock: the order's own location on an edit, the default
  // location for a new order. A range whose RETURN date is behind that today is past (a range that
  // started earlier but ends today or later is an in-progress rental — not past).
  const [todayLocal, setTodayLocal] = React.useState(null);
  React.useEffect(() => {
    let on = true;
    const tzOfOrder = order?.rental_locations?.timezone;
    const apply = (tz) => { if (!on) return; try { setTodayLocal(new Date().toLocaleDateString('en-CA', tz ? { timeZone: tz } : {})); } catch { setTodayLocal(new Date().toISOString().slice(0, 10)); } };
    if (tzOfOrder) apply(tzOfOrder); else getBizInfo().then(b => apply(b.timezone)).catch(() => apply(null));
    return () => { on = false; };
  }, [order?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const datesPast = !!(!datesReversed && f.to_date && todayLocal && f.to_date < todayLocal);

  React.useEffect(() => {
    if (f.fulfillment_type !== 'delivery' || !f.delivery_address || f.delivery_address.length < 8) return;
    clearTimeout(distTimer.current);
    distTimer.current = setTimeout(async () => {
      setDistLoading(true); setDistError('');
      try {
        const r = await axios.get(`/api/rentals/distance?destination=${encodeURIComponent(f.delivery_address)}`);
        const miles = r.data.miles_one_way;
        setF(p => ({ ...p, _delivery_miles: miles }));
        const s = await axios.get('/api/rentals/settings');
        const settings = s.data.settings || {};
        const free = Number(settings.free_miles || 10);
        const rate = Number(settings.rate_per_mile || 2.50);
        const min  = Number(settings.min_delivery_fee || 0);
        const rt   = miles * 2;
        const fee  = rt <= free ? min : Math.max(min, (rt - free) * rate);
        setF(p => ({ ...p, delivery_fee: Math.round(fee * 100) / 100, _delivery_miles: miles }));
      } catch(e) { setDistError(e.response?.data?.error || 'Could not calculate distance'); }
      setDistLoading(false);
    }, 900);
    return () => clearTimeout(distTimer.current);
  }, [f.delivery_address, f.fulfillment_type]);

  const toggleItem = (item) => {
    const exists = f.selectedItems.find(i=>i.rental_item_id===item.id);
    if (exists) {
      setF(p=>({...p, selectedItems: p.selectedItems.filter(i=>i.rental_item_id!==item.id)}));
    } else {
      const rateType  = getAutoRateType(item, days);
      const unitPrice = getAutoPrice(item, rateType, days, clientDiscount);
      const total     = getLineTotal(item, rateType, unitPrice, 1, days||1);
      setF(p=>({...p, selectedItems:[...p.selectedItems,{
        rental_item_id:item.id, item_name:item.name, quantity:1,
        rate_type:rateType, unit_price:unitPrice, total_price:total,
      }]}));
    }
  };

  // Re-calculate all line items when dates change
  React.useEffect(() => {
    if (!days) return;
    setF(p => ({...p, selectedItems: p.selectedItems.map(i => {
      if (i._priceOverridden) return i;
      const eq = items.find(x => x.id === i.rental_item_id);
      const priceSource = eq || (i._price_day != null ? {
        price_day: i._price_day, price_week: i._price_week,
        price_month: i._price_month, price_hour: i._price_hour
      } : null);
      if (!priceSource) return i;
      const rateType  = getAutoRateType(priceSource, days);
      const unitPrice = getAutoPrice(priceSource, rateType, days, clientDiscount);
      const total     = getLineTotal(priceSource, rateType, unitPrice, i.quantity, days);
      return {...i, rate_type:rateType, unit_price:unitPrice, total_price:total};
    })}));
  }, [days]);

  const updateLineItem = (itemId, field, value) => {
    setF(p=>({...p, selectedItems:p.selectedItems.map(i=>{
      if(i.rental_item_id!==itemId) return i;
      const updated = {...i,[field]:value};
      const eq = items.find(x=>x.id===itemId);
      if(eq && field==='rate_type' && !i._priceOverridden) {
        updated.unit_price = getAutoPrice(eq, value, days, clientDiscount);
      }
      if(field==='unit_price') updated._priceOverridden = true;
      updated.total_price = getLineTotal(eq, updated.rate_type, updated.unit_price, updated.quantity, days||1);
      return updated;
    })}));
  };

  // Group active items by category for picker
  const activeItems = items.filter(i => i.is_active !== false);
  const categories  = catProp.length > 0 ? catProp : [...new Set(activeItems.map(i => i.category||'Other'))].sort();
  const filteredItems = itemSearch
    ? activeItems.filter(i => (i.name||'').toLowerCase().includes(itemSearch.toLowerCase()) || (i.category||'').toLowerCase().includes(itemSearch.toLowerCase()))
    : activeItems;

  // Totals come from the server (POST /api/rentals/orders/preview-totals —
  // the same computeTotals the save path stores). Nothing about tax,
  // discount or the total is computed in the browser.
  const [preview, setPreview] = React.useState(null);
  const [previewError, setPreviewError] = React.useState('');
  const previewTimer = React.useRef(null);
  const previewKey = JSON.stringify({
    items: f.selectedItems.map(i => ({ rental_item_id: i.rental_item_id, item_name: i.item_name, quantity: i.quantity, unit_price: i.unit_price, total_price: i.total_price })),
    delivery_fee: f.fulfillment_type === 'delivery' ? Number(f.delivery_fee||0) : 0,
    discount_amount: Number(f.discount_amount||0), client_id: f.client_id || null,
    tax_exempt: !!f.tax_exempt, tax_exempt_reason: f.tax_exempt ? f.tax_exempt_reason : '',
    deposit_amount: f.deposit_amount === '' ? null : Number(f.deposit_amount),
  });
  React.useEffect(() => {
    const body = JSON.parse(previewKey);
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      try {
        const r = await axios.post('/api/rentals/orders/preview-totals', body);
        setPreview(r.data.totals); setPreviewError('');
      } catch (e) { setPreview(null); setPreviewError(e.response?.data?.error || 'Could not compute totals'); }
    }, 300);
    return () => clearTimeout(previewTimer.current);
  }, [previewKey]);
  const total = Number(preview?.total || 0);
  const suggestedDeposit = Number(preview?.deposit_amount || 0);
  const clientExempt = preview?.tax_exempt && preview?.tax_exempt_source === 'client';

  const submit = () => {
    if(!f.client_name) return alert('Client name required');
    if(datesReversed) return; // the button is disabled; belt and braces
    if(f.tax_exempt && !String(f.tax_exempt_reason||'').trim()) return alert('A reason is required to mark this order tax-exempt.');
    // status: new orders start as Draft (DB default) and move through the pipeline; edits never
    // touch status here. payment_status: derived from the ledger, never sent.
    const { selectedItems, status, payment_status, ...orderFields } = f;
    // Sends null rather than a client-side guess when blank — the backend
    // applies the tenant's real deposit rule to the taxed total.
    onSave({ ...orderFields, discount_amount: Number(f.discount_amount||0), tax_exempt: !!f.tax_exempt, tax_exempt_reason: f.tax_exempt ? f.tax_exempt_reason : null,
      deposit_amount: f.deposit_amount ? Number(f.deposit_amount) : null, items:f.selectedItems });
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',zIndex:1000,display:'flex',alignItems:'flex-start',justifyContent:'center',paddingTop:40,overflowY:'auto'}}>
      <div style={{background:'#252838',border:'1px solid #1a1d2e',borderRadius:12,width:'90%',maxWidth:920,padding:28,marginBottom:40}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:22}}>
          <div style={{fontSize:16,fontWeight:700,color:'#f1f5f9'}}>{f.id?'Edit Order':'New Order'}</div>
          <div style={{display:'flex',gap:10}}><button data-testid="save-order" disabled={datesReversed} title={datesReversed?'Fix the dates first':''} style={{...S.btn('primary'),...(datesReversed?{opacity:.45,cursor:'not-allowed'}:datesPast?{background:'#92400e'}:{})}} onClick={submit}>{datesPast?'Save (past dates)':'Save Order'}</button><button style={S.btn('ghost')} onClick={onClose}>Cancel</button></div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 300px',gap:20}}>
          <div>
            {/* Client */}
            <div style={S.card}>
              <div style={{fontSize:11,fontWeight:700,color:'#f97316',letterSpacing:1,marginBottom:14,textTransform:'uppercase'}}>Client</div>

              {/* Client autocomplete */}
              <div style={{position:'relative',marginBottom:12}} ref={clientRef}>
                <label style={S.label}>Client *</label>
                <div style={{display:'flex',gap:6}}>
                  <div style={{position:'relative',flex:1}}>
                    <input style={S.input} value={clientSearch}
                      placeholder="Type name, company, or email..."
                      onChange={e=>{ setClientSearch(e.target.value); setShowDropdown(true); setF(p=>({...p,client_name:e.target.value})); }}
                      onFocus={()=>setShowDropdown(true)}
                      autoComplete="off"
                    />
                    {showDropdown && clientResults.length > 0 && (
                      <div style={{position:'absolute',top:'100%',left:0,right:0,background:'#252838',border:'1px solid #2a3050',borderRadius:8,zIndex:200,boxShadow:'0 8px 24px rgba(0,0,0,0.5)',maxHeight:220,overflowY:'auto'}}>
                        {clientResults.map(c=>(
                          <div key={c.id} style={{padding:'9px 14px',cursor:'pointer',borderBottom:'1px solid #13141f',display:'flex',justifyContent:'space-between',alignItems:'center'}}
                            onMouseDown={()=>pickClient(c)}
                            onMouseEnter={e=>e.currentTarget.style.background='#2a2d40'}
                            onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                            <div>
                              <div style={{fontWeight:600,color:'#e2e8f0',fontSize:12}}>{c.name}</div>
                              <div style={{fontSize:10,color:'#e2e8f0'}}>{c.company||''}{c.company&&c.email?' · ':''}{c.email||''}</div>
                            </div>
                            {c.insurance_cert && <span style={{fontSize:9,background:'#14532d',color:'#4ade80',padding:'1px 6px',borderRadius:4,fontWeight:700}}>🛡 {c.insurance_discount}% off</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button type="button" style={{...S.btn('ghost'),fontSize:11,padding:'7px 10px',whiteSpace:'nowrap'}}
                    onClick={()=>{ setClientResults(allClients.slice(0,200)); setShowDropdown(d=>!d); }}>▼ All</button>
                </div>
                {selectedClient && clientDiscount > 0 && (
                  <div style={{marginTop:6,padding:'6px 10px',background:'rgba(74,222,128,0.08)',border:'1px solid rgba(74,222,128,0.2)',borderRadius:6,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <span style={{fontSize:11,color:'#4ade80'}}>🛡 Insurance discount: <strong>{clientDiscount}% off rentals</strong></span>
                    <button type="button" style={{...S.btn('sm'),fontSize:10,background:'#14532d',color:'#4ade80',padding:'3px 8px'}}
                      onClick={applyInsuranceDiscount}>Apply</button>
                  </div>
                )}
              </div>

              <div style={S.g2}>
                <div><label style={S.label}>Email</label><input style={S.input} value={f.client_email} onChange={e=>setF(p=>({...p,client_email:e.target.value}))} /></div>
                <div><label style={S.label}>Phone</label><input style={S.input} value={f.client_phone} onChange={e=>setF(p=>({...p,client_phone:e.target.value}))} /></div>
                <div><label style={S.label}>Company</label><input style={S.input} value={f.client_company} onChange={e=>setF(p=>({...p,client_company:e.target.value}))} /></div>
                {/* Status and payment status are not form fields: a new order is a Draft and the
                    pipeline button owns every move after save; payment status is derived from the ledger. */}
                <div><label style={S.label}>From Date</label><input type="date" data-testid="order-from" style={S.input} value={f.from_date} onChange={e=>setF(p=>({...p,from_date:e.target.value}))} /></div>
                <div><label style={S.label}>To Date</label><input type="date" data-testid="order-to" min={f.from_date||undefined} style={{...S.input,...(datesReversed?{borderColor:'#f87171'}:{})}} value={f.to_date} onChange={e=>setF(p=>({...p,to_date:e.target.value}))} />
                  {datesPast&&<div data-testid="dates-past" style={{fontSize:11,color:'#fbbf24',marginTop:4,fontWeight:700}}>These dates are in the past — saving is fine (backfill); Reserve will need the manual override</div>}
                  {datesReversed&&<div data-testid="dates-reversed" style={{fontSize:11,color:'#f87171',marginTop:4,fontWeight:700}}>Return before pickup — the To date must be on or after the From date</div>}
                </div>
              </div>

              <div style={{marginTop:12,marginBottom:4}}>
                <label style={S.label}>Fulfillment Type</label>
                <div style={{display:'flex',gap:0,borderRadius:8,overflow:'hidden',border:'1px solid #2a3050',width:'fit-content'}}>
                  {['pickup','delivery'].map(t=>(
                    <button key={t} type="button"
                      style={{padding:'9px 24px',border:'none',cursor:'pointer',fontWeight:700,fontSize:13,
                        background:f.fulfillment_type===t?'#f97316':'#252838',
                        color:f.fulfillment_type===t?'#fff':'#94a3b8',transition:'all .15s'}}
                      onClick={()=>setF(p=>({...p,fulfillment_type:t}))}>
                      {t==='pickup'?'🏪 Pickup':'🚚 Delivery'}
                    </button>
                  ))}
                </div>
              </div>

              {f.fulfillment_type==='delivery'&&(
                <div style={{marginTop:8,display:'grid',gridTemplateColumns:'1fr 130px',gap:12}}>
                  <div>
                    <label style={S.label}>Delivery Address</label>
                    <div style={{ position:'relative' }}>
                      <input style={S.input} value={f.delivery_address} onChange={e=>setF(p=>({...p,delivery_address:e.target.value}))} placeholder="1234 Job Site Ave, City, ST" />
                      {distLoading && <span style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', fontSize:11, color:'#60a5fa' }}>📍 Calculating...</span>}
                    </div>
                    {distError && <div style={{ fontSize:11, color:'#f87171', marginTop:3 }}>⚠️ {distError}</div>}
                    {f._delivery_miles > 0 && !distLoading && <div style={{ fontSize:11, color:'#4ade80', marginTop:3 }}>✓ {f._delivery_miles} mi one-way</div>}
                  </div>
                  <div>
                    <label style={S.label}>Delivery Fee</label>
                    <input type="number" style={S.input} value={f.delivery_fee} onChange={e=>setF(p=>({...p,delivery_fee:e.target.value}))} />
                  </div>
                </div>
              )}
              <div style={{marginTop:12}}><label style={S.label}>Notes</label><textarea style={{...S.input,resize:'vertical'}} rows={2} value={f.notes} onChange={e=>setF(p=>({...p,notes:e.target.value}))} /></div>
            </div>

            {/* Equipment — category grouped */}
            <div style={S.card}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
                <div style={{fontSize:11,fontWeight:700,color:'#f97316',letterSpacing:1,textTransform:'uppercase'}}>
                  Equipment {days>0&&<span style={{color:'#e2e8f0',fontWeight:400}}>· {days} day{days>1?'s':''}</span>}
                  {f.selectedItems.length>0&&<span style={{marginLeft:8,color:'#4ade80',fontWeight:400}}>{f.selectedItems.length} selected</span>}
                </div>
                <div style={{display:'flex',gap:6,alignItems:'center'}}>
                  {ratePresets.filter(p=>p.name).length > 0 && f.selectedItems.length > 0 && (
                    <>
                      <span style={{fontSize:10,color:'#e2e8f0'}}>Preset:</span>
                      {ratePresets.filter(p=>p.name).map(p=>(
                        <button key={p.id} type="button" style={{padding:'3px 8px',borderRadius:5,border:'1px solid #2a3050',background:'#252838',color:'#e2e8f0',fontSize:10,cursor:'pointer'}}
                          onClick={()=>applyPreset(p)}>{p.name}</button>
                      ))}
                    </>
                  )}
                </div>
              </div>

              {/* Equipment picker */}
              <div style={{display:'flex',gap:8,marginBottom:10}}>
                <button type="button" style={{...S.btn('primary'),fontSize:13,padding:'9px 18px',fontWeight:700}} onClick={()=>setShowItemPicker(true)}>+ Browse & Select Equipment</button>
                <button type="button" style={{...S.btn('ghost'),fontSize:13,padding:'7px 10px',display:'flex',alignItems:'center',gap:4}} onClick={()=>setShowOrderScanner(true)}>📷 Scan</button>
              </div>
              {showOrderScanner && (
                <BarcodeScanner items={items} onFound={item=>{ toggleItem(item); }} onClose={()=>{ setShowOrderScanner(false); itemSearchRef.current?.focus(); }} />
              )}
              {showItemPicker && (
                <ItemPickerModal
                  selectedVariantIds={f.selectedItems.map(i => i.variant_id).filter(Boolean)}
                  onSelect={(product, variant) => {
                    const exists = f.selectedItems.find(i => i.variant_id === variant.id);
                    if (!exists) {
                      const priceDay   = Number(variant.price_day_override   || product.base_price_day   || product.price_day   || 0);
                      const priceWeek  = Number(variant.price_week_override  || product.base_price_week  || product.price_week  || 0);
                      const priceMonth = Number(variant.price_month_override || product.base_price_month || product.price_month || 0);
                      const priceHour  = Number(variant.price_hour_override  || product.base_price_hour  || product.price_hour  || 0);
                      const normalizedProduct = { ...product, price_day: priceDay, price_week: priceWeek, price_month: priceMonth, price_hour: priceHour };
                      const rateType  = getAutoRateType(normalizedProduct, days);
                      const unitPrice = getAutoPrice(normalizedProduct, rateType, days, clientDiscount);
                      const total     = getLineTotal(normalizedProduct, rateType, unitPrice, 1, days||1);
                      setF(p => ({...p, selectedItems: [...p.selectedItems, {
                        rental_item_id: product.id,
                        product_group_id: product.id,
                        variant_id: variant.id,
                        item_name: product.name,
                        variant_label: [variant.serial_number || variant.sku, variant.condition, variant.hour_meter > 0 ? variant.hour_meter + ' hrs' : null].filter(Boolean).join(' · '),
                        quantity: 1,
                        rate_type: rateType,
                        unit_price: unitPrice,
                        total_price: total,
                        _price_day: priceDay,
                        _price_week: priceWeek,
                        _price_month: priceMonth,
                        _price_hour: priceHour,
                      }]}));
                    }
                  }}
                  onClose={() => setShowItemPicker(false)}
                />
              )}
            </div>
          </div>

          {/* Summary Panel */}
          <div style={{position:'sticky',top:0,alignSelf:'start'}}>
            <div style={S.card}>
              <div style={{fontSize:11,fontWeight:700,color:'#f97316',letterSpacing:1,marginBottom:14,textTransform:'uppercase'}}>Summary</div>
              {datesReversed
                ? <div style={{padding:'7px 10px',background:'#3a1a1a',borderRadius:5,marginBottom:10,fontSize:11,color:'#f87171',fontWeight:700}}>Return before pickup · {fmtDate(f.from_date)} → {fmtDate(f.to_date)}</div>
                : days>0&&<div style={{padding:'7px 10px',background:'#1e2030',borderRadius:5,marginBottom:10,fontSize:11,color:'#e2e8f0'}}>{days} day{days>1?'s':''} · {fmtDate(f.from_date)} → {fmtDate(f.to_date)}</div>}
              {clientDiscount>0&&<div style={{padding:'5px 8px',background:'rgba(74,222,128,0.08)',borderRadius:5,marginBottom:8,fontSize:10,color:'#4ade80'}}>🛡 {clientDiscount}% insurance discount applied</div>}
              {f.selectedItems.length===0?<div style={{fontSize:11,color:'#e2e8f0',textAlign:'center',padding:16}}>No items selected</div>:
                f.selectedItems.map(i=>(
                  <div key={i.rental_item_id} style={{display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:'1px solid #1a1d2e',fontSize:11}}>
                    <div>
                      <div style={{color:'#e2e8f0'}}>{i.item_name} ×{i.quantity}</div>
                      {i.variant_label && <div style={{fontSize:10,color:'#4ade80',marginTop:1}}>{i.variant_label}</div>}
                      <div style={{fontSize:9,color:'#e2e8f0'}}>
                        {i.rate_type === 'week'  ? `${Math.ceil(days/7)} week${Math.ceil(days/7)!==1?'s':''} @ ${fmt(i.unit_price)}/wk`
                        : i.rate_type === 'month' ? `${Math.ceil(days/30)} month${Math.ceil(days/30)!==1?'s':''} @ ${fmt(i.unit_price)}/mo`
                        : i.rate_type === 'hour'  ? `${days} day${days!==1?'s':''} @ ${fmt(i.unit_price)}/hr`
                        : `${days} day${days!==1?'s':''} @ ${fmt(i.unit_price)}/day`}
                      </div>
                    </div>
                    <span style={{color:'#f1f5f9',fontWeight:700}}>{fmt(i.total_price)}</span>
                  </div>
                ))
              }
              {Number(f.delivery_fee)>0&&<div style={{display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:'1px solid #1a1d2e',fontSize:11}}><span style={{color:'#e2e8f0'}}>Delivery</span><span style={{color:'#60a5fa',fontWeight:700}}>{fmt(f.delivery_fee)}</span></div>}
              <div style={{marginTop:8}}>
                <label style={S.label}>Discount ($)</label>
                <input type="number" min="0" step="0.01" style={S.input} value={f.discount_amount} onChange={e=>setF(p=>({...p,discount_amount:e.target.value}))} placeholder="0.00" />
              </div>
              {Number(preview?.discount_amount)>0&&<div style={{display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:'1px solid #1a1d2e',fontSize:11}}><span style={{color:'#e2e8f0'}}>Discount</span><span style={{color:'#4ade80',fontWeight:700}}>−{fmt(preview.discount_amount)}</span></div>}
              <div style={{marginTop:8}}>
                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:'#e2e8f0',cursor:clientExempt?'not-allowed':'pointer'}}>
                  <input type="checkbox" checked={!!f.tax_exempt || !!clientExempt} disabled={!!clientExempt} onChange={e=>setF(p=>({...p,tax_exempt:e.target.checked}))} style={{accentColor:'#f97316'}} />
                  Tax exempt{clientExempt ? ' (client certificate on file)' : ''}
                </label>
                {clientExempt && <div style={{fontSize:10,color:'#94a3b8',marginTop:2}}>{preview.tax_exempt_reason}</div>}
                {f.tax_exempt && !clientExempt && (
                  <textarea style={{...S.input,height:48,resize:'none',marginTop:6}} placeholder="Reason (required) — e.g. resale certificate #, government PO" value={f.tax_exempt_reason} onChange={e=>setF(p=>({...p,tax_exempt_reason:e.target.value}))} />
                )}
              </div>
              {preview?.tax_exempt
                ? <div style={{display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:'1px solid #1a1d2e',fontSize:11}}><span style={{color:'#e2e8f0'}}>Tax</span><span style={{color:'#a78bfa',fontWeight:700}}>Exempt</span></div>
                : (preview?.tax_breakdown||[]).map((p,idx)=>(
                  <div key={idx} style={{display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:'1px solid #1a1d2e',fontSize:11}}><span style={{color:'#e2e8f0'}}>{p.name} ({Number(p.rate)}%)</span><span style={{color:'#a78bfa',fontWeight:700}}>{fmt(p.amount)}</span></div>
                ))}
              {preview?.tax_warning==='no_tax_configured' && (
                <div data-testid="tax-warning" style={{marginTop:8,padding:'8px 10px',background:'rgba(251,191,36,0.08)',border:'1px solid rgba(251,191,36,0.35)',borderRadius:6,fontSize:11,color:'#fbbf24',lineHeight:1.4}}>⚠️ No tax configured — this order will be saved with $0 tax. Set tax parts in Settings → Tax, or confirm "no sales tax" there.</div>
              )}
              {previewError && <div style={{marginTop:8,fontSize:11,color:'#f87171'}}>⚠️ {previewError}</div>}
              <div style={{marginTop:12,paddingTop:10,borderTop:'1px solid #2a2d3e'}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:8,fontSize:11}}>
                  <span style={{color:'#e2e8f0'}}>Suggested Deposit</span>
                  <span style={{color:'#fbbf24',fontWeight:700}}>{fmt(suggestedDeposit)}</span>
                </div>
                <div style={{marginBottom:12}}>
                  <label style={S.label}>Override Deposit</label>
                  <input type="number" style={S.input} placeholder={suggestedDeposit} value={f.deposit_amount} onChange={e=>setF(p=>({...p,deposit_amount:e.target.value}))} />
                </div>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 0',borderTop:'1px solid #2a2d3e',marginTop:8}}>
                  <span style={{fontSize:13,fontWeight:700,color:'#f1f5f9'}}>Total</span>
                  <span style={{fontSize:18,fontWeight:800,color:'#f97316'}}>{preview ? fmt(total) : '…'}</span>
                </div>
                <button data-testid="save-order-bottom" disabled={datesReversed} style={{...S.btn('primary'),width:'100%',marginTop:8,...(datesReversed?{opacity:.45,cursor:'not-allowed'}:datesPast?{background:'#92400e'}:{})}} onClick={submit}>{datesPast?'Save (past dates)':'Save Order'}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── UNITS MANAGER MODAL ────────────────────────────────────────────────────────
const UNIT_STATUSES = ['available','rented','maintenance','repair','retired'];

function UnitsManagerModal({ product, onClose, onChanged, showFlash }) {
  const [variants, setVariants] = React.useState(product.variants || []);
  const [loading, setLoading]   = React.useState(true);
  const [savingId, setSavingId] = React.useState(null);
  const [deletingId, setDeletingId] = React.useState(null);
  const [adding, setAdding]     = React.useState(false);
  const blankNew = { sku:'', serial_number:'', unit_status:'available' };
  const [newUnit, setNewUnit]   = React.useState(blankNew);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`/api/rentals/products/${product.id}/variants`);
      setVariants(r.data.variants || []);
    } catch (e) {
      showFlash('Failed to load units', 'error');
    } finally {
      setLoading(false);
    }
  }, [product.id]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => { load(); }, [load]);

  const addUnit = async () => {
    setAdding(true);
    try {
      const r = await axios.post(`/api/rentals/products/${product.id}/variants`, {
        sku: newUnit.sku || null,
        serial_number: newUnit.serial_number || null,
        unit_status: newUnit.unit_status || 'available',
      });
      setVariants(p => [...p, r.data.variant]);
      setNewUnit(blankNew);
      showFlash('Unit added');
      onChanged?.();
    } catch (e) {
      showFlash(e.response?.data?.error || 'Failed to add unit', 'error');
    } finally {
      setAdding(false);
    }
  };

  const patchUnit = async (variantId, updates) => {
    setSavingId(variantId);
    try {
      const r = await axios.patch(`/api/rentals/products/variants/${variantId}`, updates);
      setVariants(p => p.map(v => v.id === variantId ? r.data.variant : v));
      onChanged?.();
    } catch (e) {
      showFlash(e.response?.data?.error || 'Failed to update unit', 'error');
    } finally {
      setSavingId(null);
    }
  };

  const deleteUnit = async (variant) => {
    const label = variant.serial_number || variant.sku || 'this unit';
    if (!window.confirm(`Remove ${label}?`)) return;
    setDeletingId(variant.id);
    try {
      await axios.delete(`/api/rentals/products/variants/${variant.id}`);
      setVariants(p => p.filter(v => v.id !== variant.id));
      showFlash('Unit removed');
      onChanged?.();
    } catch (e) {
      const body = e.response?.data;
      if (e.response?.status === 409) {
        if (body?.active_order_ids?.length) {
          showFlash(`Can't delete: unit has an active order (#${body.active_order_ids.join(', #')})`, 'error');
        } else if (body?.downtime_ids?.length) {
          showFlash(`Can't delete: unit has an open repair/downtime record`, 'error');
        } else {
          showFlash(body?.error || `Can't delete: unit is in use`, 'error');
        }
      } else {
        showFlash(body?.error || 'Failed to delete unit', 'error');
      }
    } finally {
      setDeletingId(null);
    }
  };

  const S = {
    overlay: { position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:4000,display:'flex',alignItems:'center',justifyContent:'center',padding:20 },
    box:     { background:'#252838',border:'1px solid #1a1d2e',borderRadius:14,width:'100%',maxWidth:680,maxHeight:'85vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.5)' },
    hdr:     { padding:'16px 20px',borderBottom:'1px solid #1a1d2e',display:'flex',justifyContent:'space-between',alignItems:'center',position:'sticky',top:0,background:'#252838',zIndex:1 },
    body:    { padding:'20px' },
    inp:     { padding:'7px 9px',borderRadius:6,border:'1px solid #2a3050',background:'#181928',color:'#e2e8f0',fontSize:12,width:'100%',boxSizing:'border-box' },
    sel:     { padding:'7px 9px',borderRadius:6,border:'1px solid #2a3050',background:'#181928',color:'#e2e8f0',fontSize:12,width:'100%',boxSizing:'border-box' },
    th:      { padding:'8px 10px',textAlign:'left',fontSize:10,fontWeight:700,color:'#94a3b8',textTransform:'uppercase',letterSpacing:0.5,borderBottom:'1px solid #1a1d2e' },
    td:      { padding:'6px 10px',borderBottom:'1px solid #1a1d2e' },
  };

  const statusColor = { available:'#4ade80', rented:'#60a5fa', maintenance:'#fbbf24', repair:'#f87171', retired:'#94a3b8' };

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={S.box}>
        <div style={S.hdr}>
          <div>
            <div style={{fontWeight:800,fontSize:15,color:'#e2e8f0'}}>🔧 Manage Units</div>
            <div style={{fontSize:11,color:'#e2e8f0',marginTop:2}}>{product.name}</div>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',color:'#e2e8f0',cursor:'pointer',fontSize:18}}>✕</button>
        </div>
        <div style={S.body}>
          {loading ? (
            <div style={{color:'#e2e8f0',fontSize:12,textAlign:'center',padding:20}}>Loading units...</div>
          ) : (
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr>
                  <th style={S.th}>SKU</th>
                  <th style={S.th}>Serial Number</th>
                  <th style={S.th}>Status</th>
                  <th style={{...S.th,textAlign:'right'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {variants.map(v => (
                  <UnitRow key={v.id} variant={v} S={S} statusColor={statusColor}
                    saving={savingId===v.id} deleting={deletingId===v.id}
                    onPatch={(updates)=>patchUnit(v.id, updates)}
                    onDelete={()=>deleteUnit(v)} />
                ))}
                {variants.length===0 && (
                  <tr><td colSpan={4} style={{...S.td,textAlign:'center',color:'#94a3b8',padding:20}}>No units yet — add one below.</td></tr>
                )}
                {/* Add row */}
                <tr>
                  <td style={S.td}><input style={S.inp} placeholder="SKU" value={newUnit.sku} onChange={e=>setNewUnit(p=>({...p,sku:e.target.value}))} /></td>
                  <td style={S.td}><input style={S.inp} placeholder="Serial number" value={newUnit.serial_number} onChange={e=>setNewUnit(p=>({...p,serial_number:e.target.value}))} /></td>
                  <td style={S.td}>
                    <select style={S.sel} value={newUnit.unit_status} onChange={e=>setNewUnit(p=>({...p,unit_status:e.target.value}))}>
                      {UNIT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td style={{...S.td,textAlign:'right'}}>
                    <button disabled={adding} onClick={addUnit}
                      style={{padding:'6px 14px',borderRadius:6,border:'none',background:'#f97316',color:'#fff',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                      {adding?'Adding...':'+ Add Unit'}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function UnitRow({ variant, S, statusColor, saving, deleting, onPatch, onDelete }) {
  const [sku, setSku] = React.useState(variant.sku || '');
  const [serial, setSerial] = React.useState(variant.serial_number || '');

  React.useEffect(() => { setSku(variant.sku || ''); }, [variant.sku]);
  React.useEffect(() => { setSerial(variant.serial_number || ''); }, [variant.serial_number]);

  return (
    <tr style={{opacity:deleting?0.4:1}}>
      <td style={S.td}>
        <input style={S.inp} value={sku} onChange={e=>setSku(e.target.value)}
          onBlur={()=>{ if (sku !== (variant.sku||'')) onPatch({ sku: sku || null }); }} />
      </td>
      <td style={S.td}>
        <input style={S.inp} value={serial} onChange={e=>setSerial(e.target.value)}
          onBlur={()=>{ if (serial !== (variant.serial_number||'')) onPatch({ serial_number: serial || null }); }} />
      </td>
      <td style={S.td}>
        <select style={{...S.sel,color:statusColor[variant.unit_status]||'#e2e8f0',fontWeight:700}}
          value={variant.unit_status} disabled={saving}
          onChange={e=>onPatch({ unit_status: e.target.value })}>
          {UNIT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </td>
      <td style={{...S.td,textAlign:'right'}}>
        <button disabled={deleting} onClick={onDelete}
          style={{padding:'5px 12px',borderRadius:6,border:'none',background:'#7f1d1d',color:'#fca5a5',fontSize:11,fontWeight:600,cursor:'pointer'}}>
          {deleting?'...':'Delete'}
        </button>
      </td>
    </tr>
  );
}

function ItemForm({ item, categories: catProp = [], defaultCategoryId, onSave, onClose }) {
  const CATS = catProp.length > 0 ? catProp : ['Grinders & Polishers','Floor Buffers & Burnishers','Dust Extractors','Auto Scrubbers','Poly Pumps','Concrete Saws','Pressure Washers','Generators','Cargo Trailers','Diamond Tools','Chemicals','Other'];
  const [tab, setTab] = React.useState('basic');
  const [vendors, setVendors] = React.useState([]);
  const [dbCategories, setDbCategories] = React.useState([]);
  const [newCatName, setNewCatName] = React.useState('');
  const [showNewCat, setShowNewCat] = React.useState(false);
  const [f, setF] = useState({
    name:item?.name||'', category:item?.category||CATS[0], category_id:item?.category_id||((!item?.id && defaultCategoryId)||''), sku:item?.sku||'', barcode:item?.barcode||'',
    description:item?.description||'', img_url:item?.img_url||'',
    inventory_mode:item?.inventory_mode||'rent',
    price_hour:item?.price_hour??'', price_day:item?.price_day??'',
    price_week:item?.price_week??'', price_month:item?.price_month??'',
    weekly_threshold:item?.weekly_threshold||5,
    monthly_threshold:item?.monthly_threshold||21,
    deposit:item?.deposit??'', quantity_total:item?.quantity_total||1,
    is_active: item?.is_active !== false,
    purchase_price:item?.purchase_price||'',
    purchase_date:item?.purchase_date||'',
    vendor_id:item?.vendor_id||'',
    usage_capacity:item?.usage_capacity||'',
    usage_rate:item?.usage_rate||'',
    usage_auto_retire:item?.usage_auto_retire||false,
    ...(item?.id?{id:item.id}:{}),
    _serial_no:   item?.custom_data?.serial_no   || '',
    _make:        item?.custom_data?.make         || '',
    _model:       item?.custom_data?.model        || '',
    _year:        item?.custom_data?.year         || '',
    _hour_meter:  item?.custom_data?.hour_meter   || '',
    _fuel_type:   item?.custom_data?.fuel_type    || '',
  });

  React.useEffect(() => {
    axios.get('/api/rental-assets/vendors').then(r => setVendors(r.data.vendors || [])).catch(()=>{});
    axios.get('/api/rentals/categories').then(r => setDbCategories(r.data.categories || [])).catch(()=>{});
  }, []);

  const selectedCat = dbCategories.find(c => c.id === f.category_id);
  const catSupportsUsage = selectedCat?.custom_field_schema?.some?.(s => s === 'usage') || true; // default: show usage for rent items

  const createInlineCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      const r = await axios.post('/api/rentals/categories', { name: newCatName.trim() });
      const newCat = r.data.category;
      setDbCategories(prev => [...prev, newCat]);
      setF(p => ({ ...p, category_id: newCat.id }));
      setNewCatName('');
      setShowNewCat(false);
    } catch (e) { alert(e.response?.data?.error || 'Failed to create category'); }
  };
  const [customFieldDefs, setCustomFieldDefs] = React.useState([]);
  const [customFieldVals, setCustomFieldVals] = React.useState(item?.custom_data || {});
  const [newFieldLabel, setNewFieldLabel] = React.useState('');
  const [addingField, setAddingField] = React.useState(false);
  React.useEffect(() => {
    const cat = dbCategories.find(c => c.id === f.category_id);
    const schema = cat?.custom_field_schema || [];
    // schema is array of {name, type, options} or legacy field objects
    const normalized = schema.map(s => typeof s === 'string'
      ? { field_key: s.toLowerCase().replace(/\s+/g,'_'), field_label: s, field_type: 'text' }
      : { field_key: s.key || s.field_key || s.name?.toLowerCase().replace(/\s+/g,'_'), field_label: s.label || s.field_label || s.name, field_type: s.type || s.field_type || 'text', options: s.options || [] }
    );
    setCustomFieldDefs(normalized);
  }, [f.category_id, dbCategories]);
  const saveNewField = async () => {
    if (!newFieldLabel.trim()) return;
    const cat = dbCategories.find(c => c.id === f.category_id);
    if (!cat) { alert('Select a category first'); return; }
    const key = newFieldLabel.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    const newField = { key, label: newFieldLabel.trim(), type: 'text' };
    const existingSchema = cat.custom_field_schema || [];
    const updatedSchema = [...existingSchema, newField];
    try {
      await axios.patch(`/api/rentals/categories/${cat.id}`, { custom_field_schema: updatedSchema });
      // Update local dbCategories
      setDbCategories(prev => prev.map(c => c.id === cat.id ? { ...c, custom_field_schema: updatedSchema } : c));
      setCustomFieldDefs(prev => [...prev, { field_key: key, field_label: newFieldLabel.trim(), field_type: 'text' }]);
      setNewFieldLabel('');
      setAddingField(false);
    } catch(e) { alert('Failed to save field'); }
  };

  const [priceTemplates, setPriceTemplates] = React.useState([]);
  const [selectedTemplate, setSelectedTemplate] = React.useState('');
  const [templateBaseRate, setTemplateBaseRate] = React.useState('');
  React.useEffect(() => {
    axios.get('/api/rental-price-templates').then(r => setPriceTemplates(r.data.templates || [])).catch(()=>{});
  }, []);
  const applyTemplate = (tplId, baseRate) => {
    const tpl = priceTemplates.find(t => t.id === tplId);
    if (!tpl || !baseRate) return;
    const base = Number(baseRate);
    const updates = {};
    (tpl.tiers || []).forEach(tier => {
      const key = tier.duration_days <= 1 ? 'price_day' : tier.duration_days <= 7 ? 'price_week' : 'price_month';
      updates[key] = Math.round(base * Number(tier.multiplier) * 100) / 100;
    });
    setF(p => ({ ...p, ...updates }));
  };

  const suggestWeek  = () => { if(f.price_day) setF(p=>({...p, price_week: Math.round(f.price_day * f.weekly_threshold * 0.85 * 100)/100 })); };
  const suggestMonth = () => { if(f.price_week) setF(p=>({...p, price_month: Math.round(f.price_week * 3 * 0.85 * 100)/100 })); };

  const submit = () => {
    if(!f.name) return alert('Name required');
    const custom_data = {};
    if(f._serial_no)  custom_data.serial_no  = f._serial_no;
    if(f._make)       custom_data.make        = f._make;
    if(f._model)      custom_data.model       = f._model;
    if(f._year)       custom_data.year        = f._year;
    if(f._hour_meter) custom_data.hour_meter  = f._hour_meter;
    if(f._fuel_type)  custom_data.fuel_type   = f._fuel_type;
    const merged = { ...custom_data, ...customFieldVals };
    // Strip underscore-prefixed fields — they live in custom_data, not as top-level columns
    const payload = { ...f };
    Object.keys(payload).forEach(k => { if (k.startsWith('_')) delete payload[k]; });
    // Default empty numeric fields to 0 on save
    ['price_hour','price_day','price_week','price_month','deposit','usage_capacity','usage_rate'].forEach(k => { payload[k] = Number(payload[k]) || 0; });
    ['purchase_date','last_service_date','next_service_due'].forEach(k => { if (payload[k] === '' || payload[k] === undefined) payload[k] = null; });
    onSave({ ...payload, custom_data: Object.keys(merged).length ? merged : (f.custom_data || {}) });
  };

  const [showSkuScanner, setShowSkuScanner] = React.useState(false);
  const note = { fontSize:10, color:'#e2e8f0', marginTop:3 };
  const TABS = [['basic','📋 Basic Info'],['pricing','💰 Pricing'],['machine','🔧 Machine'],['purchase','🏭 Purchase']];

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:1000,display:'flex',alignItems:'flex-start',justifyContent:'center',paddingTop:40,overflowY:'auto'}}>
      <div style={{background:'#252838',border:'1px solid #1a1d2e',borderRadius:14,width:660,marginBottom:40,overflow:'hidden'}}>

        {/* Header */}
        <div style={{padding:'20px 24px 0',borderBottom:'1px solid #1a1d2e'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
            <div style={{fontSize:16,fontWeight:700,color:'#f1f5f9'}}>{f.id?'✏️ Edit Item':'➕ Add Inventory Item'}</div>
            <div style={{display:'flex',gap:8}}>
              <button style={S.btn('primary')} onClick={submit}>Save Item</button>
              <button style={S.btn('ghost')} onClick={onClose}>Cancel</button>
            </div>
          </div>
          {/* Tabs */}
          <div style={{display:'flex',gap:0}}>
            {TABS.map(([v,l]) => (
              <div key={v} onClick={()=>setTab(v)} style={{padding:'8px 18px',fontSize:12,fontWeight:600,cursor:'pointer',color:tab===v?'#f97316':'#94a3b8',borderBottom:tab===v?'2px solid #f97316':'2px solid transparent',marginBottom:-1,whiteSpace:'nowrap'}}>
                {l}
              </div>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div style={{padding:'24px'}}>

          {/* ── BASIC INFO ── */}
          {tab==='basic' && (
            <div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>
                <div style={{gridColumn:'1/-1'}}>
                  <label style={S.label}>Item Name *</label>
                  <input style={S.input} value={f.name} onChange={e=>setF(p=>({...p,name:e.target.value}))} autoFocus placeholder="e.g. Husqvarna PG 820 RC Floor Grinder" />
                </div>
                <div style={{display:'none'}}>
                  <label style={S.label}>Category (legacy)</label>
                  <select style={S.select} value={f.category} onChange={e=>setF(p=>({...p,category:e.target.value}))}>
                    {CATS.map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={S.label}>Category</label>
                  {!showNewCat ? (
                    <select style={S.select} value={f.category_id} onChange={e => {
                      if (e.target.value === '__new__') { setShowNewCat(true); return; }
                      const selCat = dbCategories.find(c => String(c.id) === String(e.target.value));
                      setF(p => ({...p, category_id: e.target.value, category: selCat?.name || p.category}));
                    }}>
                      <option value="">— Select category —</option>
                      {dbCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      <option value="__new__">+ New category</option>
                    </select>
                  ) : (
                    <div style={{display:'flex',gap:6}}>
                      <input autoFocus style={{...S.input,flex:1}} value={newCatName} onChange={e=>setNewCatName(e.target.value)} placeholder="Category name..." onKeyDown={e=>{ if(e.key==='Enter')createInlineCategory(); if(e.key==='Escape'){setShowNewCat(false);setNewCatName('');} }} />
                      <button type="button" style={{background:'#f97316',border:'none',borderRadius:6,color:'#fff',fontSize:11,fontWeight:700,padding:'0 12px',cursor:'pointer'}} onClick={createInlineCategory}>Add</button>
                      <button type="button" style={{background:'none',border:'1px solid #1a1d2e',borderRadius:6,color:'#94a3b8',fontSize:11,padding:'0 8px',cursor:'pointer'}} onClick={()=>{setShowNewCat(false);setNewCatName('');}}>X</button>
                    </div>
                  )}
                </div>
                <div>
                  <label style={S.label}>SKU</label>
                  <div style={{display:'flex',gap:4}}>
                    <input style={{...S.input,flex:1}} value={f.sku} onChange={e=>setF(p=>({...p,sku:e.target.value}))} placeholder="e.g. GRN-PG820" />
                    <button type="button" onClick={()=>setShowSkuScanner(true)} style={{padding:'7px 10px',borderRadius:6,border:'1px solid #2a2d3e',background:'#2e3150',color:'#f97316',fontSize:13,cursor:'pointer',flexShrink:0}} title="Scan barcode into SKU">📷</button>
                  </div>
                  {showSkuScanner===true && <BarcodeScanner onRawScan={val=>{ setF(p=>({...p,sku:val})); setShowSkuScanner(false); }} onClose={()=>setShowSkuScanner(false)} />}
                </div>
                <div>
                  <label style={S.label}>Quantity</label>
                  <input type="number" min={1} style={S.input} value={f.quantity_total} onChange={e=>setF(p=>({...p,quantity_total:e.target.value}))} />
                </div>
                <div>
                  <label style={S.label}>Deposit ($)</label>
                  <input type="number" style={S.input} value={f.deposit} onChange={e=>setF(p=>({...p,deposit:e.target.value}))} />
                </div>
                <div>
                  <label style={S.label}>Barcode / QR</label>
                  <div style={{display:'flex',gap:4}}>
                    <input style={{...S.input,flex:1}} value={f.barcode||''} onChange={e=>setF(p=>({...p,barcode:e.target.value}))} placeholder="Scan or type" />
                    <button type="button" onClick={()=>setShowSkuScanner('barcode')} style={{padding:'7px 10px',borderRadius:6,border:'1px solid #2a2d3e',background:'#2e3150',color:'#f97316',fontSize:13,cursor:'pointer',flexShrink:0}} title="Scan barcode">📷</button>
                  </div>
                  {showSkuScanner==='barcode' && <BarcodeScanner onRawScan={val=>{ setF(p=>({...p,barcode:val})); setShowSkuScanner(false); }} onClose={()=>setShowSkuScanner(false)} />}
                </div>
                <div style={{display:'flex',alignItems:'center',gap:10,paddingTop:18}}>
                  <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                    <input type="checkbox" checked={f.is_active} onChange={e=>setF(p=>({...p,is_active:e.target.checked}))} style={{accentColor:'#f97316',width:14,height:14}} />
                    <span style={{fontSize:12,color:'#e2e8f0',fontWeight:600}}>Active</span>
                  </label>
                </div>
              </div>

              {/* Inventory Mode Toggle */}
              <div style={{marginBottom:14}}>
                <label style={S.label}>Inventory Mode</label>
                <div style={{display:'flex',gap:0,borderRadius:6,overflow:'hidden',border:'1px solid #1e2235',width:'fit-content'}}>
                  {['rent','sell'].map(m => (
                    <button key={m} type="button" onClick={()=>setF(p=>({...p,inventory_mode:m}))}
                      style={{padding:'8px 24px',fontSize:12,fontWeight:700,cursor:'pointer',border:'none',
                        background:f.inventory_mode===m?(m==='rent'?'#1a3a2a':'#1a2a4a'):'#1e2030',
                        color:f.inventory_mode===m?(m==='rent'?'#34d399':'#60a5fa'):'#64748b',
                        letterSpacing:0.5,textTransform:'uppercase'}}>
                      {m==='rent'?'Rent':'Sell'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Usage Fields — only for Rent mode */}
              {f.inventory_mode === 'rent' && (
                <div style={{marginBottom:14,padding:'14px',background:'#1e2235',borderRadius:10,border:'1px solid #1a1d2e'}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#f97316',marginBottom:10,textTransform:'uppercase',letterSpacing:.5}}>Usage Tracking (mm)</div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
                    <div>
                      <label style={S.label}>Capacity (mm)</label>
                      <input type="number" style={S.input} value={f.usage_capacity} onChange={e=>setF(p=>({...p,usage_capacity:e.target.value}))} placeholder="e.g. 25" />
                    </div>
                    <div>
                      <label style={S.label}>Rate per mm</label>
                      <input type="number" style={S.input} value={f.usage_rate} onChange={e=>setF(p=>({...p,usage_rate:e.target.value}))} placeholder="e.g. 0.5" />
                    </div>
                    <div style={{display:'flex',alignItems:'flex-end',paddingBottom:2}}>
                      <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                        <input type="checkbox" checked={f.usage_auto_retire} onChange={e=>setF(p=>({...p,usage_auto_retire:e.target.checked}))} style={{accentColor:'#f97316',width:14,height:14}} />
                        <span style={{fontSize:11,color:'#e2e8f0',fontWeight:600}}>Auto-retire at 0</span>
                      </label>
                    </div>
                  </div>
                  <div style={{fontSize:10,color:'#64748b',marginTop:6}}>Track diamond tooling wear — usage_remaining decrements each rental. Auto-retire deactivates the item when fully consumed.</div>
                </div>
              )}

              {/* Description */}
              <div style={{marginBottom:14}}>
                <label style={S.label}>Description</label>
                <textarea style={{...S.input,resize:'vertical'}} rows={2} value={f.description} onChange={e=>setF(p=>({...p,description:e.target.value}))} placeholder="Optional description for storefront..." />
              </div>

              {/* Photo */}
              <div>
                <label style={S.label}>Item Photo</label>
                <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap',marginBottom:8}}>
                  {f.img_url && <img src={f.img_url} alt="preview" style={{width:72,height:72,objectFit:'cover',borderRadius:8,border:'1px solid #1a1d2e'}} onError={e=>e.target.style.display='none'} />}
                  <label style={{cursor:'pointer',background:'#2e3150',border:'1px dashed #2a3050',borderRadius:8,padding:'10px 18px',fontSize:12,color:'#e2e8f0',fontWeight:600,display:'flex',alignItems:'center',gap:6}}>
                    📷 {f.img_url ? 'Change Photo' : 'Upload Photo'}
                    <input type="file" accept="image/*" style={{display:'none'}} onChange={async e=>{
                      const file = e.target.files[0]; if(!file) return;
                      const reader = new FileReader();
                      reader.onload = async ev => {
                        const base64 = ev.target.result.split(',')[1];
                        try {
                          const res = await axios.post(`/api/rentals/items/${f.id||'new'}/photo`, { photo_base64: base64, file_type: file.type });
                          if(res.data.url) setF(p=>({...p,img_url:res.data.url}));
                        } catch(err) { setF(p=>({...p,img_url:ev.target.result})); }
                      };
                      reader.readAsDataURL(file);
                    }} />
                  </label>
                  {f.img_url && <button onClick={()=>setF(p=>({...p,img_url:''}))} style={{background:'none',border:'none',color:'#f87171',cursor:'pointer',fontSize:12}}>✕ Remove</button>}
                </div>
                <input style={{...S.input,fontSize:11,color:'#e2e8f0'}} value={f.img_url} onChange={e=>setF(p=>({...p,img_url:e.target.value}))} placeholder="Or paste image URL..." />
              </div>
            </div>
          )}

          {/* ── PRICING ── */}
          {tab==='pricing' && (
            <div>
              {priceTemplates.length > 0 && (
                <div style={{padding:'14px',background:'#1e2235',borderRadius:10,border:'1px solid #1a1d2e',marginBottom:14}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#a78bfa',marginBottom:10,textTransform:'uppercase',letterSpacing:.5}}>Pricing Template</div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr auto',gap:10,alignItems:'end'}}>
                    <div>
                      <label style={S.label}>Template</label>
                      <select style={S.select} value={selectedTemplate} onChange={e=>setSelectedTemplate(e.target.value)}>
                        <option value="">— Select template —</option>
                        {priceTemplates.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={S.label}>Base Daily Rate ($)</label>
                      <input type="number" style={S.input} value={templateBaseRate} onChange={e=>setTemplateBaseRate(e.target.value)} placeholder="e.g. 150" />
                    </div>
                    <button type="button" style={{...S.btn('primary'),padding:'9px 14px',opacity:selectedTemplate&&templateBaseRate?1:0.4}} disabled={!selectedTemplate||!templateBaseRate}
                      onClick={()=>applyTemplate(selectedTemplate,templateBaseRate)}>Apply</button>
                  </div>
                  {selectedTemplate && (()=>{ const tpl=priceTemplates.find(t=>t.id===selectedTemplate); return tpl ? (
                    <div style={{marginTop:8,display:'flex',gap:8,flexWrap:'wrap'}}>
                      {(tpl.tiers||[]).map((tier,i)=>(
                        <span key={i} style={{fontSize:10,padding:'3px 8px',borderRadius:4,background:'#2e3150',color:'#e2e8f0'}}>{tier.label}: x{tier.multiplier}</span>
                      ))}
                    </div>
                  ) : null; })()}
                </div>
              )}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:16}}>
                <div>
                  <label style={S.label}>Price / Hour</label>
                  <input type="number" style={S.input} value={f.price_hour||''} placeholder="Leave blank to disable" onChange={e=>setF(p=>({...p,price_hour:e.target.value}))} />
                </div>
                <div>
                  <label style={S.label}>Price / Day * <span style={{color:'#f97316',fontWeight:400,fontSize:9}}>REQUIRED</span></label>
                  <input type="number" style={S.input} value={f.price_day} onChange={e=>setF(p=>({...p,price_day:e.target.value}))} />
                </div>
              </div>

              <div style={{padding:'14px',background:'#1e2235',borderRadius:10,border:'1px solid #1a1d2e',marginBottom:12}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
                  <span style={{fontSize:11,fontWeight:700,color:'#60a5fa'}}>📅 Weekly Rate</span>
                  <button type="button" style={{fontSize:10,padding:'4px 12px',background:'#f97316',color:'#fff',border:'none',borderRadius:5,cursor:'pointer',fontWeight:700}} onClick={suggestWeek}>⚡ Auto-suggest</button>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  <div>
                    <label style={S.label}>Price / Week</label>
                    <input type="number" style={S.input} value={f.price_week||''} placeholder="Leave blank to disable" onChange={e=>setF(p=>({...p,price_week:e.target.value}))} />
                    <div style={note}>Leave 0 to disable</div>
                  </div>
                  <div>
                    <label style={S.label}>Trigger: after X days</label>
                    <input type="number" min={1} max={30} style={S.input} value={f.weekly_threshold} onChange={e=>setF(p=>({...p,weekly_threshold:e.target.value}))} />
                    <div style={note}>Apply weekly rate when ≥ {f.weekly_threshold} days</div>
                  </div>
                </div>
                {f.price_day > 0 && f.price_week > 0 && (
                  <div style={{marginTop:8,fontSize:10,color:'#4ade80'}}>✓ Client saves {Math.round((1-f.price_week/(f.price_day*f.weekly_threshold))*100)}% at {f.weekly_threshold} days</div>
                )}
              </div>

              <div style={{padding:'14px',background:'#1e2235',borderRadius:10,border:'1px solid #1a1d2e'}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
                  <span style={{fontSize:11,fontWeight:700,color:'#a78bfa'}}>📆 Monthly Rate</span>
                  <button type="button" style={{fontSize:10,padding:'4px 12px',background:'#f97316',color:'#fff',border:'none',borderRadius:5,cursor:'pointer',fontWeight:700}} onClick={suggestMonth}>⚡ Auto-suggest</button>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  <div>
                    <label style={S.label}>Price / Month</label>
                    <input type="number" style={S.input} value={f.price_month||''} placeholder="Leave blank to disable" onChange={e=>setF(p=>({...p,price_month:e.target.value}))} />
                    <div style={note}>Leave 0 to disable</div>
                  </div>
                  <div>
                    <label style={S.label}>Trigger: after X days</label>
                    <input type="number" min={1} max={90} style={S.input} value={f.monthly_threshold} onChange={e=>setF(p=>({...p,monthly_threshold:e.target.value}))} />
                    <div style={note}>Apply monthly rate when ≥ {f.monthly_threshold} days</div>
                  </div>
                </div>
                {f.price_week > 0 && f.price_month > 0 && (
                  <div style={{marginTop:8,fontSize:10,color:'#4ade80'}}>✓ Client saves {Math.round((1-f.price_month/(f.price_week*4))*100)}% vs weekly</div>
                )}
              </div>
            </div>
          )}

          {/* ── MACHINE DETAILS ── */}
          {tab==='machine' && (
            <div>
              <div style={{fontSize:12,color:'#e2e8f0',marginBottom:16}}>Machine details are used to auto-populate repair work orders and track service history.</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
                <div><label style={S.label}>Make</label><input style={S.input} value={f._make} onChange={e=>setF(p=>({...p,_make:e.target.value}))} placeholder="e.g. Husqvarna" /></div>
                <div><label style={S.label}>Model</label><input style={S.input} value={f._model} onChange={e=>setF(p=>({...p,_model:e.target.value}))} placeholder="e.g. PG 820 RC" /></div>
                <div><label style={S.label}>Year</label><input style={S.input} value={f._year} onChange={e=>setF(p=>({...p,_year:e.target.value}))} placeholder="e.g. 2022" /></div>
                <div><label style={S.label}>Serial Number</label><input style={S.input} value={f._serial_no} onChange={e=>setF(p=>({...p,_serial_no:e.target.value}))} placeholder="e.g. HQ-2022-XXXX" /></div>
                <div><label style={S.label}>Hour Meter</label><input type="number" style={S.input} value={f._hour_meter} onChange={e=>setF(p=>({...p,_hour_meter:e.target.value}))} placeholder="Current hours" /></div>
                <div>
                  <label style={S.label}>Fuel Type</label>
                  <select style={S.select} value={f._fuel_type} onChange={e=>setF(p=>({...p,_fuel_type:e.target.value}))}>
                    <option value="">— Select —</option>
                    <option value="gas">Gas</option>
                    <option value="diesel">Diesel</option>
                    <option value="propane">Propane</option>
                    <option value="electric">Electric</option>
                    <option value="battery">Battery</option>
                    <option value="manual">Manual / N/A</option>
                  </select>
                </div>
              </div>

              {/* ── Custom fields ── */}
              {customFieldDefs.length > 0 && (
                <div style={{marginTop:20,borderTop:'1px solid #1a1d2e',paddingTop:16}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#f97316',marginBottom:12,textTransform:'uppercase',letterSpacing:.5}}>{selectedCat?.name || 'Category'} — Custom Fields</div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
                    {customFieldDefs.map(fd => (
                      <div key={fd.field_key}>
                        <label style={{fontSize:11,color:'#94a3b8',fontWeight:600,display:'block',marginBottom:4}}>{fd.field_label}</label>
                        {fd.field_type === 'select' && fd.options?.length > 0
                          ? <select style={{width:'100%',background:'#181928',border:'1px solid #1a1d2e',borderRadius:6,padding:'8px 10px',color:'#e2e8f0',fontSize:12,outline:'none'}}
                              value={customFieldVals[fd.field_key] || ''}
                              onChange={e => setCustomFieldVals(p => ({...p, [fd.field_key]: e.target.value}))}>
                              <option value="">— Select {fd.field_label} —</option>
                              {fd.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          : <input
                              type={fd.field_type === 'number' ? 'number' : 'text'}
                              style={{width:'100%',background:'#181928',border:'1px solid #1a1d2e',borderRadius:6,padding:'8px 10px',color:'#e2e8f0',fontSize:12,outline:'none',boxSizing:'border-box'}}
                              value={customFieldVals[fd.field_key] || ''}
                              onChange={e => setCustomFieldVals(p => ({...p, [fd.field_key]: e.target.value}))}
                              placeholder={fd.field_label} />
                        }
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Add custom field ── */}
              <div style={{marginTop:16}}>
                {!addingField
                  ? <button type="button" onClick={()=>setAddingField(true)} style={{background:'none',border:'1px dashed #1a1d2e',borderRadius:6,color:'#64748b',fontSize:11,padding:'6px 14px',cursor:'pointer',width:'100%'}}>+ Add custom field for {selectedCat?.name || 'this category'}</button>
                  : <div style={{display:'flex',gap:8,alignItems:'center'}}>
                      <input autoFocus style={{flex:1,background:'#181928',border:'1px solid #f97316',borderRadius:6,padding:'7px 10px',color:'#e2e8f0',fontSize:12}}
                        placeholder="Field name (e.g. Puck Size, Voltage...)"
                        value={newFieldLabel}
                        onChange={e=>setNewFieldLabel(e.target.value)}
                        onKeyDown={e=>{if(e.key==='Enter')saveNewField();if(e.key==='Escape'){setAddingField(false);setNewFieldLabel('');}}} />
                      <button type="button" onClick={saveNewField} style={{background:'#f97316',border:'none',borderRadius:6,color:'#fff',fontSize:12,fontWeight:700,padding:'7px 14px',cursor:'pointer'}}>Save</button>
                      <button type="button" onClick={()=>{setAddingField(false);setNewFieldLabel('');}} style={{background:'none',border:'1px solid #1a1d2e',borderRadius:6,color:'#94a3b8',fontSize:12,padding:'7px 10px',cursor:'pointer'}}>Cancel</button>
                    </div>
                }
              </div>

            </div>
          )}

          {/* ── PURCHASE INFO ── */}
          {tab==='purchase' && (
            <div>
              <div style={{fontSize:12,color:'#e2e8f0',marginBottom:16}}>Track purchase history for depreciation, warranty, and asset management.</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
                <div style={{gridColumn:'1/-1'}}>
                  <label style={S.label}>Vendor / Supplier</label>
                  <select style={S.select} value={f.vendor_id} onChange={e=>setF(p=>({...p,vendor_id:e.target.value}))}>
                    <option value="">— Select vendor —</option>
                    {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                  {vendors.length === 0 && <div style={{fontSize:10,color:'#e2e8f0',marginTop:4}}>No vendors yet — add them in Settings → 🏭 Vendors</div>}
                </div>
                <div>
                  <label style={S.label}>Purchase Price ($)</label>
                  <input type="number" style={S.input} value={f.purchase_price} onChange={e=>setF(p=>({...p,purchase_price:e.target.value}))} placeholder="0.00" />
                </div>
                <div>
                  <label style={S.label}>Purchase Date</label>
                  <input type="date" style={S.input} value={f.purchase_date} onChange={e=>setF(p=>({...p,purchase_date:e.target.value}))} />
                </div>
              </div>
              {f.purchase_price > 0 && f.price_day > 0 && (
                <div style={{marginTop:16,background:'#1e2235',borderRadius:10,padding:'14px 16px',border:'1px solid #1a1d2e'}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#e2e8f0',marginBottom:8,textTransform:'uppercase',letterSpacing:1}}>ROI Estimate</div>
                  <div style={{display:'flex',gap:20,flexWrap:'wrap'}}>
                    <div><div style={{fontSize:10,color:'#e2e8f0'}}>Break-even at day rate</div><div style={{fontSize:14,fontWeight:700,color:'#f97316'}}>{Math.ceil(f.purchase_price/f.price_day)} rental days</div></div>
                    {f.price_week > 0 && <div><div style={{fontSize:10,color:'#e2e8f0'}}>Break-even at week rate</div><div style={{fontSize:14,fontWeight:700,color:'#60a5fa'}}>{Math.ceil(f.purchase_price/f.price_week)} weeks</div></div>}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Footer */}
        <div style={{padding:'14px 24px',borderTop:'1px solid #1a1d2e',display:'flex',justifyContent:'space-between',alignItems:'center',background:'#181928'}}>
          <div style={{display:'flex',gap:6}}>
            {TABS.map(([v,l],i) => (
              <div key={v} onClick={()=>setTab(v)} style={{width:8,height:8,borderRadius:'50%',background:tab===v?'#f97316':'#2e3150',cursor:'pointer',transition:'background 0.15s'}} />
            ))}
          </div>
          <div style={{display:'flex',gap:8}}>
            {tab !== 'basic' && <button style={S.btn('ghost')} onClick={()=>setTab(TABS[TABS.findIndex(t=>t[0]===tab)-1][0])}>← Back</button>}
            {tab !== 'purchase' ? <button style={S.btn('primary')} onClick={()=>setTab(TABS[TABS.findIndex(t=>t[0]===tab)+1][0])}>Next →</button> : <button style={S.btn('primary')} onClick={submit}>💾 Save Item</button>}
          </div>
        </div>
      </div>
    </div>
  );
}